// LayersCore — JS wrapper that delegates to the real WASM binary for core logic.
//
// Architecture: the Rust/WASM core is the SINGLE SOURCE OF TRUTH for event
// acceptance, building, and queuing. When WASM is loaded:
//
// - track()/screen() delegate entirely to the WASM core. The Rust core handles
//   consent, rate limiting, sampling, remote config filtering, event building,
//   and queue management. JS does NOT build or queue events independently.
//
// - flushAsync() calls wasm.drainDelivery() to extract serialized event batches
//   from the Rust queue for HTTP delivery. Headers and URL come from wasm too.
//
// The JS layer handles: HTTP networking (FetchHttpClient), persistence backends,
// async flush with retry + circuit breaker, periodic flush timer, event hydration
// from persistence, and fetchRemoteConfig.
//
// When WASM is NOT available, the JS fallback builds events and manages its own
// queue (same behavior as before WASM integration).
import type { WasmLayersCore } from '../pkg/layers_core.js';

import { FeatureFlagEngine, flagValueIsTruthy, parseFeatureFlagConfig } from './feature-flags.js';
import { RateLimiter } from './rate-limiter.js';
import { SkanEngine } from './skan.js';
import { SURVEY_AUTO_EVENT_NAMES, SurveyEngine, parseSurveyConfig } from './surveys.js';
import type {
  BeforeSendEvent,
  BeforeSendHook,
  ConsentState,
  DeviceContext,
  Environment,
  EventProperties,
  FeatureFlagBootstrap,
  FeatureFlagBootstrapData,
  FeatureFlagValue,
  FeatureFlagsListener,
  GroupsState,
  HttpClient,
  LayersCoreConfig,
  PersistenceBackend,
  Platform
} from './types.js';
import { LayersError } from './types.js';
import { getWasmCtor, isWasmReady } from './wasm-state.js';

// NOTE ON THE `import type` ABOVE: the reference to ../pkg/layers_core.js is
// type-only and therefore erased at build time, creating NO module edge in the
// emitted JS. That is deliberate and load-bearing. core.ts is reached by the
// React Native entry, and Metro cannot resolve the glue's `require("url")` —
// any runtime reference here breaks bundling for every RN/Expo consumer. The
// class itself arrives at runtime via registerWasmCtor() in wasm-state.ts.

// Augment WasmLayersCore with methods added in Rust core
// but not yet in the generated .d.ts (requires wasm-pack rebuild).
declare module '../pkg/layers_core.js' {
  interface WasmLayersCore {
    // ATT delivery routing: see core/src/wasm_api.rs.
    drainDelivery(
      count: number,
      attStatus: string | null | undefined
    ): { url: string; body: string } | null;
    updateRetryAfter(status: number, retryAfterHeader: string | null): void;
    clearRetryAfter(): void;
    isRetryAfterActive(): boolean;
    retryAfterRemainingMs(): number;
    // Delivery policy (ADR 0001) — see core/src/wasm_api.rs
    shouldAttemptFlush(): boolean;
    // Same state as shouldAttemptFlush, no side effects — for requests that
    // carry no event batch (see `should_attempt_side_request` in client.rs).
    shouldAttemptSideRequest(): boolean;
    recordFlushResult(status: number, retryAfterHeader: string | null): string;
    abortFlushAttempt(): void;
    reconcileFirstOpen(queuedEventId: string | null): void;
    confirmFirstOpenDelivered(eventId: string): void;
    adoptSessionNumber(sessionNumber: number): void;
    group(group_id: string, properties: any): void;
    // Tier 1 — see core/src/wasm_api.rs
    setSuperProperties(properties: any): void;
    setSuperPropertiesOnce(properties: any): void;
    unregisterSuperProperty(key: string): void;
    clearSuperProperties(): void;
    getSuperProperties(): any;
    timeEvent(eventName: string): void;
    cancelTimedEvent(eventName: string): bigint | number;
    setGroup(groupType: string, groupId: string): void;
    addGroup(groupType: string, groupId: string): void;
    removeGroup(groupType: string): void;
    getGroups(): any;
    increment(key: string, delta: number): void;
    append(key: string, value: any): void;
    union(key: string, values: any): void;
    unset(key: string): void;
    reset(): void;
    getAnonymousId(): string | undefined;
    getDeviceId(): string | undefined;
    getSessionNumber(): number;
    getFirstOpenTime(): string | null;
    // Tier 8: Surveys
    knownSurveyIds(): string[];
    surveyDefinitionsJson(): string;
    getActiveSurveysJson(ctx: unknown): string;
    markSurveyShown(surveyId: string): void;
    markSurveyDismissed(surveyId: string): void;
    submitSurveyResponse(surveyId: string, response: unknown): void;
    surveyShowHistoryJson(): string;
    seedSurveyShowHistory(historyJson: string): void;
    // SKAN (SKAdNetwork) — see core/src/wasm_api.rs
    skanSetPreset(preset: string): void;
    skanSetRules(rulesJson: string): void;
    skanConfigureFromRemoteConfig(): void;
    skanProcessEvent(eventName: string, propertiesJson: string | null): string | null;
    skanRecordConversionResult(value: number, success: boolean): void;
    skanCurrentValue(): number;
    skanIsEnabled(): boolean;
    skanCurrentPreset(): string | null;
    // Tier 4 — feature flags. See core/src/wasm_api.rs for the source.
    getFeatureFlag(flagKey: string): unknown;
    isFeatureEnabled(flagKey: string): boolean;
    getFeatureFlagPayload(flagKey: string): unknown;
    getAllFlags(): unknown;
    reloadFeatureFlags(): boolean;
    setPersonPropertiesForFlags(properties: any): void;
    mergePersonPropertiesForFlags(properties: any): void;
    setFeatureFlagBootstrap(bootstrap: any): void;
    knownFeatureFlagKeys(): unknown;
    // Crash-safety snapshot: serialize the Rust queue without draining it.
    queueSnapshotJson(): string | null;
  }
}

/**
 * Persistence key for the rolling crash-safety queue snapshot. A single
 * key, overwritten on every snapshot, prefixed `events_` so init hydration
 * picks it up. Mirrors `QUEUE_SNAPSHOT_KEY` in core/src/client.rs.
 */
const EVENTS_SNAPSHOT_KEY = 'events_snapshot';

/**
 * Prefix shared by every durable event key: the rolling snapshot chunks,
 * `events_shutdown*` files, and legacy `events_batch_*` files. `init` hydrates
 * the whole prefix as one store, so anything that clears the store must clear
 * the same prefix rather than one family of it. Mirrors the `list_keys("events_")`
 * hydration in core/src/client.rs.
 */
const EVENTS_KEY_PREFIX = 'events_';

/**
 * Persistence key for the durable identity record. Deliberately the SAME key
 * and the SAME JSON field names as `IDENTITY_PERSISTENCE_KEY` /
 * `PersistedIdentity` in core/src/client.rs, so a record written by either
 * engine is readable by the other.
 *
 * The two engines share ONE backing store: `wasmPersistenceBridge` hands this
 * core's `PersistenceBackend` to the Rust core, so on the web the fallback
 * engine writes this blob while the binary loads and the Rust core reads the
 * very same record once it lands. Matching names is what makes that hand-off
 * free. The Rust struct carries every field it does not model through
 * untouched (`extra`, core/src/client.rs), so neither engine truncates the
 * other's record.
 *
 * Only ONE of them mints or updates it per launch: `initIdentity` and
 * `initFirstOpen` return early when WASM is live, leaving the Rust core the
 * sole writer on that path.
 */
const IDENTITY_PERSISTENCE_KEY = 'identity_state';

/**
 * The install-scoped SKAN conversion-value floor, stored as a single byte
 * under the SAME key the Rust core uses (`client.rs:70`) so a future
 * persistence bridge reads what this engine wrote.
 */
const SKAN_VALUE_PERSISTENCE_KEY = 'skan_conversion_value';

/**
 * Largest snapshot chunk written to a single persistence key, in bytes.
 *
 * The binding constraint is Android. `@layers/react-native` stores every
 * persistence key as an AsyncStorage row, and AsyncStorage reads rows through
 * a SQLite cursor whose CursorWindow is ~2 MB — a row larger than that cannot
 * be read back at all. The failure is total rather than partial: the batch
 * read in the pre-load throws, its catch abandons the WHOLE pre-load, and the
 * install comes up with neither its queued events nor its `identity_state`
 * record, which reads downstream as a brand-new install. That catch now warns
 * (`warnPersistencePreloadFailed` in `@layers/react-native`), so the state is
 * at least visible; staying under the window is what avoids it.
 *
 * A full 10K-event queue serializes to several megabytes, so the single
 * rolling key this used to write was one busy offline session away from that.
 * 512 KB leaves a 4x margin under the window for the row overhead SQLite adds
 * and for the base64 expansion the RN backend applies on write.
 */
const MAX_SNAPSHOT_CHUNK_BYTES = 512 * 1024;

/**
 * Key of the Nth chunk under `baseKey`. Chunk 0 keeps the bare key so a file
 * written by an older build still hydrates; the rest are suffixed with a
 * zero-padded index, which sorts lexicographically into queue order — the
 * order the hydration sort in `init` relies on.
 */
function chunkKey(baseKey: string, index: number): string {
  return index === 0 ? baseKey : `${baseKey}_${String(index).padStart(4, '0')}`;
}

/** Key of the Nth chunk of the rolling crash-safety snapshot. */
function snapshotChunkKey(index: number): string {
  return chunkKey(EVENTS_SNAPSHOT_KEY, index);
}

/**
 * Split events into groups that each serialize under
 * {@link MAX_SNAPSHOT_CHUNK_BYTES}.
 *
 * The accounting is explicit and lossless: events are packed in order until
 * the next one would push the group over the cap, and a single event larger
 * than the cap gets a group to itself rather than being dropped or trimmed. An
 * oversized row is a durability risk on Android; a silently discarded event is
 * data loss, which is strictly worse.
 */
function chunkEvents(events: QueuedEvent[]): QueuedEvent[][] {
  const encoder = new TextEncoder();
  const chunks: QueuedEvent[][] = [];
  let chunk: QueuedEvent[] = [];
  let chunkBytes = 2; // the enclosing `[]`

  for (const event of events) {
    const size = encoder.encode(JSON.stringify(event)).byteLength + 1; // + separator
    if (chunk.length > 0 && chunkBytes + size > MAX_SNAPSHOT_CHUNK_BYTES) {
      chunks.push(chunk);
      chunk = [];
      chunkBytes = 2;
    }
    chunk.push(event);
    chunkBytes += size;
  }
  if (chunk.length > 0) chunks.push(chunk);
  return chunks;
}

/**
 * Event name emitted exactly once per install — the very first time the SDK
 * ever runs. Mirrors `FIRST_OPEN_EVENT` in core/src/client.rs.
 */
const FIRST_OPEN_EVENT = '$first_open';

/**
 * Recursively convert any `Map` instances in a value to plain objects.
 *
 * `serde_wasm_bindgen` serializes Rust `HashMap` / `BTreeMap` values as JS
 * `Map` instances by default. Most JS consumers expect plain objects (so
 * `obj.foo` and `Object.keys()` work), so we normalize at the boundary.
 * Arrays are walked to handle the `HashMap<String, Value>` payload case.
 *
 * @internal
 */
function mapToObject(value: unknown): unknown {
  if (value instanceof Map) {
    const out: Record<string, unknown> = {};
    for (const [k, v] of value.entries()) {
      out[String(k)] = mapToObject(v);
    }
    return out;
  }
  if (Array.isArray(value)) {
    return value.map(mapToObject);
  }
  return value;
}

/**
 * Normalize a feature-flag bootstrap object to the snake_case shape that
 * the Rust deserializer expects. Accepts either:
 *  - the snake_case `FeatureFlagBootstrapData` shape (round-trips with the
 *    wire protocol), or
 *  - the camelCase `FeatureFlagBootstrap` shape preferred by JS / TS.
 *
 * Returns `null` when the input is null / undefined, or `undefined` when
 * the input was provided but contained no recognizable keys (so callers
 * can distinguish "not provided" from "provided but empty").
 *
 * @internal Exported for tests via `core.ts` re-export.
 */
function normalizeBootstrap(
  bootstrap: FeatureFlagBootstrap | FeatureFlagBootstrapData | undefined
): {
  feature_flags: Record<string, FeatureFlagValue>;
  feature_flag_payloads: Record<string, unknown>;
} | null {
  if (!bootstrap || typeof bootstrap !== 'object') return null;
  const featureFlags =
    (bootstrap as FeatureFlagBootstrap).featureFlags ??
    (bootstrap as FeatureFlagBootstrapData).feature_flags ??
    {};
  const featureFlagPayloads =
    (bootstrap as FeatureFlagBootstrap).featureFlagPayloads ??
    (bootstrap as FeatureFlagBootstrapData).feature_flag_payloads ??
    {};
  if (Object.keys(featureFlags).length === 0 && Object.keys(featureFlagPayloads).length === 0) {
    return null;
  }
  return {
    feature_flags: featureFlags,
    feature_flag_payloads: featureFlagPayloads
  };
}

/** Generate a UUID v4. Uses crypto.randomUUID() when available (browsers, Node 19+),
 *  falls back to Math.random() for environments like React Native Hermes. */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Rewrite a BCP-47 language tag into the POSIX spelling the wire protocol
 * documents (`schema/events.schema.json`: "Device locale (e.g. 'en_US')").
 *
 * Every TS wrapper reads a hyphenated tag — `navigator.language` on web,
 * `Intl.DateTimeFormat().resolvedOptions().locale` on Node, `AppleLocale` on
 * React Native/iOS — while Android's `I18nManager.localeIdentifier` and every
 * native SDK (`Locale.current.identifier`, `Locale.toString()`) hand over the
 * underscore form. All of them land in the same `sdk_events.locale` column, so
 * `en-US` and `en_US` split every GROUP BY on locale by client platform.
 *
 * The Rust core stores whatever it is given verbatim — `client.rs:2366` only
 * supplies the DEFAULT `en_US` — so the normalization has to happen here, at
 * the one boundary both engines pass through.
 */
function normalizeLocale(locale: string): string {
  return locale.replace(/-/g, '_');
}

const DEFAULT_BASE_URL = 'https://in.layers.com';
const DEFAULT_TRACKING_BASE_URL = 'https://att.layers.com';
const DEFAULT_FLUSH_THRESHOLD = 20;
const DEFAULT_MAX_QUEUE_SIZE = 10_000;
const DEFAULT_MAX_BATCH_SIZE = 1_000;
const DEFAULT_FLUSH_INTERVAL_MS = 30_000;
const DEFAULT_SESSION_TIMEOUT_MS = 30 * 60 * 1_000; // 30 minutes

// Read version from package.json at build time via tsdown define
declare const __LAYERS_CORE_WASM_VERSION__: string;
const PACKAGE_VERSION =
  typeof __LAYERS_CORE_WASM_VERSION__ !== 'undefined' ? __LAYERS_CORE_WASM_VERSION__ : '0.1.0';

interface QueuedEvent {
  event: string;
  timestamp: string;
  event_id: string;
  app_id: string;
  environment: Environment;
  platform: Platform;
  os_version: string;
  app_version: string;
  device_model: string;
  locale: string;
  session_id?: string;
  session_number?: number;
  is_first_session?: boolean;
  app_user_id?: string;
  user_id?: string;
  anonymous_id?: string;
  device_id?: string;
  first_open_time?: string;
  build_number?: string;
  screen_size?: string;
  install_id?: string;
  idfa?: string;
  idfv?: string;
  att_status?: string;
  timezone?: string;
  /** Legacy singleton group, mirrored from the `company` group (event.rs:112-114). */
  group_id?: string;
  /** Multi-group membership map. Wire key is `$groups` (event.rs:116-121). */
  $groups?: Record<string, string>;
  /** Elapsed ms since `timeEvent(name)`, on the next `track(name)` only. */
  $duration_ms?: number;
  /** `screen()` only — the screen name, top-level (client.rs:923-926). */
  screen_name?: string;
  /** `screen()` only — breadcrumb to the previous screen (event.rs:157-164). */
  previous_screen_name?: string;
  previous_screen_class?: string;
  properties?: EventProperties;
  [key: string]: unknown;
}

function withoutIdfa(event: QueuedEvent): QueuedEvent {
  const copy = { ...event };
  delete copy.idfa;
  return copy;
}

/**
 * The `events` block of the remote config, as the fallback engine reads it.
 * Mirrors `EventsConfig` in core/src/remote_config.rs. Parsed once per
 * `/config` fetch rather than per event.
 */
interface FallbackEventsConfig {
  killswitch?: boolean;
  allowlist?: string[];
  denylist?: string[];
  sampling_rate?: number;
  per_event_sampling?: Record<string, number>;
}

interface EventBatch {
  events: QueuedEvent[];
  batch_id: string;
  sent_at: string;
  /** Wire protocol version. Mirrors `PROTOCOL_VERSION` in core/src/event.rs. */
  protocol_version: number;
}

/**
 * Current wire protocol version stamped on every batch this file builds.
 * Mirrors `PROTOCOL_VERSION` in core/src/event.rs (`event.rs:409-425`), which
 * the Rust core stamps in `EventBatch::new`. Batches built here — the fallback
 * engine's, plus the WASM path's rehydrated-queue and beacon batches — never
 * pass through `EventBatch::new`, so they carried no version at all.
 */
const PROTOCOL_VERSION = 3;

/**
 * Device-context fields that describe the install and are kept when a
 * `setDeviceContext` call omits them. Mirrors the carry-over in
 * core/src/client.rs::set_device_context.
 */
const INSTALL_IDENTITY_KEYS = [
  'installId',
  'platform',
  'osVersion',
  'appVersion',
  'deviceModel'
] as const satisfies ReadonlyArray<keyof DeviceContext>;

// ── `$sdk_health` — the "no silent loss" health beacon ────────────────────
//
// NAMING. "Beacon" is already taken in this file by the `navigator.sendBeacon`
// page-hide path (`createBeaconPayload` / `_lastBeaconEvents`). Every
// identifier below uses `health`; "beacon" appears only in prose.
//
// WHAT IT IS. This engine owns ~18 places where an event or a batch stops
// existing and nothing records that it did — a killswitch, a sampler verdict, a
// FIFO eviction, a 4xx drop, an unreadable persisted file. `HealthCounters`
// puts one named counter behind every one of them, and `$sdk_health` carries
// them to the server, where the reconciler evaluates one identity per beacon:
//
//   accepted == delivered + delivered_unacked + Σdropped
//               + queue_depth_engine + queue_depth_wrapper + inflight
//
// A non-zero residual is an event that left through a path with no counter,
// which is the whole defect class this exists to make impossible to hide.
//
// The wire shape is FROZEN and shared with the Rust core — see
// `schema/events.schema.json` (`$defs/SdkHealthProperties`) and the
// `$sdk_health` section of `schema/PROTOCOL.md`. Nothing here may drift from it.

/** Event name of the health beacon. Mirrors `HEALTH_EVENT` in core/src/client.rs. */
const HEALTH_EVENT = '$sdk_health';

/**
 * Beacon payload version, independent of the batch `protocol_version` (which
 * stays 3). Bump only on a breaking change to the `$health_*` field set.
 */
const HEALTH_PAYLOAD_VERSION = 1;

/** Cadence defaults, overridable per-app from `/config` → `config.health`. */
const DEFAULT_HEALTH_INTERVAL_S = 21_600; // 6h
const DEFAULT_HEALTH_FLUSH_INTERVAL = 50; // flush ticks

/**
 * Why an event or batch stopped existing. Closed vocabulary shared verbatim
 * with the Rust core's `DropReason`; `other` exists so a site can be counted
 * before it is named — a value the reconciler still sums, so the identity holds.
 *
 * Three of these count FILES rather than events (`persisted_file_unreadable`,
 * `persisted_file_over_cap`, `persisted_payload_expired`): a persisted record
 * destroyed before it was parsed has an unknowable event count. A beacon whose
 * counters include any of the three carries `file_counted_drops` in
 * `$health_degraded`, which tells the reconciler to read the identity as a
 * LOWER BOUND rather than as exact.
 */
export const HEALTH_DROP_REASONS = [
  // build-time: the event never entered a queue
  'killswitch',
  'not_allowlisted',
  'denylisted',
  'rate_limited',
  'sampled_out',
  'before_send_returned_none',
  'before_send_errored',
  'engine_error',
  // queue-time
  'queue_overflow',
  'requeue_overflow',
  'hydration_overflow',
  // persistence-time (the three file-counted ones, plus shutdown)
  'persisted_file_unreadable',
  'persisted_file_over_cap',
  'persisted_payload_expired',
  'shutdown_write_failed',
  // delivery-time
  'delivery_dropped',
  'requeue_failed',
  // lifecycle
  'reset_discarded',
  'other'
] as const;

export type HealthDropReason = (typeof HEALTH_DROP_REASONS)[number];

/** Reasons whose unit is a persisted FILE rather than an event (wire delta #1). */
const FILE_COUNTED_DROP_REASONS: readonly HealthDropReason[] = [
  'persisted_file_unreadable',
  'persisted_file_over_cap',
  'persisted_payload_expired'
];

/** Delivery outcome classes, mirroring the verdict split in the Rust core. */
export type HealthStatusClass = '2xx' | '4xx_drop' | '4xx_retry' | '5xx' | 'network';

/** The `health` block of the remote config. Mirrors `HealthConfig` in Rust. */
interface HealthRemoteConfig {
  enabled?: boolean;
  interval_s?: number;
  flush_interval?: number;
}

/** Totals snapshotted at the previous beacon, so every `_delta` is exact. */
interface HealthTotals {
  accepted: number;
  delivered: number;
  delivered_unacked: number;
  /** Reason -> count, NOT a scalar total. Field names mirror the Rust struct. */
  dropped: Record<string, number>;
  batches_attempted: number;
  batches_by_status: Record<string, number>;
}

/**
 * Version of the PERSISTED counter record — distinct from `$health_v`, which
 * versions the wire payload. Both are 1 today and they are free to diverge.
 */
const HEALTH_RECORD_VERSION = 1;

/**
 * Render epoch millis the way the Rust core's `chrono` serializer does:
 * RFC 3339 with an explicit `+00:00` offset rather than `Z`.
 *
 * Both spellings are the same instant and every RFC 3339 parser takes either,
 * so this is cosmetic — but the persisted record is diffed against the Rust
 * reference fixture byte for byte, and a cosmetic difference in a shared record
 * is exactly the kind of thing that gets "fixed" later by whichever side reads
 * it as wrong.
 */
function toRfc3339Offset(ms: number): string {
  return new Date(ms).toISOString().replace(/Z$/, '+00:00');
}

/** Parse either spelling back to epoch millis; null when unusable. */
function parseRfc3339(value: unknown): number | null {
  if (typeof value !== 'string' || value.length === 0) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Monotonic-since-install counters, persisted inside the identity record.
 *
 * MONOTONIC, never deltas-only. An install whose consent is never granted
 * queues everything and delivers nothing; the first beacon after a grant has to
 * carry the ENTIRE denied-period history, including evictions that happened
 * while the queue backed up. Delta-only counters would lose all of it, because
 * the queued beacons from that period are themselves subject to eviction.
 */
class HealthCounters {
  /** Identifies the monotonic series. Re-minted only when a record is created from nothing. */
  epoch = '';
  /** Beacons emitted so far. The NEXT beacon's `$health_seq` is `seq + 1`. */
  seq = 0;
  accepted = 0;
  delivered = 0;
  deliveredUnacked = 0;
  dropped: Partial<Record<HealthDropReason, number>> = {};
  persistWriteFailures = 0;
  breakerOpens = 0;
  batchesAttempted = 0;
  batchesByStatus: Partial<Record<HealthStatusClass, number>> = {};
  hydratedEvents = 0;
  lastDropReason: string | null = null;
  lastDropAtMs: number | null = null;
  lastBeaconAtMs: number | null = null;
  prev: HealthTotals | null = null;
  /** In-flight events are process-scoped: a restart has nothing on the wire. */
  inflight = 0;
  /** Set by every mutation; cleared by a successful persist. */
  dirty = false;
}

/** Number of events inside a serialized `EventBatch`; 0 when it cannot be read. */
function countBatchEvents(batchJson: string): number {
  try {
    const parsed = JSON.parse(batchJson) as { events?: unknown[] };
    return Array.isArray(parsed.events) ? parsed.events.length : 0;
  } catch {
    return 0;
  }
}

/** Sum every value in a reason/status map, INCLUDING keys this build never emits. */
function sumCounts(counts: Record<string, number>): number {
  let total = 0;
  for (const value of Object.values(counts)) total += value;
  return total;
}

/** Drop the zero-valued entries — the wire omits them to keep the payload small. */
function compactCounts(counts: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(counts)) {
    if (value > 0) out[key] = value;
  }
  return out;
}

/** `after − before` per key, zero-valued entries omitted. */
function deltaCounts(
  after: Record<string, number>,
  before: Record<string, number> | undefined
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(after)) {
    const delta = value - (before?.[key] ?? 0);
    if (delta > 0) out[key] = delta;
  }
  return out;
}

/**
 * A snapshot of the counters, for tests and for the future `record_drop`
 * hand-off to the Rust core on the WASM path (design §4.5).
 *
 * @internal Not part of the supported SDK surface.
 */
export interface HealthSnapshot {
  epoch: string;
  seq: number;
  accepted: number;
  delivered: number;
  deliveredUnacked: number;
  dropped: Record<string, number>;
  droppedTotal: number;
  inflight: number;
  queueDepthEngine: number;
  queueDepthWrapper: number;
  hydratedEvents: number;
  persistWriteFailures: number;
  breakerOpens: number;
  batchesAttempted: number;
  batchesByStatus: Record<string, number>;
  degraded: string[];
}

/**
 * On-disk identity record. Field names are the serde names of
 * `PersistedIdentity` in core/src/client.rs — do not rename them.
 *
 * The index signature exists so read-modify-write round-trips preserve fields
 * this engine does not understand (e.g. `debug_token`) instead of truncating
 * another engine's record.
 */
interface PersistedIdentity {
  first_open_time?: string | null;
  /**
   * `$first_open` was BUILT. Deliberately not a delivery receipt — see
   * {@link LayersCore.shouldReArmFirstOpen} for the pair it forms with the two
   * fields below.
   */
  first_open_emitted?: boolean;
  /**
   * `event_id` of the `$first_open` this install built, so a re-arm re-emits
   * the SAME id and server-side `ON CONFLICT (event_id) DO NOTHING` absorbs
   * the second copy if the first turned out to have landed.
   *
   * Every writer that marks `first_open_emitted` for an event that was BUILT
   * records this alongside it — the build itself, the crash repair, and the
   * migration settle. The flag can therefore only appear without an id on a
   * record written before this field existed, or on a `reset()` that retired
   * a `$first_open` which was never built. Neither has anything to re-emit,
   * which is exactly how {@link LayersCore.shouldReArmFirstOpen} reads it.
   */
  first_open_event_id?: string | null;
  /** A flush confirmed `$first_open` reached the server. The actual receipt. */
  first_open_delivered?: boolean;
  /** Stable per-install device identifier (rotated on `reset()`). */
  device_id?: string | null;
  /** Stable per-install anonymous identifier (rotated on `reset()`). */
  anonymous_id?: string | null;
  /** Last-known monotonic session counter; the next launch resumes at +1. */
  last_session_number?: number;
  /**
   * Durable `$sdk_health` counters. Lives INSIDE the identity record rather
   * than under a key of its own: `identity_state` is classified `Durable` by
   * the persistence key policy and therefore never expires, and adding a second
   * key would re-open the TTL hole that policy was written to close. Both
   * engines carry each other's unmodelled fields through untouched, so a record
   * written by one is readable by the other.
   */
  health?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * The `$first_open` `event_id` a record carries, or null when it carries
 * none. One reader for every consumer of the field: absent, empty and
 * non-string all mean the same thing — "no event to re-emit" — and a
 * `first_open_emitted: true` record that hits this branch is the pre-upgrade
 * shape the re-arm has to leave alone.
 */
/** Ceiling of the `u32` the Rust core stores `last_session_number` in. */
const U32_MAX = 4294967295;

/**
 * Coerce a stored `last_session_number` into the counter it has to be.
 *
 * Verbatim port of `identity_session_number` in core/src/client.rs. Both
 * engines write this one record, so a value neither of them expects has to
 * resolve identically on both sides — otherwise the same bytes put the same
 * install on two different sessions depending on which engine read them.
 *
 * A monotonic counter that reads back as `7.5` or `-1` is a bug in whoever
 * wrote it; clamping costs at most one session number, while rejecting the
 * value would re-mint the whole install.
 */
function coerceSessionNumber(value: unknown): number {
  const clamp = (n: number): number => {
    if (!Number.isFinite(n) || n <= 0) return 0;
    return Math.min(Math.trunc(n), U32_MAX);
  };
  if (typeof value === 'number') return clamp(value);
  if (typeof value === 'string') {
    const parsed = Number(value.trim());
    return Number.isNaN(parsed) ? 0 : clamp(parsed);
  }
  return 0;
}

function persistedFirstOpenEventId(identity: PersistedIdentity): string | null {
  const eventId = identity.first_open_event_id;
  return typeof eventId === 'string' && eventId.length > 0 ? eventId : null;
}

/**
 * Whether a persisted record already accounts for this install's `$first_open`
 * — BUILT or DELIVERED, either one alone.
 *
 * The two flags are written by different calls at different moments, so a store
 * that does not order writes can land one without the other. Reading them as an
 * ORDERED PAIR — treating `first_open_delivered` as meaningful only under
 * `first_open_emitted === true` — made a record carrying just the receipt read
 * as an install that had never built one, which arms and ships a duplicate.
 *
 * That shape is reachable: {@link LayersCore.seedIdentityBeforeMint} writes the
 * receipt it was handed at init, and `settleFirstOpenAfterMigration` writes the
 * build flag in a SECOND write that AsyncStorage v3 is free to commit first.
 * A receipt is strictly stronger evidence than the build flag anyway — nothing
 * can make an install that has already SENT one owe another — so every reader
 * deciding "does this install still owe a `$first_open`" goes through here.
 *
 * Only readers that run on a FRESH LAUNCH need it. Within one process both
 * flags travel through the same ordered in-memory store, so a core reading its
 * own record (`exportMigrationState`) can never see the pair split.
 * (BugBot, PR #260.)
 */
function firstOpenAccountedFor(identity: PersistedIdentity): boolean {
  return identity.first_open_emitted === true || identity.first_open_delivered === true;
}

/**
 * The install's identity as carried across a core swap by
 * {@link LayersCore.exportMigrationState}. Camel-cased because this crosses a
 * JS API, unlike the snake_cased {@link PersistedIdentity} record whose field
 * names are pinned to core/src/client.rs.
 */
export interface MigrationIdentity {
  anonymousId: string | null;
  deviceId: string | null;
  sessionNumber: number;
  firstOpenTime: string | null;
  firstOpenEmitted: boolean;
  /**
   * Whether a `$first_open` was still sitting in the replaced core's queue at
   * export time — i.e. emitted but NOT yet delivered. Separates "already on
   * the wire, nothing to migrate" from "should have arrived with the queue",
   * which is the only way the destination can tell a successful migration from
   * a lost one.
   */
  firstOpenQueued: boolean;
  /**
   * `event_id` of the `$first_open` the replaced core built, so the
   * destination re-emits under the SAME id when the copy does not survive the
   * swap and server-side dedup absorbs whichever copy lands second.
   *
   * The id is as load-bearing across the swap as the flag is. Carrying
   * `firstOpenEmitted` without it left the destination holding a record that
   * says BUILT with nothing to re-emit under — the exact shape
   * {@link LayersCore.shouldReArmFirstOpen} has to read as a pre-upgrade
   * record and take at its word, which retired `$first_open` for good on a
   * BRAND-NEW install whenever the migrated copy died before delivery.
   */
  firstOpenEventId: string | null;
  /**
   * Whether a flush CONFIRMED the replaced core's `$first_open` reached the
   * server. Only meaningful to a destination that adopts the ids that
   * delivery went out under; carried so such a destination inherits the
   * receipt and leaves an already-sent event alone.
   */
  firstOpenDelivered: boolean;
  /**
   * The replaced core's session id (JS engine only). A destination that takes
   * over the replaced core's identity takes this too, so the events the
   * replaced core queued this launch (React Native's pre-init events, the
   * eager `$first_open`, the swap's `$sdk_health`) share one `session_id` with
   * the `app_open` the destination tracks. Optional: an older caller omits it.
   */
  sessionId?: string | null;
}

/**
 * Everything a core hands to its replacement across a swap — the whole return
 * of {@link LayersCore.exportMigrationState}.
 */
export interface MigrationState {
  consent: ConsentState;
  superProperties: Record<string, unknown>;
  groups: GroupsState;
  deviceContext: DeviceContext;
  identity: MigrationIdentity;
}

export interface LayersCoreInit {
  config: LayersCoreConfig;
  httpClient: HttpClient;
  persistence?: PersistenceBackend;
  /**
   * iOS wrappers register this to read the live ATT status once per delivery
   * attempt, before the delivery gate. Android, web and Node leave it unset.
   * A throw, a 1,000 ms timeout or an empty result counts as not_determined:
   * delivery strips the IDFA and uses the main host.
   */
  attStatusProvider?: () => Promise<string | null | undefined> | string | null | undefined;
  /**
   * State from the core this one replaces, handed over AT INIT rather than
   * through a follow-up {@link LayersCore.applyMigrationState} call.
   *
   * The two are not equivalent, and the difference is a persisted one. `init`
   * mints an identity whenever the store holds none, and WRITES it; a swap
   * that hands the real identity over afterwards therefore leaves the record
   * carrying a throwaway `anonymous_id` / `device_id` / `first_open_time` for
   * as long as it takes the second write to land. Passing the state here lets
   * {@link LayersCore.seedIdentityBeforeMint} seat the real identity first, so
   * the record is written ONCE, already correct, and the throwaway one never
   * exists on disk at all.
   *
   * That window is not theoretical. `@react-native-async-storage/async-storage`
   * v3 dropped the `SerialExecutor` v1/v2 ran every storage operation through
   * (`android/.../SerialExecutor.java`) in favour of one unsynchronised
   * `launch {}` per call (`android/.../storage/RNStorage.kt`), so two writes to
   * the same key issued in the same tick can COMMIT IN EITHER ORDER. When the
   * mint won, the install's durable record named an identity that had never
   * been on the wire — and the next launch resumed it, splitting one install
   * into two `anonymous_id`s, two `device_id`s and two `$first_open`s. Measured
   * at 24% of offline-first installs on v3.1.1; 0/18 on v2.2.0.
   */
  migration?: MigrationState;
}

// --- WASM initialization ---
//
// WASM loading is split across entry points, and core.ts names NONE of the
// loaders — importing one here would pull the wasm-bindgen glue into every
// platform's bundle, which is exactly what made @layers/core-wasm impossible
// to bundle under Metro:
// - Browser entry (browser.ts): uses initWasm() (async fetch)
// - Node entry (index.ts): uses ensureWasmNode() (sync fs.readFileSync)
// - React Native entry (react-native.ts): no loader at all — the JS engine
//   below is the production engine there.
// - The loaders publish their result through wasm-state.ts; LayersCore reads
//   isWasmReady() / getWasmCtor() from there.
//
// `initWasm` is re-exported by the entry points themselves (index.ts,
// browser.ts, react-native.ts), NOT from here: a re-export is a real module
// edge, so re-exporting the loader would re-taint this file.

// Hook for the entry point to register a sync WASM loader (Node only).
// Browser entry does NOT call this — it relies on async initWasm().
let _ensureWasmHook: (() => void) | null = null;

/**
 * Register a synchronous WASM loading function.
 * Called by the Node.js entry point to wire in ensureWasmNode().
 * Browser entry never calls this, so node:fs is never imported.
 * @internal
 */
export function registerEnsureWasm(fn: () => void): void {
  _ensureWasmHook = fn;
}

function ensureWasm(): void {
  if (isWasmReady()) return;
  if (_ensureWasmHook) {
    _ensureWasmHook();
  }
}

export class LayersCore {
  private wasm: WasmLayersCore | null = null;

  private readonly appId: string;
  private readonly baseUrl: string;
  private readonly trackingBaseUrl: string;
  private readonly attStatusProvider: LayersCoreInit['attStatusProvider'];
  private readonly environment: Environment;
  private readonly flushThreshold: number;
  private readonly maxQueueSize: number;
  private readonly maxBatchSize: number;
  private readonly flushIntervalMs: number;
  private readonly sdkVersion: string;
  private readonly enableDebug: boolean;
  /**
   * EU opt-in mode: when true, delivery requires an EXPLICIT analytics
   * grant — the allowed-unless-denied default is not enough. Mirrors the
   * Rust core's consent_required gate for the JS fallback path.
   */
  private readonly consentRequired: boolean;
  /**
   * Tier 9: when true, an active Do-Not-Track signal shuts the delivery gate,
   * exactly as `respect_dnt` does in core/src/client.rs:1514. Immutable after
   * init, like the Rust field.
   */
  private readonly respectDnt: boolean;
  /**
   * The last DNT signal a wrapper reported via {@link setDntSignal}. Only
   * consulted when {@link respectDnt} is on (client.rs:1493, 1514).
   */
  private dntActive = false;
  /** When true, every outbound request to /events carries `X-Debug-Token`. */
  private readonly debugMode: boolean;
  /**
   * Per-device debug token (UUID). Generated lazily on first access when
   * `debugMode` is true, persisted under the `debug_token` key so it survives
   * restarts. Null when debug mode is off.
   */
  private debugToken: string | null = null;

  private readonly httpClient: HttpClient;
  private readonly persistence: PersistenceBackend;

  // JS fallback event queue — only used when WASM is NOT loaded
  private queue: QueuedEvent[] = [];

  /**
   * Props for a `$first_open` detected at init but not yet built (JS fallback
   * only — the Rust core parks its own). Non-null means "owed, not yet
   * queued". See the `$first_open` section further down.
   */
  private pendingFirstOpen: EventProperties | null = null;
  /** Fallback mirror of the persisted `first_open_time` (ISO-8601), or null. */
  private firstOpenTime: string | null = null;
  /**
   * Durable pseudonymous identity for the fallback engine (JS fallback only —
   * the Rust core owns both when WASM is live). Minted at init and persisted
   * in the identity record, rotated by `reset()`, and stamped on EVERY event:
   * without them the ingest service cannot stitch `user_id` ↔ device, so every
   * React Native install was anonymous-but-unjoinable. Mirrors the
   * `anonymous_id` / `device_id` fields set in core/src/client.rs:2387-2393.
   */
  private anonymousId: string | null = null;
  private deviceId: string | null = null;
  /**
   * True when {@link initIdentity} MINTED this core's ids because its
   * persistence held no identity record, false when it RESUMED ids a previous
   * launch had already persisted. Read only by {@link adoptMigratedIdentity},
   * which needs to tell "this install has no identity yet" apart from "this
   * install has one and it is durable" — the two cases take opposite sides in
   * a core swap.
   */
  private identityMinted = false;
  /**
   * True when {@link seedIdentityBeforeMint} wrote the replaced core's identity
   * into an empty record, so {@link initIdentity} RESUMED ids that in every
   * other respect belong to the core being replaced. To
   * {@link adoptMigratedIdentity} that is the same fact `identityMinted` used
   * to carry — this core took the replaced core's identity — reached one write
   * earlier.
   */
  private identitySeeded = false;
  /**
   * Session number handed over by the replaced core, to be CLAIMED rather than
   * counted. Both cores call {@link initIdentity} on the same record, so
   * counting again would ship the pre-swap events as session N and the rest as
   * N+1 — one launch reported as two sessions, and `is_first_session` lost on a
   * genuine first install. `null` when no hand-off is in play.
   */
  private seededSessionNumber: number | null = null;
  /**
   * Monotonic session counter for the fallback engine, resumed from the
   * persisted `last_session_number` at init (`client.rs:509-517`) and
   * incremented by {@link rotateSession} — the single entry point every
   * session boundary goes through. `0` means "not initialized"
   * (WASM path — the Rust core keeps its own).
   */
  private _sessionNumber = 0;
  private consent: ConsentState = {};
  private deviceContext: DeviceContext = {};
  private userId: string | undefined;
  private _isShutDown = false;
  private _flushing = false; // mutex to prevent concurrent flush/requeue races
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * The fallback engine's batch that is on the wire right now: drained from
   * `queue`, verdict not yet in. `doFlush()` writes these into the snapshot
   * ahead of the queue, so a snapshot taken mid-request still holds them. A
   * process killed before the verdict lands then re-sends them next launch
   * (server-side `event_id` dedup absorbs the repeat). Without this, any
   * snapshot written during a send dropped the in-flight batch from disk.
   */
  private _inflightEvents: QueuedEvent[] = [];

  // Circuit breaker state (JS-side, for HTTP retry logic)
  private circuitFailures = 0;
  private circuitState: 'closed' | 'open' | 'half-open' = 'closed';
  private circuitOpenedAt = 0;
  // Mirror the Rust core's breaker defaults (ADR 0001): 3 consecutive
  // failures to open, 60s before the half-open probe.
  private readonly circuitThreshold = 3;
  private readonly circuitResetMs = 60_000;

  // JS fallback Retry-After gate (mirrors WASM/Rust Retry-After behavior)
  private _retryAfterUntilMs = 0;
  private static readonly RETRY_AFTER_MAX_SECS = 300; // 5 minutes, same as WASM path

  /**
   * Snapshot chunks hydration could not consume because the in-memory queue
   * filled first. They still hold undelivered events, so every later snapshot
   * write must leave them alone — see {@link reserveSnapshotKey}.
   */
  private readonly _reservedSnapshotKeys: Set<string> = new Set();

  // Events drained by createBeaconPayload(), held until sendBeacon confirms or fails
  private _lastBeaconEvents: QueuedEvent[] = [];
  private _lastBeaconWasmJson: string | null = null;

  // Cached remote config JSON from the last successful fetchRemoteConfig() call.
  // Stored on the JS side because WASM uses NoopHttpClient and cannot fetch itself.
  private _remoteConfigJson: string | null = null;

  // JS fallback session with timeout-based rotation (matches Rust 30min default)
  private _sessionId: string = generateUUID();
  private _sessionLastActivity: number = Date.now();
  private readonly _sessionTimeoutMs: number = DEFAULT_SESSION_TIMEOUT_MS;

  // JS-side before_send hook. Applied before delegating to the WASM core
  // so that hook returning null prevents the event from being queued at all.
  // Mirrors the Swift / Kotlin set_before_send behavior; WASM does not yet
  // expose set_before_send so we apply the hook on the JS side and rely on
  // queue-depth gating in track()/screen() for the dropped case.
  private _beforeSendHook: BeforeSendHook | null = null;

  // JS-side super-properties + timed events + groups, kept in sync with the
  // Rust core when WASM is loaded. When WASM is NOT loaded (rare —
  // wasm-loader failure) these provide a JS fallback so the public API still
  // behaves correctly.
  private _superProperties: Record<string, unknown> = {};
  private _superPropertiesOnceKeys: Set<string> = new Set();
  private _timedEvents: Map<string, number> = new Map();
  private _groups: Record<string, string> = {};
  /**
   * Legacy singleton group, mirrored from the `company` group type for v3
   * servers. Rust keeps the same separate slot (`client.rs:1220-1227`), which
   * is why this is not derived from `_groups` on read.
   */
  private _groupId: string | null = null;

  /**
   * Breadcrumb for `previous_screen_name` / `previous_screen_class`. Advanced
   * only after a `screen()` event survives every drop gate, so the next screen
   * never points at one that was discarded (client.rs:973-976).
   */
  private _lastScreenName: string | null = null;
  private _lastScreenClass: string | null = null;

  /**
   * The `events` block of the last fetched remote config, parsed once at fetch
   * time. `null` means "no config" — which is allow-everything.
   */
  private _eventsConfig: FallbackEventsConfig | null = null;

  /**
   * Sliding-window rate limits for the fallback engine. Only consulted on that
   * path — when the Rust core is live it runs its own (client.rs:764), and
   * checking here too would spend the budget twice for one event.
   */
  private readonly rateLimiter = new RateLimiter();

  // Tier 4: feature flag listeners. The Rust core has its own listener slot
  // but it's not exposed via the WASM API, so we maintain the subscriber set
  // JS-side and fan out manually after `reloadFeatureFlags()` /
  // `setFeatureFlagBootstrap()` complete. New subscribers are NOT fired with
  // the current state — callers receive the next refresh only. Pass `null` to
  // un-subscribe via the returned function.
  private _featureFlagListeners: Set<FeatureFlagsListener> = new Set();

  /**
   * The three feature engines for the JS path. Each is a port of its Rust
   * counterpart (`feature_flags.rs`, `surveys.rs`, `skan.rs`); consulted ONLY
   * when `this.wasm` is null, so the Rust core stays the single evaluator
   * wherever it can run and there is never a second answer in play.
   *
   * They exist because all three surfaces used to be `if (!this.wasm) return
   * <empty>`, which on React Native — where the JS engine is the only engine —
   * meant every flag read returned undefined, every survey query returned [],
   * and no SKAN conversion value was ever produced. Whole features, dark for
   * 100% of RN installs.
   */
  private readonly flagEngine = new FeatureFlagEngine();
  private readonly surveyEngine = new SurveyEngine();
  private readonly skanEngine = new SkanEngine();

  /**
   * The `skan` block of the last fetched remote config, held for
   * `skanConfigureFromRemoteConfig()`. `undefined` means no config has been
   * fetched yet, which that call treats as "do not clobber a manual preset"
   * (client.rs:1649-1651); an explicit `null` disables (client.rs:1655-1660).
   */
  private _skanConfig: unknown = undefined;

  /**
   * Session id the flag exposure dedup cache belongs to. A rotation clears the
   * cache so the new session re-emits one `$feature_flag_called` per flag —
   * port of `refresh_flag_session` (client.rs:2319-2326).
   */
  private _flagSessionId: string | null = null;

  // ── `$sdk_health` state ─────────────────────────────────────────────
  /** Monotonic-since-install counters. Hydrated from persistence at construction. */
  private readonly _health = new HealthCounters();
  /** The `health` block of the last fetched remote config; `null` = never fetched. */
  private _healthConfig: HealthRemoteConfig | null = null;
  /** Re-entrancy latch: the beacon's own enqueue must not emit a second beacon. */
  private _healthEmitting = false;
  /** Flush ticks since the last beacon — one half of the cadence (§4.2). */
  private _healthFlushTicks = 0;
  /** Wall clock at construction, for `$health_uptime_s`. */
  private readonly _startedAtMs = Date.now();
  /** Bytes written by the most recent crash-safety snapshot, or null. */
  private _snapshotBytes: number | null = null;
  /** Wall clock of the last successful `/config` fetch, or null. */
  private _configFetchedAtMs: number | null = null;
  private _configEtag: string | null = null;
  private _configVersion: string | null = null;
  /** Open-ended tokens for subsystems known-degraded on this build. */
  private readonly _healthDegraded: Set<string> = new Set();
  /** Events handed to `sendBeacon`, held until the outcome is known. */
  private _lastBeaconInflight = 0;

  private constructor(init: LayersCoreInit, persistence: PersistenceBackend) {
    const { config, httpClient } = init;
    this.appId = config.appId;
    this.baseUrl = (config.baseUrl ?? DEFAULT_BASE_URL).replace(/\/$/, '');
    this.trackingBaseUrl = (config.trackingBaseUrl ?? DEFAULT_TRACKING_BASE_URL).replace(/\/$/, '');
    this.attStatusProvider = init.attStatusProvider;
    this.environment = config.environment;
    this.flushThreshold = config.flushThreshold ?? DEFAULT_FLUSH_THRESHOLD;
    this.maxQueueSize = config.maxQueueSize ?? DEFAULT_MAX_QUEUE_SIZE;
    this.maxBatchSize = config.maxBatchSize ?? DEFAULT_MAX_BATCH_SIZE;
    this.flushIntervalMs = config.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS;
    this.enableDebug = config.enableDebug ?? false;
    this.debugMode = config.debug ?? false;
    this.consentRequired = config.consentRequired ?? false;
    this.respectDnt = config.respectDnt ?? false;

    // Package-identity half of X-SDK-Version. The engine token is appended by
    // `sdkVersionHeader()` at header-build time, because which engine is live
    // is not known until the WASM constructor below has been tried.
    this.sdkVersion = config.sdkVersion
      ? `${config.sdkVersion} core-wasm/${PACKAGE_VERSION}`
      : `core-wasm/${PACKAGE_VERSION}`;

    this.httpClient = httpClient;
    this.persistence = persistence;

    // BEFORE anything that can drop an event — queue hydration in `init()` runs
    // against a constructed core, and its discards have to land in a loaded
    // counter rather than in one that is about to be overwritten by a load.
    this.loadHealthCounters();

    // If DebugView is enabled, hydrate (or mint) the persistent debug token.
    // The token is persisted as raw UTF-8 bytes under the key `debug_token`.
    if (this.debugMode) {
      this.debugToken = this.loadOrMintDebugToken();
    }

    // Try to initialize the WASM core. The constructor is whatever a loader
    // registered with wasm-state.ts; when none did (React Native, or a failed
    // load) it is null and we stay on the JS engine below.
    ensureWasm();
    const WasmCtor = getWasmCtor();
    if (isWasmReady() && WasmCtor) {
      try {
        const wasmConfig: Record<string, unknown> = {
          appId: config.appId,
          environment: config.environment,
          baseUrl: config.baseUrl,
          trackingBaseUrl: config.trackingBaseUrl,
          flushThreshold: config.flushThreshold,
          maxQueueSize: config.maxQueueSize,
          maxBatchSize: config.maxBatchSize,
          enableDebug: config.enableDebug,
          sdkVersion: config.sdkVersion,
          // Tier 9 (privacy posture)
          respectDnt: config.respectDnt,
          cookielessMode: config.cookielessMode,
          consentRequired: config.consentRequired,
          // The durable store, handed to the Rust core as four synchronous
          // callbacks. Without it the core ran on NullPersistence and every
          // page load minted a new install — see the bridge doc block on
          // `wasmPersistenceBridge`.
          persistence: this.wasmPersistenceBridge()
        };
        // Tier 4 — bootstrap data for feature flags. JS callers may pass
        // either camelCase (FeatureFlagBootstrap) or snake_case
        // (FeatureFlagBootstrapData); normalize before handing off to
        // the WASM constructor.
        const bootstrap = normalizeBootstrap(config.bootstrap);
        if (bootstrap) wasmConfig.bootstrap = bootstrap;
        this.wasm = new WasmCtor(wasmConfig);
      } catch (e) {
        if (this.enableDebug) {
          console.warn('[Layers] WASM initialization failed:', e);
        }
        this.wasm = null;
      }
    }

    // JS-engine feature seeding. Runs only when the Rust core did NOT come up,
    // so the two never hold divergent state for the same install.
    if (!this.wasm) {
      // Tier 4 SSR bootstrap — the Rust core takes this through its
      // constructor above (client.rs:523-526); here it seeds the JS engine.
      const bootstrap = normalizeBootstrap(config.bootstrap);
      if (bootstrap) {
        this.flagEngine.setBootstrap(bootstrap.feature_flags, bootstrap.feature_flag_payloads);
      }
      // Hydrate the install-scoped SKAN floor so a relaunch does not re-post a
      // value Apple already holds (client.rs:537-541). The WASM path cannot do
      // this — its persistence is NullPersistence (wasm_api.rs:66) — so this is
      // durability the JS engine has and the Rust-on-WASM engine does not.
      try {
        const stored = this.persistence.read(SKAN_VALUE_PERSISTENCE_KEY);
        if (stored && stored.length > 0) this.skanEngine.seedValue(stored[0]!);
      } catch {
        // Persistence unavailable — start from zero.
      }
    }

    // Tier 9: web wrappers — sniff the browser DNT signal and record it.
    //
    // This sits OUTSIDE the WASM branch on purpose. It used to run only when
    // a Rust core had just been constructed, so on the JS engine — every
    // React Native install, and every web page whose binary failed to load —
    // `respectDnt: true` was configuration the SDK accepted and then ignored.
    // `setDntSignal` stores the signal for both engines and forwards it to
    // the Rust core when there is one.
    if (this.respectDnt && typeof navigator !== 'undefined') {
      // Check both WHATWG / W3C / IE / Mozilla legacy variants.
      const nav = navigator as Navigator & {
        doNotTrack?: string;
        msDoNotTrack?: string;
      };
      const win = (typeof window !== 'undefined' ? window : undefined) as
        | (Window & { doNotTrack?: string })
        | undefined;
      const dntRaw = nav.doNotTrack ?? nav.msDoNotTrack ?? win?.doNotTrack ?? null;
      this.setDntSignal(dntRaw === '1' || dntRaw === 'yes');
    }

    // Known-degraded subsystems on this engine. `identify`, `setUserProperties`,
    // `setUserPropertiesOnce` and the increment/append/union/unset verbs are all
    // `if (this.wasm)` no-ops here, so on React Native — where this engine is
    // the ONLY engine — every one of them is a silent no-op. That is a live
    // shipping loss, and surfacing it in the fleet inventory is exactly what
    // `$health_degraded` is for.
    if (!this.wasm) this._healthDegraded.add('user_properties_unsupported');
  }

  // ── `$sdk_health` — counters ────────────────────────────────────────

  /**
   * Record `count` events (or, for the three file-counted reasons, `count`
   * persisted FILES) as having stopped existing for `reason`.
   *
   * Every discard site in this file calls this. The counter is in-memory; a
   * `sampled_out` increment at a 0.05 rate must never cause a disk write.
   * Durability comes from the three flush points — beacon emit, `doFlush()`,
   * and `shutdown()` — which gives the counters exactly the crash-loss window
   * the event queue already has. The beacon is therefore never MORE
   * trustworthy than the data it describes, which is the property that makes
   * it safe to reason from.
   */
  private recordDrop(reason: HealthDropReason, count = 1): void {
    if (count <= 0) return;
    this._health.dropped[reason] = (this._health.dropped[reason] ?? 0) + count;
    this._health.lastDropReason = reason;
    this._health.lastDropAtMs = Date.now();
    this._health.dirty = true;
  }

  /**
   * Record that the SDK took ownership of one event.
   *
   * Counted at the INGEST boundary — the top of `track()` / `screen()`, after
   * argument validation and before every drop gate — not at the queue push.
   * This is what makes the reconciler's identity exact: every event the SDK
   * accepted has exactly one fate (delivered, handed off unacked, dropped for a
   * named reason, still queued, or in flight), and a killswitched or
   * sampled-out event has the fate "dropped". Counting only queue pushes would
   * leave every build-time drop on the right-hand side with nothing on the
   * left, and the residual would read as silent loss on a healthy install.
   *
   * Property-validation rejection is deliberately NOT counted: it THROWS to the
   * caller, so the host sees it. These counters are scoped to invisible losses.
   */
  private recordAccepted(count = 1): void {
    this._health.accepted += count;
    this._health.dirty = true;
  }

  /** Classify a delivery outcome, mirroring the verdict split in the Rust core. */
  private static healthStatusClass(status: number): HealthStatusClass {
    if (status >= 200 && status < 300) return '2xx';
    if (status === 0) return 'network';
    if (status >= 500) return '5xx';
    if (status === 403 || status === 408 || status === 429) return '4xx_retry';
    return '4xx_drop';
  }

  /**
   * Account for one delivery attempt of `count` events: the batch's status
   * class, and the fate of the events themselves.
   */
  private recordFlushOutcome(
    status: number,
    verdict: 'delivered' | 'retry_later' | 'drop',
    count: number
  ): void {
    const cls = LayersCore.healthStatusClass(status);
    this._health.batchesAttempted += 1;
    this._health.batchesByStatus[cls] = (this._health.batchesByStatus[cls] ?? 0) + 1;
    this._health.inflight = Math.max(0, this._health.inflight - count);
    if (verdict === 'delivered') {
      this._health.delivered += count;
    } else if (verdict === 'drop') {
      this.recordDrop('delivery_dropped', count);
    }
    // `retry_later` is NOT a drop — the events go back on the queue and the
    // next gated flush tick retries them.
    this._health.dirty = true;
  }

  /**
   * Whether a rolling crash-safety snapshot currently exists on disk.
   *
   * The one question that separates "the shutdown write failed and the events
   * come back next launch" from "the events are gone". Only the shutdown path
   * asks it, and only to decide whether a failed write is loss.
   */
  private hasDurableSnapshot(): boolean {
    try {
      if (this.persistence.listKeys(EVENTS_SNAPSHOT_KEY).length > 0) return true;
      const base = this.persistence.read(EVENTS_SNAPSHOT_KEY);
      return base !== null && base.length > 0;
    } catch {
      // A store that cannot even be listed is not one to credit with a copy.
      return false;
    }
  }

  /** Which persistence backend is durable behind this core, for the beacon. */
  private healthPersistBackend(): 'file' | 'memory' | 'null' | 'bridge' {
    const store = this.persistence as { __layersBackend?: string };
    if (store.__layersBackend === 'null') return 'null';
    if (store.__layersBackend === 'memory') return 'memory';
    return 'file';
  }

  /**
   * Tokens that were explicitly MARKED as degraded. This is what gets
   * PERSISTED, and it deliberately excludes `file_counted_drops`.
   *
   * Mirrors `degraded()` in the Rust core.
   */
  private healthMarkedDegraded(): string[] {
    return [...this._healthDegraded].sort();
  }

  /**
   * Every degraded token to EMIT, marked plus derived. Mirrors `degraded_now()`
   * in the Rust core.
   *
   * `file_counted_drops` is DERIVED from the three file-counted buckets on
   * every beacon rather than remembered, because a remembered token can
   * disagree with the buckets in BOTH directions. The obvious direction is a
   * stale token pinned on for an install whose buckets are now zero. The worse
   * direction is the opposite: a record round-tripped through something that
   * does not model the array comes back with the token MISSING while the counts
   * behind it are still there — and then the server marks a lower bound
   * `exact`, which is the error that hides real loss rather than inventing it.
   * Deriving closes both directions at once.
   */
  private healthDegradedTokens(): string[] {
    const tokens = new Set(this._healthDegraded);
    // Wire delta #1: the identity is a LOWER BOUND whenever a file-counted
    // reason fired, because a record destroyed before parsing has an unknowable
    // event count. The reconciler keys `residual_class` off this token.
    if (FILE_COUNTED_DROP_REASONS.some((reason) => (this._health.dropped[reason] ?? 0) > 0)) {
      tokens.add('file_counted_drops');
    }
    return [...tokens].sort();
  }

  /**
   * Snapshot of the counters.
   *
   * @internal Not part of the supported SDK surface. Exists for the parity and
   * balance-identity tests, and for the future hand-off of JS-layer counts to
   * the Rust core on the WASM path (design §4.5).
   */
  healthSnapshot(): HealthSnapshot {
    const dropped = compactCounts(this._health.dropped as Record<string, number>);
    return {
      epoch: this._health.epoch,
      seq: this._health.seq,
      accepted: this._health.accepted,
      delivered: this._health.delivered,
      deliveredUnacked: this._health.deliveredUnacked,
      dropped,
      droppedTotal: sumCounts(dropped),
      inflight: this._health.inflight,
      queueDepthEngine: this.wasm ? this.wasm.queueDepth() : this.queue.length,
      queueDepthWrapper: this.wasm ? this.queue.length : 0,
      hydratedEvents: this._health.hydratedEvents,
      persistWriteFailures: this._health.persistWriteFailures,
      breakerOpens: this._health.breakerOpens,
      batchesAttempted: this._health.batchesAttempted,
      batchesByStatus: compactCounts(this._health.batchesByStatus as Record<string, number>),
      degraded: this.healthDegradedTokens()
    };
  }

  /** Totals as they stand right now, for the next beacon's `_delta` baseline. */
  private currentHealthTotals(): HealthTotals {
    const dropped = compactCounts(this._health.dropped as Record<string, number>);
    return {
      accepted: this._health.accepted,
      delivered: this._health.delivered,
      delivered_unacked: this._health.deliveredUnacked,
      dropped,
      batches_attempted: this._health.batchesAttempted,
      batches_by_status: compactCounts(this._health.batchesByStatus as Record<string, number>)
    };
  }

  /**
   * Hydrate the counters from the identity record, minting a fresh epoch when
   * there is nothing to hydrate.
   *
   * THE EPOCH IS THE POINT. A lost or corrupt identity record restarts the
   * monotonic totals at zero, and a reconciler differencing consecutive beacons
   * would compute a negative delta and read it as corruption. A new epoch
   * begins a NEW series instead, which can never produce one. `reset()`
   * deliberately does NOT mint a new epoch — see there.
   */
  private loadHealthCounters(): void {
    const h = this._health;
    let stored: Record<string, unknown> | null = null;
    try {
      const raw = this.readIdentity().health;
      if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        stored = raw as Record<string, unknown>;
      }
    } catch {
      // Unreadable record — start a fresh series below.
    }

    const num = (value: unknown): number =>
      typeof value === 'number' && Number.isFinite(value) && value >= 0 ? Math.trunc(value) : 0;

    if (stored === null || typeof stored.epoch !== 'string' || stored.epoch.length === 0) {
      h.epoch = generateUUID();
      h.dirty = true;
      return;
    }

    h.epoch = stored.epoch;
    h.seq = num(stored.seq);
    h.accepted = num(stored.accepted);
    h.delivered = num(stored.delivered);
    h.deliveredUnacked = num(stored.delivered_unacked);
    h.persistWriteFailures = num(stored.persist_write_failures);
    h.breakerOpens = num(stored.breaker_opens);
    h.batchesAttempted = num(stored.batches_attempted);
    h.hydratedEvents = num(stored.hydrated_events);
    h.lastDropReason = typeof stored.last_drop_reason === 'string' ? stored.last_drop_reason : null;
    h.lastDropAtMs = parseRfc3339(stored.last_drop_at);
    h.lastBeaconAtMs = parseRfc3339(stored.last_beacon_at);
    if (Array.isArray(stored.degraded)) {
      for (const token of stored.degraded) {
        // `file_counted_drops` is DERIVED from the counters on every beacon, so
        // restoring it would pin it on for an install whose file-counted
        // buckets are all zero.
        if (typeof token === 'string' && token !== 'file_counted_drops') {
          this._healthDegraded.add(token);
        }
      }
    }

    // A reason key this build does not know folds into `other` rather than
    // being discarded, so the totals still balance after a downgrade.
    const known = new Set<string>(HEALTH_DROP_REASONS);
    const dropped = stored.dropped;
    if (dropped && typeof dropped === 'object' && !Array.isArray(dropped)) {
      for (const [key, value] of Object.entries(dropped as Record<string, unknown>)) {
        const reason = (known.has(key) ? key : 'other') as HealthDropReason;
        h.dropped[reason] = (h.dropped[reason] ?? 0) + num(value);
      }
    }
    const byStatus = stored.batches_by_status;
    if (byStatus && typeof byStatus === 'object' && !Array.isArray(byStatus)) {
      for (const [key, value] of Object.entries(byStatus as Record<string, unknown>)) {
        h.batchesByStatus[key as HealthStatusClass] = num(value);
      }
    }
    const prev = stored.prev;
    if (prev && typeof prev === 'object' && !Array.isArray(prev)) {
      h.prev = prev as unknown as HealthTotals;
    }
  }

  /**
   * Persist the counters into the identity record, read-modify-write so a
   * concurrent `$first_open` or session-number update is not clobbered.
   *
   * Dirty-gated: `doFlush()` runs on every delivery outcome and every consent
   * change, and an unconditional write there would be a storage round-trip per
   * flush for counters that did not move.
   */
  private persistHealthCounters(force = false): void {
    const h = this._health;
    if (!force && !h.dirty) return;
    this.updateIdentity(() => true);
    h.dirty = false;
  }

  /**
   * The ledger as the in-memory counters currently stand.
   *
   * Field names, types and ORDER mirror the Rust core's persisted struct
   * exactly — see `test/fixtures/health-persisted-shape.json`, which is the
   * Rust reference verbatim and which a round-trip test diffs against. On the
   * web BOTH engines write this one record, so a fallback→WASM upgrade
   * mid-session has to read what the other side wrote; a shape it cannot parse
   * silently restarts the series (new epoch, totals to 0) and the reconciler
   * sees a fresh install where an old one continued.
   */
  private serializeHealth(): Record<string, unknown> {
    const h = this._health;
    return {
      v: HEALTH_RECORD_VERSION,
      epoch: h.epoch,
      seq: h.seq,
      accepted: h.accepted,
      delivered: h.delivered,
      delivered_unacked: h.deliveredUnacked,
      dropped: compactCounts(h.dropped as Record<string, number>),
      batches_attempted: h.batchesAttempted,
      batches_by_status: compactCounts(h.batchesByStatus as Record<string, number>),
      persist_write_failures: h.persistWriteFailures,
      breaker_opens: h.breakerOpens,
      hydrated_events: h.hydratedEvents,
      last_drop_reason: h.lastDropReason,
      last_drop_at: h.lastDropAtMs === null ? null : toRfc3339Offset(h.lastDropAtMs),
      last_beacon_at: h.lastBeaconAtMs === null ? null : toRfc3339Offset(h.lastBeaconAtMs),
      prev: h.prev,
      // Marked tokens ONLY — `file_counted_drops` is derived at emit time from
      // the buckets, never stored. See `healthDegradedTokens()`.
      degraded: this.healthMarkedDegraded()
    };
  }

  /**
   * The ledger to write, given whatever the outgoing record already holds.
   *
   * DEFENCE IN DEPTH behind {@link updateIdentity}, suggested by the Rust lane
   * after they found their own core immune to this defect for exactly this
   * reason: stamping the ledger from MEMORY at write time makes a stale
   * snapshot unable to roll it back at all, so the whole class is unreachable
   * without depending on every one of the record's writers being correct.
   *
   * The monotonic guard is the part their core does not need and this one does.
   * On the web BOTH engines write this record through the persistence bridge,
   * so "memory" is not necessarily the most advanced ledger — a JS core
   * constructed before the Rust core wrote holds an older one. Since the
   * counters are monotonic by construction, the rule that is safe in every
   * direction is simply: never write a ledger that moves backwards.
   */
  private healthForWrite(existing: unknown): Record<string, unknown> {
    const mine = this.serializeHealth();
    if (existing && typeof existing === 'object' && !Array.isArray(existing)) {
      const theirs = existing as Record<string, unknown>;
      const ahead = Number(theirs.accepted ?? 0);
      const here = Number(mine.accepted ?? 0);
      if (theirs.epoch === mine.epoch && Number.isFinite(ahead) && ahead > here) {
        return theirs;
      }
    }
    return mine;
  }

  // ── `$sdk_health` — emission ────────────────────────────────────────

  /**
   * Emit the beacon if the cadence says one is due.
   *
   * CALLED FROM FOUR PLACES, and the obvious single hook does not work. The
   * tempting one is the flush gate, which every platform consults once per
   * tick — but the wrapper checks `queueDepth() > 0` FIRST, so a fully-drained
   * healthy install never reaches it, and a killswitched install never has a
   * non-empty queue at all. The killswitch would stay exactly as invisible as
   * it is today. So: `track()`, `screen()`, the flush gate, and `shutdown()`.
   *
   * GATES BYPASSED, each deliberately (design §4.3):
   *  - remote-config killswitch / allowlist / denylist: a killswitched app must
   *    still be observable, or the killswitch itself is the silent-loss event
   *    nobody can see.
   *  - sampler: at `sampling_rate: 0.05`, 19 of every 20 beacons vanish and the
   *    reconciler's gap detection fires constantly on HEALTHY installs.
   *  - `before_send`: the hook is a PII scrubber over host event data, and the
   *    beacon carries none. A host returning `null` unconditionally — a
   *    legitimate kill-my-analytics hook — would otherwise blind the fleet.
   *    This narrows the documented contract of a public hook; it is called out
   *    in `setBeforeSend` and in PROTOCOL.md.
   *  - rate limiter: already exempt, `$`-prefixed.
   *  - survey targeting counter: a beacon must not move survey eligibility.
   *
   * GATES HONOURED: `health.enabled: false` (an explicit operator instruction
   * about the beacon itself), and consent/DNT — which gate DELIVERY, not
   * ingest, so the beacon queues like every other event and ships when the gate
   * opens. An install that never grants consent is unobservable; that is a
   * documented blind spot no mechanism can close without violating ADR 0001.
   */
  private maybeEmitHealth(force = false): void {
    // On the WASM path the Rust core owns the beacon — it can see its own
    // queue, its own breaker and its own config, none of which this layer can
    // read accurately. The counters below are still maintained here so the
    // JS-layer discard sites are ready to hand over via `record_drop`.
    if (this.wasm) return;
    if (this._healthEmitting) return;
    if (this._healthConfig?.enabled === false) return;

    const now = Date.now();
    if (!force) {
      const intervalMs = (this._healthConfig?.interval_s ?? DEFAULT_HEALTH_INTERVAL_S) * 1_000;
      const tickBudget = this._healthConfig?.flush_interval ?? DEFAULT_HEALTH_FLUSH_INTERVAL;
      // An install that has never beaconed measures the interval from process
      // start, so the FIRST beacon is on the same cadence as every later one.
      // Emitting eagerly on the first `track()` was tried and rejected: it puts
      // an extra event in every install's first batch, and at 6h/50-ticks the
      // beacon is cheap precisely because it is not per-launch.
      const since = now - (this._health.lastBeaconAtMs ?? this._startedAtMs);
      if (since < intervalMs && this._healthFlushTicks < tickBudget) return;
    }

    this._healthEmitting = true;
    try {
      const event = this.buildEvent(HEALTH_EVENT);
      // Super properties are host-controlled data and may carry PII. The beacon
      // carries operational telemetry only, so `buildEvent` alone — never
      // `mergeSuperProperties` — is the whole payload envelope.
      event.properties = this.buildHealthProperties(now) as EventProperties;

      this._healthFlushTicks = 0;
      this._health.seq += 1;
      this._health.lastBeaconAtMs = now;
      // Baseline for the NEXT beacon's deltas, taken BEFORE this beacon's own
      // enqueue so the self-count rule holds on both sides.
      this._health.prev = this.currentHealthTotals();

      this.enqueueHealthEvent(event);
      this.persistHealthCounters(true);
    } catch (e) {
      if (this.enableDebug) console.warn(`[Layers] failed to emit ${HEALTH_EVENT}:`, e);
    } finally {
      this._healthEmitting = false;
    }
  }

  /**
   * Push the beacon straight onto the queue, skipping every gate `track()`
   * applies.
   *
   * `_enqueuedCount` is deliberately NOT incremented. That counter answers one
   * question — "did `track()` accept the CALLER's event?" — and
   * `emitPendingFirstOpen` reads it across a `track()` call to decide whether
   * `$first_open` survived its gates. A beacon emitted from inside that same
   * call would move the counter and make a suppressed `$first_open` look
   * accepted, retiring it forever with nothing ever sent.
   *
   * `$health_accepted_total` DOES include the beacon (design §4.6): it enters a
   * queue, so excluding it would make the accepted count disagree with the
   * queue's actual contents and break the identity for a worse reason than it
   * fixes. The reconciler subtracts it with `$health_seq - 1`.
   */
  private enqueueHealthEvent(event: QueuedEvent): void {
    if (this.queue.length >= this.maxQueueSize) {
      this.queue.shift();
      this.recordDrop('queue_overflow');
    }
    this.queue.push(withoutIdfa(event));
    this.recordAccepted();
  }

  /** The frozen `$health_*` payload. See `$defs/SdkHealthProperties` in the schema. */
  private buildHealthProperties(now: number): Record<string, unknown> {
    const h = this._health;
    const prev = h.prev;
    const dropped = compactCounts(h.dropped as Record<string, number>);
    const droppedTotal = sumCounts(dropped);
    const byStatus = compactCounts(h.batchesByStatus as Record<string, number>);

    const explicitConsent =
      typeof this.consent.analyticsStorage === 'boolean' ||
      typeof this.consent.analytics === 'boolean';
    const consentAnalytics = explicitConsent
      ? this.analyticsAllowed()
        ? 'granted'
        : 'denied'
      : this.analyticsAllowed()
        ? 'default_granted'
        : 'default_denied';

    const breakerState =
      this.circuitState === 'half-open' ? 'half_open' : (this.circuitState as 'closed' | 'open');

    const sinceInstallMs = this.firstOpenTime ? now - Date.parse(this.firstOpenTime) : 0;

    return {
      $health_v: HEALTH_PAYLOAD_VERSION,
      // 1-based per epoch. A GAP means a beacon was lost; the monotonic totals
      // stay valid across it, which is why both forms ship.
      $health_seq: h.seq + 1,
      $health_epoch: h.epoch,
      // Repeated in the BODY, not just the header, because `sendBeacon` batches
      // arrive with no SDK headers whatsoever — and page hide is exactly when a
      // web install ships the batch most likely to carry loss evidence.
      $health_engine: this.isWasmActive() ? 'wasm' : 'js',
      $health_sdk_version: this.sdkVersionHeader(),
      $health_window_s:
        h.lastBeaconAtMs === null
          ? null
          : Math.max(0, Math.round((now - h.lastBeaconAtMs) / 1_000)),
      $health_uptime_s: Math.max(0, Math.round((now - this._startedAtMs) / 1_000)),
      $health_since_install_s: Number.isFinite(sinceInstallMs)
        ? Math.max(0, Math.round(sinceInstallMs / 1_000))
        : 0,

      $health_accepted_total: h.accepted,
      $health_accepted_delta: Math.max(0, h.accepted - (prev?.accepted ?? 0)),
      $health_delivered_total: h.delivered,
      $health_delivered_delta: Math.max(0, h.delivered - (prev?.delivered ?? 0)),
      $health_delivered_unacked_total: h.deliveredUnacked,
      $health_delivered_unacked_delta: Math.max(
        0,
        h.deliveredUnacked - (prev?.delivered_unacked ?? 0)
      ),
      // Redundant with the reason map ON PURPOSE — a mismatch is a
      // serialization bug, and the reconciler asserts on it.
      $health_dropped_total: droppedTotal,
      $health_dropped_delta: Math.max(0, droppedTotal - sumCounts(prev?.dropped ?? {})),

      $health_dropped_by_reason_total: dropped,
      $health_dropped_by_reason_delta: deltaCounts(dropped, prev?.dropped),
      $health_last_drop_reason: h.lastDropReason,
      $health_last_drop_at: h.lastDropAtMs === null ? null : new Date(h.lastDropAtMs).toISOString(),

      $health_queue_depth_engine: this.queue.length,
      // Always 0 on this engine: the JS layer's second queue only exists when
      // the Rust core owns the first one.
      $health_queue_depth_wrapper: 0,
      $health_queue_capacity: this.maxQueueSize,
      $health_inflight_events: h.inflight,

      $health_batches_attempted_total: h.batchesAttempted,
      $health_batches_by_status_total: byStatus,
      $health_batches_by_status_delta: deltaCounts(byStatus, prev?.batches_by_status),

      $health_breaker_state: breakerState,
      $health_breaker_opens_total: h.breakerOpens,
      $health_retry_after_active: this.isRetryAfterActive(),
      $health_retry_after_remaining_s: Math.round(this.retryAfterRemainingMs() / 1_000),

      $health_config_version: this._configVersion,
      $health_config_etag: this._configEtag,
      $health_config_age_s:
        this._configFetchedAtMs === null
          ? null
          : Math.max(0, Math.round((now - this._configFetchedAtMs) / 1_000)),
      $health_config_ever_fetched: this._configFetchedAtMs !== null,

      $health_persist_backend: this.healthPersistBackend(),
      $health_persist_write_failures_total: h.persistWriteFailures,
      $health_snapshot_bytes: this._snapshotBytes,
      $health_hydrated_events_total: h.hydratedEvents,

      $health_consent_analytics: consentAnalytics,
      $health_dnt_active: this.dntActive,
      // A long run of `false` against a rising queue depth is the pre-loss
      // signature the reconciler alerts on.
      $health_flush_allowed: this.flushAllowed(),

      $health_degraded: this.healthDegradedTokens()
    };
  }

  /**
   * Expose this core's persistence backend to the Rust core as four
   * synchronous callbacks.
   *
   * WHY THIS EXISTS. `core/src/wasm_api.rs` used to build the Rust core with
   * `NullPersistence`, so everything the engine keeps durable —
   * `anonymous_id`, `device_id`, `first_open_time`, `first_open_emitted`, the
   * session counter, the set-once ledger, super-properties, the SKAN
   * conversion floor — lived in memory that dies with the page. On the web a
   * RELOAD was therefore indistinguishable from a fresh install: rotated
   * pseudonymous ids that server-side stitching cannot join, `session_number`
   * pinned at 1, and another `$first_open`. Production carried 6,431
   * `$first_open` events against 2,962 installs over seven days.
   *
   * `PersistenceBackend` is already synchronous — `localStorage` and the RN
   * pre-loaded AsyncStorage mirror both are — which is the whole reason this
   * shape works: `PersistenceBackend` in Rust returns values, not futures, so
   * an async store could not be bridged without changing the trait on every
   * platform.
   *
   * Each method swallows host-store failures the way `LocalStoragePersistence`
   * does. A full quota or a disabled store degrades identity to
   * per-page-load, which is what it already was; letting the exception cross
   * the FFI boundary would fail SDK construction instead.
   */
  private wasmPersistenceBridge(): {
    read(key: string): Uint8Array | null;
    write(key: string, data: Uint8Array): void;
    delete(key: string): void;
    listKeys(prefix: string): string[];
  } {
    const store = this.persistence;
    const health = this._health;
    return {
      read(key) {
        try {
          return store.read(key);
        } catch {
          return null;
        }
      },
      write(key, data) {
        try {
          store.write(key, data);
        } catch {
          // Quota or unavailable store — same silent degrade as
          // LocalStoragePersistence, now with a counter behind it so a fleet
          // whose durability has quietly stopped working is visible.
          health.persistWriteFailures += 1;
          health.dirty = true;
        }
      },
      delete(key) {
        try {
          store.delete(key);
        } catch {
          // Ignore.
        }
      },
      listKeys(prefix) {
        try {
          return store.listKeys(prefix);
        } catch {
          return [];
        }
      }
    };
  }

  static init(init: LayersCoreInit): LayersCore {
    const { config, persistence } = init;

    if (!config.appId) {
      throw new LayersError('INVALID_CONFIG', 'appId must not be empty');
    }
    if (!init.httpClient) {
      throw new LayersError(
        'INVALID_CONFIG',
        'httpClient must be provided for WASM/JS environments'
      );
    }

    // Null-object persistence: hosts that pass none get deliberate no-ops.
    /* eslint-disable no-empty-function -- intentional null-object pattern */
    const resolvedPersistence = persistence ?? {
      __layersBackend: 'null',
      write() {},
      read() {
        return null;
      },
      delete() {},
      listKeys() {
        return [];
      }
    };
    /* eslint-enable no-empty-function */

    const core = new LayersCore(init, resolvedPersistence);

    // Hydrate persisted events into the JS fallback queue.
    // When WASM is loaded, persisted events from a previous session are
    // rehydrated into the JS queue and flushed via the JS path. This is
    // acceptable because persisted events were already accepted by the Rust
    // core in the previous session.
    try {
      const keys = resolvedPersistence.listKeys(EVENTS_KEY_PREFIX);
      // Hydration priority: the live rolling snapshot first, then shutdown
      // files, then legacy per-flush batch keys. Lexicographic order puts
      // `events_snapshot` AFTER `events_batch_*`, so a full queue (or a
      // legacy install with many batch files) could otherwise delete the
      // live snapshot unread. Mirrors the same fix in core/src/client.rs.
      const keyPriority = (k: string): number =>
        k.startsWith(EVENTS_SNAPSHOT_KEY) ? 0 : k.startsWith('events_shutdown') ? 1 : 2;
      keys.sort((a, b) => keyPriority(a) - keyPriority(b) || a.localeCompare(b));
      // Hard cap on files loaded per init, mirroring MAX_PERSISTED_FILES in
      // core/src/client.rs — bounds the disk reads on legacy installs that
      // accumulated per-flush `events_batch_*` files. Keys beyond the cap are
      // deleted unread.
      //
      // The cap now applies to THAT legacy class only (priority 2). Its
      // original justification — "the priority sort guarantees the live
      // snapshot and shutdown files come first, so only legacy leftovers can
      // be dropped" — held while each of those was a single key. Both are
      // chunked now, so a large queue produces many priority-0/1 keys: at
      // ~5 KB per event a full 10K queue is ~100 chunks. Counting them would
      // delete the tail of the queue unread, and after a successful shutdown
      // the rolling snapshot is gone, so those chunks are the ONLY copy.
      //
      // Exempting them costs nothing: their count is bounded by the queue that
      // produced them, so reading them all is exactly proportional to the
      // queue about to be hydrated anyway.
      const maxPersistedFiles = 100;
      let loaded = 0;
      let queueFull = false;
      for (const key of keys) {
        const isEngineWritten = keyPriority(key) < 2;
        if (!isEngineWritten && loaded >= maxPersistedFiles) {
          // Beyond the load cap — legacy leftovers, delete unread.
          //
          // Counted per FILE, not per event: the file is destroyed without ever
          // being parsed, so its event count is unknowable. The beacon carries
          // `file_counted_drops` whenever this fires, which tells the
          // reconciler to read the balance identity as a lower bound.
          core.recordDrop('persisted_file_over_cap');
          resolvedPersistence.delete(key);
          continue;
        }
        if (queueFull) {
          // Queue is full — leave the remaining files on disk untouched so
          // the next init can hydrate them (deleting them unread lost data).
          core.reserveSnapshotKey(key);
          continue;
        }
        if (!isEngineWritten) loaded += 1;
        const data = resolvedPersistence.read(key);
        if (data) {
          let unconsumed: QueuedEvent[] | null = null;
          try {
            const raw = new TextDecoder().decode(data);
            const parsed = JSON.parse(raw);
            // Persisted data may be a batch { events: [...] } or a raw array
            const events: QueuedEvent[] = Array.isArray(parsed) ? parsed : (parsed.events ?? []);
            for (let i = 0; i < events.length; i++) {
              if (core.queue.length < core.maxQueueSize) {
                core.queue.push(withoutIdfa(events[i]!));
                // Hydration is NOT an acceptance: these events were counted
                // into `accepted` by the launch that first queued them, and
                // that total is durable, so counting them again would double
                // the left-hand side of the balance identity.
                core._health.hydratedEvents += 1;
                core._health.dirty = true;
              } else {
                if (core.enableDebug) {
                  console.warn('[Layers] Rehydration paused: queue full');
                }
                queueFull = true;
                unconsumed = events.slice(i).map(withoutIdfa);
                break;
              }
            }
          } catch {
            // Corrupted data — skip. Counted per FILE (see the over-cap branch
            // above): the parse is what failed, so the event count is unknown.
            core.recordDrop('persisted_file_unreadable');
          }
          if (unconsumed && unconsumed.length > 0) {
            // The queue filled mid-file. Write the unconsumed remainder back
            // under the same key instead of deleting it — deleting a
            // partially-loaded file dropped the rest permanently.
            core.reserveSnapshotKey(key);
            try {
              resolvedPersistence.write(key, new TextEncoder().encode(JSON.stringify(unconsumed)));
            } catch {
              // Write-back failed — keep the ORIGINAL full file. Deliberate
              // at-least-once trade-off: the next launch re-hydrates the
              // already-consumed leading events too (duplicates, absorbed by
              // server-side event_id dedup), but the unconsumed tail is
              // never lost. Deleting here would choose loss over duplicates.
            }
          } else {
            resolvedPersistence.delete(key);
          }
        }
      }
    } catch {
      // Persistence unavailable during hydration — continue. The durable store
      // holds an unknown number of undelivered events that this launch will
      // never see, so it is counted as one unreadable record rather than left
      // as an unexplained residual.
      core.recordDrop('persisted_file_unreadable');
    }

    // BEFORE the mint, not after it. A swapped-in core that mints its own ids
    // and then adopts the replaced core's leaves the durable record holding a
    // throwaway identity between the two writes — see `LayersCoreInit.migration`
    // for why that window costs installs. Seating first makes `initIdentity`
    // RESUME rather than mint, so the record is written once and never names an
    // identity that was not on the wire.
    if (init.migration) core.seedIdentityBeforeMint(init.migration.identity);

    // Identity + session counter first, so a parked `$first_open` is built
    // with the same anonymous_id / device_id / session_number as every other
    // event. AFTER hydration: the `$first_open` crash-repair branch has to see
    // events restored from a previous run's snapshot.
    core.initIdentity();
    core.initFirstOpen();
    core.reconcileWasmFirstOpen();

    // The rest of the hand-off — queued-event reconciliation, the `$first_open`
    // settle, consent, super properties, groups, device context — still runs
    // after hydration and identity, exactly where a caller doing this in two
    // steps would have put it.
    if (init.migration) core.applyMigrationState(init.migration);

    core.startFlushTimer();
    return core;
  }

  track(
    eventName: string,
    properties?: EventProperties,
    userId?: string,
    anonymousId?: string
  ): void {
    this.checkNotShutDown();

    if (!eventName) {
      throw new LayersError('INVALID_ARGUMENT', 'eventName must not be empty');
    }

    // A parked `$first_open` must precede the first user event even if the
    // wrapper never supplied device context. No-op when nothing is pending —
    // including the recursive call this triggers, and every WASM-path call
    // (only the fallback ever parks). Mirrors the same call at the head of
    // `track()` in core/src/client.rs.
    this.emitPendingFirstOpen();

    // Validate properties before passing to either path
    if (properties) {
      LayersCore.validateProperties(properties, 'Event properties');
    }

    // Cadence hook 1 of 4. BEFORE the config / rate-limit / sampler gates, so a
    // killswitched or fully sampled-out install still reports.
    //
    // AND BEFORE `recordAccepted()` BELOW — that order is load-bearing. The
    // beacon reports the counters as a closed set, and THIS call's event has no
    // fate yet: it is neither queued, nor delivered, nor dropped. Admitting it
    // first and snapshotting second would put it on the identity's left-hand
    // side with nothing on the right, so every beacon would ship a residual of
    // exactly 1 — read server-side as one silently lost event per beacon, on
    // every healthy install in the fleet.
    this.maybeEmitHealth();

    // The SDK now owns this event: every path below either queues it or drops
    // it for a named reason, which is what makes the reconciler's identity
    // exact. Counted after validation, because validation THROWS — a failure
    // the host can see, and therefore out of scope for these counters.
    this.recordAccepted();

    // When WASM is loaded, delegate entirely to the Rust core.
    // Rust handles: consent, rate limiting, sampling, remote config filtering,
    // event building (with correct timestamps, event IDs, session IDs), and
    // queue management. No JS event building or JS queue involved.
    if (this.wasm) {
      // BEFORE_SEND ORDERING, WASM PATH: the hook must run here, before the
      // hand-off, because `wasm_api.rs` exposes no `set_before_send` — this
      // is the only seam that exists. It is the one deliberate ordering
      // divergence from the Rust pipeline; see the fallback branch below for
      // the contract this engine implements everywhere it can.
      const filtered = this.applyBeforeSend(eventName, properties, userId, anonymousId);
      if (filtered === null) return; // dropped by hook
      if (filtered) {
        eventName = filtered.event;
        properties = filtered.properties as EventProperties;
        userId = filtered.userId;
        anonymousId = filtered.anonymousId;
      }

      const depthBefore = this.wasm.queueDepth();
      try {
        this.wasm.track(eventName, properties ?? null, userId ?? null, anonymousId ?? null);
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM track failed:', e);
        this.recordDrop('engine_error');
        return;
      }
      const depthAfter = this.wasm.queueDepth();

      // A depth that did not grow means the Rust core dropped the event — for
      // a reason only IT knows, because this side cannot see which gate fired.
      // `other` is the honest bucket, and it keeps the identity balanced until
      // `record_drop` lets the two ledgers merge (design §4.5).
      if (depthAfter <= depthBefore) this.recordDrop('other');

      // If queue depth increased, auto-flush if threshold reached
      if (depthAfter > depthBefore && depthAfter >= this.flushThreshold) {
        this.doFlush();
      }
      return;
    }

    // JS fallback path (no WASM).
    //
    // THE PIPELINE ORDER BELOW IS THE CONTRACT, and it is core/src/client.rs:
    // 731-888 step for step: remote config → rate limit → build → super
    // properties → $duration_ms → commerce promotion → per-call id overrides
    // → sampler → before_send → enqueue. Two consequences are load-bearing
    // rather than incidental:
    //   - `before_send` runs LAST, so a hook sees the fully-stamped event
    //     (super properties merged, commerce keys promoted) and is never
    //     consulted about an event the SDK has already decided to drop.
    //   - super properties merge BEFORE commerce promotion, so a globally
    //     registered `currency`/`price` lands in the top-level revenue
    //     columns exactly as a per-call one does.
    //
    // Tier 9 (Consent Mode v2): events ALWAYS queue. Flushing is gated
    // separately by `flushAllowed()` so that on consent grant we can drain the
    // events queued while it was denied. The exception is ad_user_data and
    // ad_personalization: those don't drop events but tag them with metadata
    // flags so the ingest service can omit forwarding (see `buildEvent`).
    // Mirrors core/src/client.rs:751-755 — there is deliberately NO consent
    // check here. Dropping at ingest destroys the crash window between "user
    // opens the app" and "user answers the consent banner", which is exactly
    // the backlog the grant is supposed to release.
    //
    // The circuit breaker is likewise NOT consulted here. It is a DELIVERY
    // control (adr/0001-rust-owned-delivery-policy.md) — gating ingest on it
    // discarded events precisely during the outage the queue exists to
    // survive, and threw `CIRCUIT_OPEN` out of `track()` into host app code.
    const verdict = this.fallbackConfigVerdict(eventName);
    if (verdict !== 'allowed') {
      if (this.enableDebug) console.log(`[Layers] Event denied by remote config: ${eventName}`);
      this.recordDrop(verdict);
      return;
    }

    if (!this.rateLimiter.checkAndRecord(eventName)) {
      if (this.enableDebug) console.log(`[Layers] Event rate limited: ${eventName}`);
      this.recordDrop('rate_limited');
      return;
    }

    // Tier 8: feed the survey targeting engine's event-history counter, AFTER
    // the config + rate-limit gates so a dropped event never leaks into
    // targeting state. The survey auto-events are skipped to avoid a feedback
    // loop (client.rs:769-778).
    if (!SURVEY_AUTO_EVENT_NAMES.includes(eventName as never)) {
      this.surveyEngine.recordEvent(eventName);
    }

    const event = this.buildEvent(eventName);

    const merged = this.mergeSuperProperties(properties);
    if (merged) {
      event.properties = merged;
    }

    // A `timeEvent(name)` timer is one-shot and keyed by exact event name
    // (client.rs:786-791).
    const startedAt = this._timedEvents.get(eventName);
    if (startedAt !== undefined) {
      this._timedEvents.delete(eventName);
      event.$duration_ms = Math.max(0, Date.now() - startedAt);
    }

    this.promoteCommerceKeys(eventName, event);
    if (userId) {
      event.app_user_id = userId;
      event.user_id = userId;
    }
    if (anonymousId) {
      event.anonymous_id = anonymousId;
    }

    if (!this.fallbackSampled(eventName)) {
      if (this.enableDebug) console.log(`[Layers] Event sampled out: ${eventName}`);
      this.recordDrop('sampled_out');
      return;
    }

    // `applyBeforeSendToEvent` records its own drop reason — it is the only
    // caller that can tell "the hook returned null" from "the hook threw".
    if (!this.applyBeforeSendToEvent(event, false)) return; // dropped by hook

    this.enqueue(event);
    this.maybeFlush();
  }

  screen(
    screenName: string,
    properties?: EventProperties,
    userId?: string,
    anonymousId?: string
  ): void {
    this.checkNotShutDown();

    if (!screenName) {
      throw new LayersError('INVALID_ARGUMENT', 'screenName must not be empty');
    }

    // A parked `$first_open` must precede the first user event (see track()).
    this.emitPendingFirstOpen();

    // Validate properties before passing to either path
    if (properties) {
      LayersCore.validateProperties(properties, 'Screen properties');
    }

    // See `track()` — same cadence hook, same ingest-boundary accounting, and
    // the same load-bearing order between them.
    this.maybeEmitHealth();
    this.recordAccepted();

    // When WASM is loaded, delegate entirely to the Rust core.
    if (this.wasm) {
      // Hook first on this path only — see the note in `track()`.
      const filtered = this.applyBeforeSend('screen_view', properties, userId, anonymousId, {
        isScreen: true,
        screenName
      });
      if (filtered === null) return; // dropped by hook
      if (filtered) {
        // Hook may rewrite properties but not the event name for screen()
        properties = filtered.properties as EventProperties;
        userId = filtered.userId;
        anonymousId = filtered.anonymousId;
      }

      const depthBefore = this.wasm.queueDepth();
      try {
        this.wasm.screen(screenName, properties ?? null, userId ?? null, anonymousId ?? null);
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM screen failed:', e);
        this.recordDrop('engine_error');
        return;
      }
      const depthAfter = this.wasm.queueDepth();

      // See `track()` — a Rust-side drop this layer cannot attribute.
      if (depthAfter <= depthBefore) this.recordDrop('other');

      if (depthAfter > depthBefore && depthAfter >= this.flushThreshold) {
        this.doFlush();
      }
      return;
    }

    // JS fallback path — same pipeline order as `track()` (client.rs:895-984),
    // minus `$duration_ms` and commerce promotion, which `screen()` does not
    // do in the Rust core either. Consent gates FLUSH, not ingest, and the
    // circuit breaker gates DELIVERY only.
    const screenVerdict = this.fallbackConfigVerdict('screen_view');
    if (screenVerdict !== 'allowed') {
      if (this.enableDebug) console.log('[Layers] screen_view denied by remote config');
      this.recordDrop(screenVerdict);
      return;
    }

    // Keyed on the literal `screen_view`, not the screen name, so every screen
    // shares one budget (client.rs:919).
    if (!this.rateLimiter.checkAndRecord('screen_view')) {
      if (this.enableDebug) console.log('[Layers] screen_view rate limited');
      this.recordDrop('rate_limited');
      return;
    }

    const event = this.buildEvent('screen_view');
    event.screen_name = screenName;

    // Breadcrumb to the screen the user came FROM. Read before the new one is
    // recorded; both keys are omitted on the first screen of the session
    // (client.rs:942-948).
    if (this._lastScreenName !== null) {
      event.previous_screen_name = this._lastScreenName;
      if (this._lastScreenClass !== null) {
        event.previous_screen_class = this._lastScreenClass;
      }
    }
    // `screen_class` is a wrapper hint read out of `properties` and left there
    // verbatim — it is only snapshotted for the NEXT screen's breadcrumb
    // (client.rs:936-941). Non-string values are ignored.
    const screenClass =
      properties && typeof properties.screen_class === 'string' ? properties.screen_class : null;

    const merged = this.mergeSuperProperties(properties);
    if (merged) {
      event.properties = merged;
    }

    if (userId) {
      event.app_user_id = userId;
      event.user_id = userId;
    }
    if (anonymousId) {
      event.anonymous_id = anonymousId;
    }

    if (!this.fallbackSampled('screen_view')) {
      if (this.enableDebug) console.log('[Layers] screen_view sampled out');
      this.recordDrop('sampled_out');
      return;
    }

    if (!this.applyBeforeSendToEvent(event, true)) return; // dropped by hook

    // Advance the breadcrumb ONLY once the event has survived every drop gate
    // — otherwise the next screen() points at a screen whose event was never
    // sent (client.rs:973-976). The pre-hook `screenName` is what is recorded,
    // matching Rust, which advances from the call parameter rather than from
    // whatever the hook rewrote the event into.
    this._lastScreenName = screenName;
    this._lastScreenClass = screenClass;

    this.enqueue(event);
    this.maybeFlush();
  }

  identify(userId: string): void {
    this.checkNotShutDown();

    if (!userId) {
      throw new LayersError('INVALID_ARGUMENT', 'userId must not be empty');
    }

    this.userId = userId;

    if (this.wasm) {
      try {
        this.wasm.identify(userId);
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM identify failed:', e);
      }
    }
  }

  /**
   * Associate subsequent events with a group (company, team, organization).
   * Pass an empty string to clear the group association.
   * Optionally send group properties with the `group` event.
   */
  group(groupId: string, properties?: EventProperties): void {
    this.checkNotShutDown();

    if (this.wasm) {
      try {
        this.wasm.group(groupId, properties ?? null);
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM group failed:', e);
      }
      return;
    }

    // JS fallback — port of client.rs:1010-1039. An empty id clears the
    // association and emits nothing; otherwise the id is stored (so every
    // subsequent event carries the top-level `group_id`) and a `group` event
    // is emitted whether or not properties were supplied. `group_id` inside
    // the properties is set only when the caller did not provide one, which
    // is Rust's `entry().or_insert_with`.
    if (!groupId) {
      this._groupId = null;
      return;
    }
    this._groupId = groupId;
    const eventProperties: EventProperties = { ...(properties ?? {}) };
    if (eventProperties.group_id === undefined) {
      eventProperties.group_id = groupId;
    }
    this.track('group', eventProperties);
  }

  setUserProperties(properties: Record<string, unknown>): void {
    this.checkNotShutDown();

    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
      throw new LayersError('INVALID_ARGUMENT', 'properties must be a JSON object');
    }

    LayersCore.validateProperties(properties, 'User properties');

    if (this.wasm) {
      try {
        this.wasm.setUserProperties(properties);
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM setUserProperties failed:', e);
      }
    }
  }

  setUserPropertiesOnce(properties: Record<string, unknown>): void {
    this.checkNotShutDown();

    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
      throw new LayersError('INVALID_ARGUMENT', 'properties must be a JSON object');
    }

    LayersCore.validateProperties(properties, 'User properties');

    if (this.wasm) {
      try {
        this.wasm.setUserPropertiesOnce(properties);
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM setUserPropertiesOnce failed:', e);
      }
    }
  }

  /**
   * Update consent across the Firebase Consent Mode v2 categories.
   * On a Denied → Granted transition for `analyticsStorage`, the Rust
   * core auto-drains queued events.
   */
  setConsent(consent: ConsentState): void {
    this.checkNotShutDown();
    // Mirror `ConsentManager::set` in core/src/consent.rs: when the caller
    // set only one of a CMv2 field and its legacy alias, the other takes the
    // same value. `adUserDataAllowed()` / `adPersonalizationAllowed()` fall
    // back to `advertising`, so without this sync `setConsent({ adStorage:
    // false })` would deny ad storage on the Rust engine and leave the JS
    // engine forwarding user data to ad networks after the same call.
    const next: ConsentState = { ...consent };
    if (next.analytics == null && next.analyticsStorage != null)
      next.analytics = next.analyticsStorage;
    if (next.analyticsStorage == null && next.analytics != null)
      next.analyticsStorage = next.analytics;
    if (next.advertising == null && next.adStorage != null) next.advertising = next.adStorage;
    if (next.adStorage == null && next.advertising != null) next.adStorage = next.advertising;
    this.consent = next;

    if (this.wasm) {
      try {
        this.wasm.setConsent(consent);
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM setConsent failed:', e);
      }
    }

    // Re-sync the crash-safety snapshot. On WASM the Rust core's own
    // grant-time snapshot lands in NullPersistence (a no-op) — durability
    // lives in JS persistence, so the TS layer must take the snapshot
    // here. Unconditional on purpose: doFlush() is cheap and a consent
    // change in either direction shouldn't leave a stale snapshot.
    this.doFlush();
  }

  // ── Tier 4: feature flags ────────────────────────────────────────────

  /**
   * Evaluate a feature flag. Side-effects an exposure event
   * (`$feature_flag_called`) at most once per `(flag_key, response)` pair
   * per session — the Rust core handles the dedup.
   *
   * Returns:
   * - the variant key string for multivariate flags
   * - `true` / `false` for binary flags
   * - `undefined` if the flag is unknown (no definition + no bootstrap)
   *
   * Rust returns `null` for unknown flags; we map that to `undefined` so
   * callers can use the natural `??` default-value pattern.
   */
  getFeatureFlag(flagKey: string): FeatureFlagValue | undefined {
    if (!flagKey) {
      throw new LayersError('INVALID_ARGUMENT', 'flagKey must not be empty');
    }
    if (!this.wasm) return this.fallbackGetFeatureFlag(flagKey);
    try {
      const value = this.wasm.getFeatureFlag(flagKey);
      if (value === null || value === undefined) return undefined;
      if (typeof value === 'boolean' || typeof value === 'string') return value;
      return undefined;
    } catch (e) {
      if (this.enableDebug) console.warn('[Layers] WASM getFeatureFlag failed:', e);
      return undefined;
    }
  }

  /** Convenience: returns true iff the flag is truthy. */
  isFeatureEnabled(flagKey: string): boolean {
    if (!flagKey) {
      throw new LayersError('INVALID_ARGUMENT', 'flagKey must not be empty');
    }
    // client.rs:2190-2195 — `is_feature_enabled` is `get_feature_flag` plus
    // truthiness, exposure event and all.
    if (!this.wasm) return flagValueIsTruthy(this.fallbackGetFeatureFlag(flagKey));
    try {
      return this.wasm.isFeatureEnabled(flagKey);
    } catch (e) {
      if (this.enableDebug) console.warn('[Layers] WASM isFeatureEnabled failed:', e);
      return false;
    }
  }

  /**
   * Look up the JSON payload attached to a flag. Returns `undefined` if the
   * flag has no payload. Does NOT emit `$feature_flag_called` (use
   * `getFeatureFlag(...)` for the exposure event).
   */
  getFeatureFlagPayload<T = unknown>(flagKey: string): T | undefined {
    if (!flagKey) {
      throw new LayersError('INVALID_ARGUMENT', 'flagKey must not be empty');
    }
    if (!this.wasm) {
      const payload = this.flagEngine.payloadFor(flagKey);
      return payload === undefined || payload === null ? undefined : (payload as T);
    }
    try {
      const value = this.wasm.getFeatureFlagPayload(flagKey);
      if (value === null || value === undefined) return undefined;
      // serde_wasm_bindgen serializes JSON objects as `Map`s by default —
      // normalize back to a plain object so consumers don't need to remember
      // to call `Object.fromEntries` on the result.
      return mapToObject(value) as T;
    } catch (e) {
      if (this.enableDebug) console.warn('[Layers] WASM getFeatureFlagPayload failed:', e);
      return undefined;
    }
  }

  /**
   * Snapshot every known flag's current value. Does NOT emit exposure events.
   *
   * Returns an empty object when no definitions have been loaded yet.
   */
  getAllFlags(): Record<string, FeatureFlagValue> {
    if (!this.wasm) return this.flagEngine.evaluateAll(this.flagDistinctId());
    try {
      const value = this.wasm.getAllFlags();
      const obj = mapToObject(value);
      if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
        return obj as Record<string, FeatureFlagValue>;
      }
      return {};
    } catch (e) {
      if (this.enableDebug) console.warn('[Layers] WASM getAllFlags failed:', e);
      return {};
    }
  }

  /**
   * Reload feature flag definitions by re-fetching `/config`. The fetched
   * config is automatically applied to the Rust core (the existing
   * `fetchRemoteConfig` call already calls `update_remote_config`, which
   * the core uses to refresh definitions).
   *
   * Fires every registered `onFeatureFlags` listener after the refresh
   * completes — even if the flag set didn't change. Listeners that
   * subscribed AFTER an in-flight refresh are not back-filled.
   */
  async reloadFeatureFlags(): Promise<void> {
    this.checkNotShutDown();
    try {
      // First clear local cache so the next /config fetch re-populates.
      if (this.wasm) {
        try {
          this.wasm.reloadFeatureFlags();
        } catch (e) {
          if (this.enableDebug) {
            console.warn('[Layers] WASM reloadFeatureFlags failed:', e);
          }
        }
      } else {
        // client.rs:2221-2228 — definitions are dropped (bootstrap survives)
        // and the exposure dedup is cleared so the refreshed answers re-emit.
        this.flagEngine.setDefinitions([]);
        this.flagEngine.clearExposureDedup();
      }
      // Then re-fetch — this round-trips through fetchRemoteConfig which
      // calls update_remote_config inside the WASM core, repopulating the
      // definition map.
      await this.fetchRemoteConfig();
    } finally {
      this._fireFeatureFlagListeners();
    }
  }

  /** Override the person-properties bag used during flag evaluation only. */
  setPersonPropertiesForFlags(properties: Record<string, unknown>): void {
    this.checkNotShutDown();
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
      throw new LayersError('INVALID_ARGUMENT', 'properties must be a JSON object');
    }
    if (!this.wasm) {
      // A property change can move every flag, so the dedup cache is cleared
      // and the new responses re-emit exposures (client.rs:2245-2249).
      this.flagEngine.setPersonProperties(properties);
      this.flagEngine.clearExposureDedup();
      return;
    }
    try {
      this.wasm.setPersonPropertiesForFlags(properties);
    } catch (e) {
      if (this.enableDebug) {
        console.warn('[Layers] WASM setPersonPropertiesForFlags failed:', e);
      }
    }
  }

  /** Merge additional person-properties into the flag-evaluation override map. */
  mergePersonPropertiesForFlags(properties: Record<string, unknown>): void {
    this.checkNotShutDown();
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
      throw new LayersError('INVALID_ARGUMENT', 'properties must be a JSON object');
    }
    if (!this.wasm) {
      this.flagEngine.mergePersonProperties(properties);
      this.flagEngine.clearExposureDedup();
      return;
    }
    try {
      this.wasm.mergePersonPropertiesForFlags(properties);
    } catch (e) {
      if (this.enableDebug) {
        console.warn('[Layers] WASM mergePersonPropertiesForFlags failed:', e);
      }
    }
  }

  /**
   * Seed bootstrap flag values + payloads after init. Use this when the
   * SSR layer didn't have flag values at construction time (e.g. delayed
   * /decide from a separate edge function).
   */
  setFeatureFlagBootstrap(bootstrap: FeatureFlagBootstrap | FeatureFlagBootstrapData): void {
    this.checkNotShutDown();
    const normalized = normalizeBootstrap(bootstrap);
    if (!normalized) {
      throw new LayersError('INVALID_ARGUMENT', 'bootstrap must be a JSON object');
    }
    if (!this.wasm) {
      this.flagEngine.setBootstrap(normalized.feature_flags, normalized.feature_flag_payloads);
      this._fireFeatureFlagListeners();
      return;
    }
    try {
      this.wasm.setFeatureFlagBootstrap(normalized);
      // Bootstrap may surface flag keys that weren't in the prior /config
      // — fan out to listeners so consumers re-render.
      this._fireFeatureFlagListeners();
    } catch (e) {
      if (this.enableDebug) {
        console.warn('[Layers] WASM setFeatureFlagBootstrap failed:', e);
      }
    }
  }

  /** Snapshot the list of currently-known flag keys. */
  knownFeatureFlagKeys(): string[] {
    if (!this.wasm) return this.flagEngine.knownKeys();
    try {
      const v = this.wasm.knownFeatureFlagKeys();
      if (Array.isArray(v)) return v as string[];
      return [];
    } catch (e) {
      if (this.enableDebug) console.warn('[Layers] WASM knownFeatureFlagKeys failed:', e);
      return [];
    }
  }

  /**
   * Subscribe to feature-flag refreshes. The callback fires each time
   * `reloadFeatureFlags()` or `setFeatureFlagBootstrap()` completes — even
   * if the flag set is unchanged.
   *
   * Returns a function that removes the subscription. Idempotent: calling
   * the unsubscriber twice is safe.
   *
   * The callback is invoked with the snapshot of currently-known flag
   * keys; use `getFeatureFlag` / `getAllFlags` from inside the callback
   * to read fresh values.
   */
  onFeatureFlags(callback: FeatureFlagsListener): () => void {
    if (typeof callback !== 'function') {
      throw new LayersError('INVALID_ARGUMENT', 'callback must be a function');
    }
    this._featureFlagListeners.add(callback);
    return () => {
      this._featureFlagListeners.delete(callback);
    };
  }

  /**
   * Fan out to every subscriber. Errors are swallowed individually.
   *
   * Uses `getAllFlags()` to compute the snapshot rather than
   * `knownFeatureFlagKeys()` because the latter only returns keys for
   * which the Rust core has a *definition* — bootstrap-only flags don't
   * appear there. From a consumer perspective both paths surface a usable
   * flag, so the listener fires the union.
   */
  private _fireFeatureFlagListeners(): void {
    if (this._featureFlagListeners.size === 0) return;
    const keys = Object.keys(this.getAllFlags());
    for (const cb of this._featureFlagListeners) {
      try {
        cb(keys);
      } catch (e) {
        if (this.enableDebug) {
          console.warn('[Layers] onFeatureFlags listener threw:', e);
        }
      }
    }
  }

  /**
   * The bucketing input for flag evaluation — port of
   * `feature_flag_distinct_id` (client.rs:2308-2315). An identified user
   * buckets on `user_id`; an anonymous one buckets on the SESSION id, so a
   * single anonymous session gets a stable answer. Not `anonymous_id`:
   * matching the Rust core is what keeps a user in the same rollout half
   * across the engine boundary.
   */
  private flagDistinctId(): string {
    return this.userId ?? this.getOrRotateSessionId();
  }

  /**
   * JS-engine `getFeatureFlag`: evaluate, then emit the exposure event if this
   * `(flag_key, response)` pair is new for the session. Port of
   * `LayersCore::get_feature_flag` (client.rs:2173-2185).
   */
  private fallbackGetFeatureFlag(flagKey: string): FeatureFlagValue | undefined {
    this.refreshFlagSession();
    const { value, exposure } = this.flagEngine.evaluate(flagKey, this.flagDistinctId());
    if (exposure) {
      // Property names are wire contract (client.rs:2334-2338). This routes
      // through the normal `track()` pipeline on purpose so exposures respect
      // remote config, sampling and the queue like every other event; the rate
      // limiter exempts `$`-prefixed system events (rate-limiter.ts:112-114).
      try {
        this.track('$feature_flag_called', {
          $feature_flag: exposure.flagKey,
          $feature_flag_response: exposure.flagResponse,
          $feature_flag_matched: exposure.matched
        });
      } catch (e) {
        if (this.enableDebug) {
          console.warn('[Layers] failed to emit $feature_flag_called:', e);
        }
      }
    }
    return value;
  }

  /**
   * Clear the exposure dedup cache when the session rotated underneath us, so
   * the new session emits one fresh `$feature_flag_called` per flag. Port of
   * `refresh_flag_session` (client.rs:2319-2326).
   */
  private refreshFlagSession(): void {
    const current = this.getOrRotateSessionId();
    if (this._flagSessionId !== current) {
      if (this._flagSessionId !== null) this.flagEngine.clearExposureDedup();
      this._flagSessionId = current;
    }
  }

  // --- Tier 1: super-properties ---

  /**
   * Register one or more super-properties — auto-merged into every
   * `track()` / `screen()` call until cleared. Survives within the SDK
   * process lifetime (the Rust core handles persistence).
   */
  setSuperProperties(properties: Record<string, unknown>): void {
    this.checkNotShutDown();
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
      throw new LayersError('INVALID_ARGUMENT', 'properties must be a JSON object');
    }
    LayersCore.validateProperties(properties, 'Super properties');
    this._superProperties = { ...this._superProperties, ...properties };
    if (this.wasm) {
      try {
        this.wasm.setSuperProperties(properties);
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM setSuperProperties failed:', e);
      }
    }
  }

  /**
   * Register super-properties with "set once" semantics — only keys that
   * have not been previously registered via `setSuperPropertiesOnce` are
   * applied. Useful for capturing first-touch attribution.
   */
  setSuperPropertiesOnce(properties: Record<string, unknown>): void {
    this.checkNotShutDown();
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
      throw new LayersError('INVALID_ARGUMENT', 'properties must be a JSON object');
    }
    LayersCore.validateProperties(properties, 'Super properties');
    // The gate is membership in the once-keys set, NOT presence in the
    // property bag (super_properties.rs:119-133). A key set by the plain
    // `setSuperProperties` is not in that set, so the first "once" call for it
    // overwrites and then locks it — matching the Rust core, which is the
    // engine every other platform runs.
    for (const [key, value] of Object.entries(properties)) {
      if (!this._superPropertiesOnceKeys.has(key)) {
        this._superPropertiesOnceKeys.add(key);
        this._superProperties[key] = value;
      }
    }
    if (this.wasm) {
      try {
        this.wasm.setSuperPropertiesOnce(properties);
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM setSuperPropertiesOnce failed:', e);
      }
    }
  }

  /** Remove a single super-property by key. */
  unregisterSuperProperty(key: string): void {
    this.checkNotShutDown();
    if (!key) return;
    delete this._superProperties[key];
    if (this.wasm) {
      try {
        this.wasm.unregisterSuperProperty(key);
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM unregisterSuperProperty failed:', e);
      }
    }
  }

  /** Clear all super-properties (set + once). */
  clearSuperProperties(): void {
    this.checkNotShutDown();
    this._superProperties = {};
    this._superPropertiesOnceKeys.clear();
    if (this.wasm) {
      try {
        this.wasm.clearSuperProperties();
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM clearSuperProperties failed:', e);
      }
    }
  }

  /** Return a snapshot of the registered super-properties. */
  getSuperProperties(): Record<string, unknown> {
    if (this.wasm) {
      try {
        const v = this.wasm.getSuperProperties();
        if (v && typeof v === 'object' && Object.keys(v).length > 0) {
          return v as Record<string, unknown>;
        }
      } catch {
        // fall through to JS state
      }
    }
    // Either WASM returned an empty object (e.g. WASM-side state not in sync
    // due to instance recreation in tests) or WASM is unavailable. Use the
    // JS-side mirror which is updated on every set/unregister/clear.
    return { ...this._superProperties };
  }

  // --- Tier 1: timed events ---

  /**
   * Start a duration timer for the next `track(name)` call. The Rust core
   * automatically attaches `duration_ms` to the next track invocation that
   * matches `eventName`. JS-side bookkeeping is kept as a fallback.
   */
  timeEvent(eventName: string): void {
    this.checkNotShutDown();
    if (!eventName) {
      throw new LayersError('INVALID_ARGUMENT', 'eventName must not be empty');
    }
    this._timedEvents.set(eventName, Date.now());
    if (this.wasm) {
      try {
        this.wasm.timeEvent(eventName);
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM timeEvent failed:', e);
      }
    }
  }

  /**
   * Cancel a timed event without emitting it. Returns the elapsed time in
   * milliseconds, or 0 if no timer was started for this event.
   */
  cancelTimedEvent(eventName: string): number {
    this.checkNotShutDown();
    if (!eventName) return 0;
    const start = this._timedEvents.get(eventName);
    this._timedEvents.delete(eventName);
    let wasmMs = 0;
    if (this.wasm) {
      try {
        const v = this.wasm.cancelTimedEvent(eventName);
        wasmMs = typeof v === 'bigint' ? Number(v) : Number(v ?? 0);
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM cancelTimedEvent failed:', e);
      }
    }
    if (wasmMs > 0) return wasmMs;
    return start != null ? Date.now() - start : 0;
  }

  // --- Tier 1: multi-group ---

  /** Set membership for a single group_type. Empty `groupId` removes it. */
  setGroup(groupType: string, groupId: string): void {
    this.checkNotShutDown();
    if (!groupType) {
      throw new LayersError('INVALID_ARGUMENT', 'groupType must not be empty');
    }
    if (groupId) this._groups[groupType] = groupId;
    else delete this._groups[groupType];
    this.mirrorLegacyGroupId(groupType, groupId);
    if (this.wasm) {
      try {
        this.wasm.setGroup(groupType, groupId);
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM setGroup failed:', e);
      }
    }
  }

  /** Add a group membership without overwriting other types (alias for setGroup). */
  addGroup(groupType: string, groupId: string): void {
    this.checkNotShutDown();
    if (!groupType || !groupId) {
      throw new LayersError('INVALID_ARGUMENT', 'groupType and groupId must not be empty');
    }
    this._groups[groupType] = groupId;
    this.mirrorLegacyGroupId(groupType, groupId);
    if (this.wasm) {
      try {
        this.wasm.addGroup(groupType, groupId);
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM addGroup failed:', e);
      }
    }
  }

  /** Remove a group_type from the membership map. */
  removeGroup(groupType: string): void {
    this.checkNotShutDown();
    if (!groupType) return;
    delete this._groups[groupType];
    this.mirrorLegacyGroupId(groupType, '');
    if (this.wasm) {
      try {
        this.wasm.removeGroup(groupType);
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM removeGroup failed:', e);
      }
    }
  }

  /**
   * Keep the deprecated singleton `group_id` in step with the `company` group.
   *
   * Rust keys this off the literal group type `"company"` — not "the first
   * group registered", despite what the comment above `client.rs:1220` says —
   * so `setGroup('team', …)` deliberately leaves `group_id` alone.
   */
  private mirrorLegacyGroupId(groupType: string, groupId: string): void {
    if (groupType !== 'company') return;
    this._groupId = groupId || null;
  }

  /** Snapshot the current $groups membership map. */
  getGroups(): GroupsState {
    if (this.wasm) {
      try {
        const v = this.wasm.getGroups();
        if (v && typeof v === 'object' && Object.keys(v).length > 0) {
          return v as GroupsState;
        }
      } catch {
        // fall through to JS state
      }
    }
    return { ...this._groups };
  }

  // --- Tier 1: user-property mutators ---

  /** Increment a numeric user property by `delta` (negative allowed). */
  increment(key: string, delta: number = 1): void {
    this.checkNotShutDown();
    if (!key) throw new LayersError('INVALID_ARGUMENT', 'key must not be empty');
    if (typeof delta !== 'number' || !Number.isFinite(delta)) {
      throw new LayersError('INVALID_ARGUMENT', 'delta must be a finite number');
    }
    if (this.wasm) {
      try {
        this.wasm.increment(key, delta);
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM increment failed:', e);
      }
    }
  }

  /** Append a single value to a user-property list (creates list if missing). */
  append(key: string, value: unknown): void {
    this.checkNotShutDown();
    if (!key) throw new LayersError('INVALID_ARGUMENT', 'key must not be empty');
    if (this.wasm) {
      try {
        this.wasm.append(key, value);
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM append failed:', e);
      }
    }
  }

  /** Union (set-add) a list of values into a user-property list. */
  union(key: string, values: unknown[]): void {
    this.checkNotShutDown();
    if (!key) throw new LayersError('INVALID_ARGUMENT', 'key must not be empty');
    if (!Array.isArray(values)) {
      throw new LayersError('INVALID_ARGUMENT', 'values must be an array');
    }
    if (this.wasm) {
      try {
        this.wasm.union(key, values);
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM union failed:', e);
      }
    }
  }

  /** Remove a user property by key. */
  unset(key: string): void {
    this.checkNotShutDown();
    if (!key) return;
    if (this.wasm) {
      try {
        this.wasm.unset(key);
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM unset failed:', e);
      }
    }
  }

  // --- Tier 1: identity reset ---

  /**
   * Clear identity:
   *  - drops user_id
   *  - rotates device_id and anonymous_id
   *  - clears super-properties + multi-group memberships
   *  - drops in-flight events queued under the old identity.
   *
   * Behaves like Mixpanel/PostHog `reset()`. Use after a user logs out so
   * subsequent events don't carry the previous user's identity.
   */
  reset(): void {
    this.checkNotShutDown();
    this.userId = undefined;
    this._superProperties = {};
    this._superPropertiesOnceKeys.clear();
    this._timedEvents.clear();
    this._groups = {};
    // Both group slots and the screen breadcrumb go with the identity —
    // client.rs:1352-1354 clears group_id, groups, timed events and
    // last_screen in the same block.
    this._groupId = null;
    this._lastScreenName = null;
    this._lastScreenClass = null;
    if (this.wasm) {
      try {
        this.wasm.reset();
        // The Rust core drops ITS queue (client.rs:1373). The events this
        // core hydrated from a previous launch are not in it — the WASM path
        // rehydrates them into the JS queue — and returning here left them to
        // ship after the logout under the `anonymous_id` the reset just
        // rotated away from. That is exactly the join `reset()` exists to
        // break, so the JS-side backlog and its on-disk chunks go too.
        //
        // Reserved chunks (events a full queue never hydrated) are included:
        // they are the same install's pre-logout events, so "never read" is
        // no reason to keep them past a logout.
        //
        // Deliberate loss is still loss, and the counters record it. The EPOCH
        // and the monotonic totals are preserved (wire delta #2): zeroing them
        // here would destroy the very `reset_discarded` count this line exists
        // to write, and the counters are install-scoped exactly like
        // `first_open_emitted` and the SKAN floor, which reset() already keeps.
        this.recordDrop('reset_discarded', this.queue.length);
        this.queue = [];
        this.dropPersistedEvents();
        this.persistHealthCounters(true);
        return;
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM reset failed:', e);
      }
    }
    // JS fallback: rotate session and drop the in-memory queue.
    //
    // The rotation itself predates the session counter (it arrived in #136).
    // Rust's `reset()` does NOT rotate — it leaves the session alone and
    // re-persists the current number (client.rs:1370-1373) — so this engine
    // starts one more session than the Rust core would for the same reset()
    // call. That divergence is deliberate and out of scope here; what is NOT
    // optional is that a rotation which does happen goes through
    // `rotateSession`. Minting a fresh `session_id` while leaving
    // `session_number` behind is the defect this whole helper exists to
    // prevent, and it applies to a logout exactly as it applies to
    // `startNewSession()`.
    this.rotateSession();
    // Counted before the queue is emptied — see the WASM branch above for why
    // the epoch and the totals survive a reset.
    this.recordDrop('reset_discarded', this.queue.length);
    this.queue = [];
    // Same reasoning as the WASM branch above: dropping the in-memory queue
    // while leaving its durable copy on disk only defers the leak to the next
    // launch's hydration.
    this.dropPersistedEvents();
    // Rotate the pseudonymous identity — the whole point of reset() is that
    // events after a logout must not be joinable to the previous user
    // (core/src/client.rs:1346-1406).
    this.anonymousId = generateUUID();
    this.deviceId = generateUUID();
    // A parked-but-unemitted `$first_open` goes with them, and the install is
    // recorded as done: `$first_open` is install-scoped, not identity-scoped,
    // so a logout must not re-arm it. Leaving the parked payload in memory
    // would let the next track() queue an event that persistence already says
    // was handled (core/src/client.rs::reset).
    //
    // No `first_open_event_id` is written here. On an install that never
    // BUILT one there is nothing to re-emit, and the next launch reads the
    // resulting flag-without-id as retired. On an install that did build one
    // the record already carries its id — and the queue this reset just
    // dropped may have held the only copy — so that install stays re-armable
    // under the original id, which is what separating BUILT from SENT buys.
    this.pendingFirstOpen = null;
    // One read-modify-write for the whole record. `first_open_time` and
    // `last_session_number` are install-scoped and deliberately preserved:
    // Rust carries both across reset() too, so a logout does not make a
    // long-standing install look brand new.
    this.updateIdentity((identity) => {
      identity.first_open_emitted = true;
      identity.anonymous_id = this.anonymousId;
      identity.device_id = this.deviceId;
      identity.last_session_number = this._sessionNumber;
    });
    // The `reset_discarded` count is only useful if it survives the launch it
    // happened in.
    this.persistHealthCounters(true);
    // The rotated pair is now the durable one, so a later core swap must not
    // seed the pre-logout identity back over it — that would re-join this
    // device to the user who just logged out.
    this.identityMinted = false;
  }

  /** Returns the current anonymous ID (rotated on `reset()`). */
  getAnonymousId(): string | null {
    if (this.wasm) {
      try {
        return this.wasm.getAnonymousId() ?? null;
      } catch {
        // fall through
      }
    }
    return this.anonymousId;
  }

  /** Returns the current device ID (stable per install, rotated on `reset()`). */
  getDeviceId(): string | null {
    if (this.wasm) {
      try {
        return this.wasm.getDeviceId() ?? null;
      } catch {
        // fall through
      }
    }
    return this.deviceId;
  }

  /** Returns the monotonically-increasing session number. */
  getSessionNumber(): number {
    if (this.wasm) {
      try {
        return this.wasm.getSessionNumber();
      } catch {
        // fall through
      }
    }
    return this._sessionNumber;
  }

  // ── SKAN (SKAdNetwork conversion values) ────────────────────────────
  // Evaluation lives in the Rust core wherever the Rust core can run, and in
  // `skan.ts` — a line-for-line port of `core/src/skan.rs` — where it cannot.
  //
  // The port exists because these methods used to be `if (this.wasm)` and
  // nothing else. React Native never loads WASM (Metro cannot bundle a .wasm),
  // so `skanProcessEvent` returned null for every event and an RN app posted
  // no conversion value to Apple, ever. The native bridge in
  // `@layers/react-native/src/skan.ts` was wired the whole time — it just had
  // nothing to apply.
  //
  // This is NOT the parallel JS engine the core unification removed. That one
  // was an independent implementation free to drift; this one is pinned to the
  // Rust tables by a dual-engine diff test (test/engine-parity-features.ts).

  /** Load a built-in SKAN preset (`subscriptions` | `engagement` | `iap`). */
  skanSetPreset(preset: string): void {
    if (this.wasm) {
      this.wasm.skanSetPreset(preset);
      return;
    }
    try {
      this.skanEngine.setPreset(preset);
    } finally {
      this.reportSkanDiagnostics('api');
    }
  }

  /** Replace SKAN rules with a custom JSON array string. */
  skanSetRules(rulesJson: string): void {
    if (this.wasm) this.wasm.skanSetRules(rulesJson);
    else this.skanEngine.setRules(rulesJson);
  }

  /** Apply the `skan` block from the cached remote config (no-op if absent). */
  skanConfigureFromRemoteConfig(): void {
    if (this.wasm) {
      this.wasm.skanConfigureFromRemoteConfig();
      return;
    }
    // No config fetched yet is a no-op, so a manually-set preset isn't
    // clobbered before the first fetch; an omitted OR null block disables
    // (client.rs:1647-1662).
    if (this._skanConfig === undefined) return;
    if (this._skanConfig === null) {
      this.skanEngine.disable();
      return;
    }
    try {
      this.skanEngine.configure(this._skanConfig);
    } finally {
      this.reportSkanDiagnostics('remote_config');
    }
  }

  /**
   * Drain every SKAN configuration diagnostic the engine queued during the call
   * just made, and emit one event each — port of
   * `LayersCore::report_skan_diagnostics` (client.rs). Without it the failures
   * never leave the device: zero installed rules is wire-indistinguishable from
   * an app that never configured SKAN, and with no rules no SKAN event can ever
   * fire to carry the signal as a property.
   *
   * `SkanEngine` owns the dedup — a bad preset or a malformed block the server
   * repeats on every config refresh reports once per process — and every
   * producer dedups before pushing, so the queue's lifetime length is bounded
   * (8 preset names + 1 decode + 5 JSON types) and this loop always terminates.
   * `track()` produces no diagnostics of its own.
   *
   * Routed through `track()` so the events obey consent, killswitches and
   * sampling like every other event (the rate limiter exempts `$`-prefixed
   * system events). Emission failures are swallowed — a diagnostic must never
   * change what the host app sees.
   */
  private reportSkanDiagnostics(source: 'api' | 'remote_config'): void {
    for (
      let diagnostic = this.skanEngine.takePendingDiagnostic();
      diagnostic !== null;
      diagnostic = this.skanEngine.takePendingDiagnostic()
    ) {
      let event: string;
      let properties: Record<string, unknown>;
      switch (diagnostic.kind) {
        case 'presetRejected':
          event = '$skan_preset_rejected';
          properties = { $skan_preset: diagnostic.preset, $skan_preset_source: source };
          break;
        case 'configStringDecoded':
          event = '$skan_config_string_decoded';
          properties = { $skan_config_source: source };
          break;
        case 'configRejected':
          event = '$skan_config_rejected';
          properties = { $skan_config_type: diagnostic.jsonType, $skan_config_source: source };
          break;
      }
      try {
        this.track(event, properties);
      } catch (e) {
        if (this.enableDebug) {
          console.warn(`[Layers] failed to emit ${event}:`, e);
        }
      }
    }
  }

  /**
   * Evaluate an event against the SKAN rules. Returns the OS-level update to
   * apply (fine value + optional coarse value / lock-window), or null if the
   * value did not increase.
   */
  skanProcessEvent(
    eventName: string,
    properties: Record<string, unknown> | null = null
  ): { fineValue: number; coarseValue: string | null; lockWindow: boolean } | null {
    if (!this.wasm) return this.skanEngine.processEvent(eventName, properties ?? {});
    const json = this.wasm.skanProcessEvent(
      eventName,
      properties ? JSON.stringify(properties) : null
    );
    return json ? JSON.parse(json) : null;
  }

  /**
   * Report the outcome of applying a SKAN update from `skanProcessEvent`. On
   * `success` the monotonic floor advances to `value` and is persisted; on
   * failure nothing changes so the next matching event re-issues the update.
   */
  skanRecordConversionResult(value: number, success: boolean): void {
    if (this.wasm) {
      this.wasm.skanRecordConversionResult(value, success);
      return;
    }
    // Persist only when the floor actually advanced — a handful of writes per
    // install, and never one that could lower it (client.rs:1697-1705).
    if (this.skanEngine.recordConversionResult(value, success)) {
      try {
        this.persistence.write(
          SKAN_VALUE_PERSISTENCE_KEY,
          new Uint8Array([this.skanEngine.currentValue()])
        );
      } catch {
        // Best-effort durability; the in-memory floor still holds.
      }
    }
  }

  /** The highest SKAN conversion value posted so far (monotonic). */
  skanCurrentValue(): number {
    if (this.wasm) {
      try {
        return this.wasm.skanCurrentValue();
      } catch {
        // fall through
      }
      return 0;
    }
    return this.skanEngine.currentValue();
  }

  /** Whether SKAN is currently enabled (drives whether the wrapper arms). */
  skanIsEnabled(): boolean {
    if (this.wasm) {
      try {
        return this.wasm.skanIsEnabled();
      } catch {
        // fall through
      }
      return false;
    }
    return this.skanEngine.isEnabled();
  }

  /** The active SKAN preset name, or null if unconfigured. */
  skanCurrentPreset(): string | null {
    if (this.wasm) {
      try {
        // wasm-bindgen maps Rust's `Option<String>::None` to `undefined`, not
        // `null`, so the raw value contradicted this method's own signature.
        // Normalizing here means one answer for both engines and no caller
        // needing a `?? null` to make the declared type true.
        return this.wasm.skanCurrentPreset() ?? null;
      } catch {
        // fall through
      }
      return null;
    }
    return this.skanEngine.currentPreset();
  }

  /** Returns the SDK first-open timestamp as an RFC3339 string, or null. */
  getFirstOpenTime(): string | null {
    if (this.wasm) {
      try {
        const v = this.wasm.getFirstOpenTime();
        return typeof v === 'string' ? v : null;
      } catch {
        // fall through
      }
    }
    // Fallback engine: minted and persisted by initFirstOpen().
    return this.firstOpenTime;
  }

  // --- Tier 1: before_send hook ---

  /**
   * Register a synchronous filter callback called for every event before it
   * is queued. Return `null` to drop the event; return a (possibly modified)
   * `BeforeSendEvent` to forward it.
   *
   * Pass `null` to clear the hook.
   *
   * Mirrors the Swift / Kotlin / Flutter `setBeforeSend` API. WASM does not
   * yet expose `set_before_send`, so the hook runs JS-side. This means the
   * Rust core never sees a hook-rejected event (no queue insertion at all).
   *
   * ONE EVENT IS EXEMPT. The `$sdk_health` beacon bypasses this hook. The hook
   * exists to scrub PII out of HOST event data, and the beacon carries none —
   * only operational counters. A hook that returns `null` unconditionally is a
   * legitimate kill-my-analytics switch, and honouring it for the beacon would
   * blind the fleet exactly where an operator most needs to see it. This
   * narrows the documented contract of a public hook; it is stated again in the
   * `$sdk_health` section of `schema/PROTOCOL.md`.
   */
  setBeforeSend(hook: BeforeSendHook | null): void {
    this._beforeSendHook = hook;
  }

  // --- Tier 9: privacy posture (DNT + cookieless + flush gating) ---

  /**
   * Tier 9 — web wrappers call this with the current DNT signal value
   * (`navigator.doNotTrack === '1'` etc). When `respectDnt=true` was
   * set in config, an active DNT signal gates analytics flush.
   *
   * Native wrappers should not call this — there is no equivalent
   * OS-level signal.
   */
  setDntSignal(dntActive: boolean): void {
    if (this._isShutDown) return;
    // Recorded unconditionally, for both engines: `respectDnt` is what decides
    // whether the signal has any effect (client.rs:1490-1496 stores it the
    // same way), and `flushAllowed()` is the only reader.
    this.dntActive = dntActive;
    if (this.wasm) {
      try {
        this.wasm.setDntSignal(dntActive);
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM setDntSignal failed:', e);
      }
    }
  }

  /**
   * Whether the real Rust/WASM engine is driving this core (vs the JS
   * fallback). Wrappers use this to upgrade a fallback-constructed core
   * once `initWasm()` resolves, and to warn when the upgrade never lands.
   */
  isWasmActive(): boolean {
    return this.wasm !== null;
  }

  /**
   * The `X-SDK-Version` value this instance puts on the wire, engine token
   * included.
   *
   * Three engines ship under the same package versions — the native Rust
   * library, the Rust core compiled to WASM, and this TypeScript engine, which
   * is what runs on React Native (Metro cannot bundle a `.wasm`). They agree
   * on the wire format but not on everything behind it; sampling is the
   * documented divergence (the Rust core hashes `event_id` for a deterministic
   * verdict, this engine calls `Math.random` — see `test/engine-parity.test.ts`).
   * Until the last token named the engine, nothing on the wire distinguished
   * them, so a server-side investigation into engine-specific behaviour had
   * nothing to filter on.
   *
   * The token reports the engine ACTIVE IN THIS INSTANCE, so one install
   * reports one shape across every endpoint. On the WASM path the Rust core
   * builds `/events` headers itself and stamps `engine/wasm` from
   * `core/src/client.rs` — this method agrees with it by construction, because
   * both read the same fact.
   *
   * Wrappers building their own requests (`/users/properties`,
   * `/clicks/resolve`, session replay) must use this rather than rebuilding
   * `{name}/{version}` by hand.
   */
  sdkVersionHeader(): string {
    return `${this.sdkVersion} engine/${this.isWasmActive() ? 'wasm' : 'js'}`;
  }

  /**
   * Snapshot wrapper-visible in-memory state for migration across a core
   * swap (the fallback→WASM upgrade in the wrappers' init()). Queue
   * contents migrate separately — shutdown() persists them durably and the
   * new core's hydration re-queues them — but consent, super properties,
   * groups, and device context live only in memory and would otherwise be
   * silently dropped by the swap.
   */
  exportMigrationState(): MigrationState {
    const record = this.readIdentity();
    return {
      consent: this.getConsentState(),
      superProperties: this.getSuperProperties(),
      groups: this.getGroups(),
      deviceContext: this.getDeviceContext(),
      identity: {
        anonymousId: this.getAnonymousId(),
        deviceId: this.getDeviceId(),
        sessionNumber: this.getSessionNumber(),
        firstOpenTime: typeof record.first_open_time === 'string' ? record.first_open_time : null,
        firstOpenEmitted: record.first_open_emitted === true,
        firstOpenQueued: this.queue.some((e) => e.event === FIRST_OPEN_EVENT),
        // The queued copy outranks the record: it is the event that actually
        // migrates, so its id cannot drift from what the destination ends up
        // holding. The record covers the copy that already went out on the
        // wire — and on a first install it is THIS core that built the event,
        // so its own record is the only place the id exists at all.
        firstOpenEventId: this.queuedFirstOpenEventId() ?? persistedFirstOpenEventId(record),
        firstOpenDelivered: record.first_open_delivered === true,
        sessionId: this.wasm ? null : this._sessionId
      }
    };
  }

  /** Re-apply state captured by {@link exportMigrationState} on a fresh core. */
  applyMigrationState(state: {
    consent: ConsentState;
    superProperties: Record<string, unknown>;
    groups: GroupsState;
    deviceContext: DeviceContext;
    identity?: MigrationIdentity;
  }): void {
    try {
      if (state.identity) this.adoptMigratedIdentity(state.identity);
      if (Object.values(state.consent).some((v) => v !== undefined)) {
        this.setConsent(state.consent);
      }
      if (Object.keys(state.superProperties).length > 0) {
        this.setSuperProperties(state.superProperties);
      }
      for (const [groupType, groupId] of Object.entries(state.groups)) {
        this.addGroup(groupType, groupId);
      }
      if (Object.values(state.deviceContext).some((v) => v !== undefined)) {
        this.setDeviceContext(state.deviceContext);
      }
    } catch (e) {
      // Best-effort: a migration failure must not break init.
      if (this.enableDebug) console.warn('[Layers] Core state migration failed:', e);
    }
  }

  /**
   * Returns true when the privacy gates allow analytics flush. When
   * false, queued events are held until consent is granted, DNT clears,
   * or `consentRequired` mode receives an explicit grant.
   */
  flushAllowed(): boolean {
    if (this._isShutDown) return false;
    if (this.wasm) {
      try {
        return this.wasm.flushAllowed();
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM flushAllowed failed:', e);
        return false;
      }
    }
    // No WASM core — the three gates of `flush_allowed()`
    // (core/src/client.rs:1510-1521), in the same order, with the same
    // semantics:
    //   1. analytics consent (allowed-unless-denied; the old `=== true` check
    //      here was default-DENIED, which combined with the flushAsync() gate
    //      meant the JS-only engine accepted events and never delivered them),
    //   2. Do-Not-Track — an unconditional veto when `respectDnt` is on, even
    //      over an explicit analytics grant,
    //   3. consentRequired (EU opt-in mode), which demands an EXPLICIT grant:
    //      the allowed-unless-denied default must not open the gate.
    if (!this.analyticsAllowed()) return false;
    if (this.respectDnt && this.dntActive) return false;
    if (this.consentRequired && !this.analyticsExplicitlyGranted()) return false;
    return true;
  }

  /**
   * The delivery gate for a wrapper-built request that carries NO event batch
   * — `/clicks/resolve` on React Native is the caller this exists for.
   *
   * Reads the same state as the flush gate (privacy, Retry-After, circuit
   * breaker) and takes NONE of its side effects. `shouldAttemptFlush()` is
   * unusable here in both directions: it MAY claim the breaker's half-open
   * probe slot, and a caller with no batch has no `recordFlushResult` to
   * follow through with — while the release primitive, `abortFlushAttempt()`,
   * also clears the `$first_open` in-flight claim, so handing the probe back
   * would cost a concurrent batch its delivery receipt and re-arm the event.
   *
   * So the breaker is READ, not checked: only `closed` admits a side request.
   * `open` is refusing traffic, and `half-open` means the flush loop's single
   * probe is the one request allowed through — best-effort traffic must not
   * spend it.
   */
  shouldAttemptSideRequest(): boolean {
    if (this._isShutDown) return false;
    if (!this.flushAllowed()) return false;
    if (this.wasm) {
      try {
        return this.wasm.shouldAttemptSideRequest();
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM shouldAttemptSideRequest failed:', e);
        return false;
      }
    }
    // JS engine mirror of `should_attempt_side_request` in client.rs.
    if (this._retryAfterUntilMs > 0 && Date.now() < this._retryAfterUntilMs) return false;
    return this.circuitState === 'closed';
  }

  /** Whether analytics consent was GRANTED outright, never merely defaulted. */
  private analyticsExplicitlyGranted(): boolean {
    return this.consent.analyticsStorage === true || this.consent.analytics === true;
  }

  /** Whether cookieless mode (web) was enabled at init. */
  isCookielessMode(): boolean {
    if (this.wasm) {
      try {
        return this.wasm.isCookielessMode();
      } catch (_e) {
        return false;
      }
    }
    return false;
  }

  /**
   * Take a crash-safety snapshot of the queued events (synchronous).
   *
   * Does NOT deliver. HTTP delivery is `flushAsync()` — the public SDK
   * wrappers' `flush()` methods await that. This method exists for
   * synchronous contexts (pagehide, process exit) where only a durable
   * copy can be guaranteed.
   */
  flush(): void {
    this.checkNotShutDown();
    this.doFlush();
  }

  async flushAsync(): Promise<void> {
    this.checkNotShutDown();

    // Cadence hook 3 of 4, and it sits ABOVE the privacy gate deliberately:
    // emitting only QUEUES the beacon, and a consent-denied install still has
    // to be able to report the backlog it is accumulating. Ticking here rather
    // than after the empty-queue check is what lets a fully-drained, healthy,
    // traffic-free install keep beaconing at all.
    this._healthFlushTicks += 1;
    this.maybeEmitHealth();

    // Privacy gate for EVERY delivery path. The Rust queue is already
    // protected (drain() is gated internally), but the JS queue — the
    // fallback engine plus rehydrated events, which now include gated
    // pre-consent backlogs persisted at shutdown — was posted without a
    // check, so denied/pre-grant events could ship after a restart.
    if (!this.flushAllowed()) {
      if (this.enableDebug) {
        console.log('[Layers] flushAsync skipped — privacy gate not passing');
      }
      return;
    }

    // Prevent concurrent flush calls from causing drain-send-requeue races
    if (this._flushing) return;
    this._flushing = true;

    // Delivery policy lives in the Rust core — or its JS mirror on the
    // fallback engine (adr/0001-rust-owned-delivery-policy.md): one
    // pre-flight gate (Retry-After + circuit breaker), one HTTP attempt
    // per batch, one post-flight report whose verdict decides requeue vs
    // drop. No JS-side retry loops or backoff — the next flush tick
    // retries, gated by the policy.
    try {
      // WASM path: drain a batch from the Rust queue as a serialized JSON string
      if (this.wasm) {
        const liveAttStatus = await this.readLiveAttStatus();
        if (!this.flushAllowed()) return;
        // First flush any rehydrated JS events (from persistence on
        // init). flushJsQueue gates and reports through the same policy.
        if (this.queue.length > 0) {
          await this.flushJsQueue(liveAttStatus);
        }

        if (this.wasm.queueDepth() === 0) {
          // Nothing in the Rust queue — but flushJsQueue above may have
          // just delivered rehydrated events. Re-sync the snapshot so a
          // crash/reload doesn't re-hydrate and resend them.
          this.doFlush();
          return;
        }

        // Gate only when there's something to send: shouldAttemptFlush()
        // may claim the circuit breaker's half-open probe slot, which an
        // idle tick must not consume.
        if (!this.wasm.shouldAttemptFlush()) {
          if (this.enableDebug) {
            console.log('[Layers] flushAsync skipped — core delivery gate closed');
          }
          return;
        }

        // After a passed gate, EVERY pre-wire abort path must call
        // abortFlushAttempt() — it releases a claimed half-open breaker
        // probe WITHOUT counting a delivery failure (no request was made,
        // so neither success nor failure is true).
        const delivery = this.wasm.drainDelivery(this.maxBatchSize, liveAttStatus);
        if (delivery === null) {
          this.wasm.abortFlushAttempt();
          this.doFlush();
          return;
        }
        const { url, body: batchJson } = delivery;

        // No pre-parsing needed for requeue: the core's requeue() accepts
        // the full EventBatch JSON exactly as drain() produced it, so the
        // drained batch can ALWAYS be put back — there is no
        // "unparseable, can't requeue" loss path.
        const body = new TextEncoder().encode(batchJson);
        let headers: Array<[string, string]>;
        try {
          headers = this.wasm.flushHeaders();
        } catch (e) {
          // Pre-wire setup failure — release the probe, requeue, surface.
          this.wasm.abortFlushAttempt();
          try {
            this.wasm.requeue(batchJson);
            this.doFlush();
          } catch {
            // Requeue failed — events lost. The drained batch is gone from the
            // Rust queue and could not be put back, so this is real loss and
            // the only place it can be counted.
            this.recordDrop('requeue_failed', countBatchEvents(batchJson));
          }
          throw e;
        }
        // Inject X-Debug-Token in JS rather than relying on the Rust core to add
        // it. WASM SDKs always do HTTP from JS, so this is the correct seam.
        const debugToken = this.getDebugToken();
        if (debugToken !== null) {
          headers.push(['X-Debug-Token', debugToken]);
        }

        // One attempt; a missing response (network error / timeout) is
        // reported to the core as status 0.
        let status = 0;
        let retryAfterHeader: string | null = null;
        try {
          const resp = await this.httpClient.post(url, headers, body);
          status = resp.status;
          retryAfterHeader = this.extractHeader(resp.headers, 'retry-after');
        } catch {
          // No response — status stays 0.
        }

        const verdict = this.wasm.recordFlushResult(status, retryAfterHeader);
        if (verdict === 'delivered') {
          // Re-sync the crash-safety snapshot with the (now smaller)
          // queue so a crash doesn't re-deliver what just shipped.
          this.doFlush();
          return;
        }
        if (verdict === 'drop') {
          // Permanent rejection — discard the batch (identical bytes
          // cannot succeed later; retrying would wedge the queue behind a
          // poison batch), re-sync the snapshot, surface the error.
          this.doFlush();
          throw new LayersError('HTTP', `HTTP ${status}`, status);
        }
        // retry_later — requeue; the next flush tick retries, gated by
        // the core's Retry-After guard and circuit breaker.
        try {
          this.wasm.requeue(batchJson);
          // Re-sync the crash-safety snapshot — the emptying drain
          // deleted it, so the requeued events would otherwise exist
          // only in memory until the next flush.
          this.doFlush();
        } catch {
          // Requeue failed — events lost. A `retry_later` verdict is NOT a drop
          // on its own; it becomes one exactly here, when the batch cannot be
          // put back. Counting it is what stops the batch leaving the ledger
          // with no trace.
          this.recordDrop('requeue_failed', countBatchEvents(batchJson));
        }
        return;
      }

      // JS fallback path — mirrors the core policy in TS.
      if (this.queue.length === 0) return;
      const liveAttStatus = await this.readLiveAttStatus();
      if (!this.flushAllowed()) return;
      if (!this.fallbackShouldAttemptFlush()) {
        if (this.enableDebug) {
          console.log('[Layers] flushAsync skipped — fallback delivery gate closed');
        }
        return;
      }

      const events = this.drainJsQueue(this.maxBatchSize);
      if (events.length === 0) {
        // Gate passed but the queue raced empty — pre-wire abort: release
        // a claimed probe WITHOUT counting a delivery failure (mirrors
        // the core's abortFlushAttempt).
        this.fallbackAbortFlushAttempt();
        return;
      }
      // These events are neither queued nor delivered until the verdict lands.
      // Counting them makes the balance identity exact rather than "± whatever
      // happens to be on the wire".
      this._health.inflight += events.length;
      this._inflightEvents = events;

      const routed = this.routeForDelivery(events, liveAttStatus);
      const batch = this.createBatch(routed.events);
      const body = new TextEncoder().encode(JSON.stringify(batch));
      const headers = this.buildHeaders();

      let status = 0;
      let retryAfterHeader: string | null = null;
      try {
        const resp = await this.httpClient.post(routed.url, headers, body);
        status = resp.status;
        retryAfterHeader = this.extractHeader(resp.headers, 'retry-after');
      } catch {
        // No response — status stays 0.
      }
      // Off the wire. Cleared BEFORE any snapshot below: after the verdict
      // these events are either delivered, dropped, or back in `queue`.
      this._inflightEvents = [];

      const verdict = this.fallbackRecordFlushResult(status, retryAfterHeader);
      this.recordFlushOutcome(status, verdict, events.length);
      if (verdict === 'delivered') {
        // The receipt `first_open_emitted` was never allowed to be.
        this.confirmFirstOpenDelivered(events);
        // Re-sync the crash-safety snapshot with the (now smaller) queue.
        this.doFlush();
        return;
      }
      if (verdict === 'drop') {
        this.doFlush();
        throw new LayersError('HTTP', `HTTP ${status}`, status);
      }
      // retry_later — re-enqueue and re-sync the snapshot so the requeued
      // events have a durable copy.
      this.queue.unshift(...events.map(withoutIdfa));
      this.doFlush();
    } finally {
      this._inflightEvents = [];
      this._flushing = false;
    }
  }

  async fetchRemoteConfig(): Promise<void> {
    this.checkNotShutDown();

    const ctx = this.deviceContext;
    const platform = ctx.platform ?? 'web';
    const url = `${this.baseUrl}/config?app_id=${encodeURIComponent(this.appId)}&platform=${platform}`;
    // This path builds its own headers rather than going through the Rust
    // core's `config_headers()` (which Swift and Kotlin use), so it has to
    // carry the engine token itself. Without it, /config from web, Node AND
    // every React Native install was the one endpoint where the three engines
    // stayed indistinguishable — the exact gap this header exists to close.
    const headers: Array<[string, string]> = [
      ['X-App-Id', this.appId],
      ['X-SDK-Version', this.sdkVersionHeader()]
    ];
    const debugToken = this.getDebugToken();
    if (debugToken !== null) {
      headers.push(['X-Debug-Token', debugToken]);
    }

    const resp = await this.httpClient.get(url, headers);
    if (resp.status >= 200 && resp.status < 300) {
      const body = new TextDecoder().decode(resp.body);
      if (body) {
        // Normalize: extract the config object from the server envelope so the
        // JS fallback cache matches the WASM path's flat RemoteConfig shape.
        let config: Record<string, unknown> | null = null;
        try {
          const parsed = JSON.parse(body);
          config = (parsed?.data?.config ?? parsed?.config ?? parsed) as Record<string, unknown>;
          this._remoteConfigJson = JSON.stringify(config);
          // Parse the events block ONCE per fetch rather than once per event:
          // the fallback engine consults it twice on every track() (allow/deny
          // then sampling), and re-parsing the whole config each time is work
          // React Native pays on the main thread.
          const events: unknown = config?.events;
          this._eventsConfig =
            events && typeof events === 'object' && !Array.isArray(events)
              ? (events as FallbackEventsConfig)
              : null;
        } catch {
          this._remoteConfigJson = body;
          // Unparseable config — never block events on a config error. That
          // FAILS OPEN: the killswitch and the denylist stop being enforced,
          // which is a server-side control silently not working. It costs no
          // events, so it is a degradation rather than a drop.
          this._eventsConfig = null;
          this._healthDegraded.add('remote_config_unparseable');
        }

        // Cadence knobs for the beacon itself, served alongside `events` — see
        // `sdk_apps.health_config`. An absent block leaves the defaults in
        // place; `enabled: false` is the rollback kill, and the ONLY thing that
        // suppresses the beacon.
        if (config !== null) {
          const health: unknown = config.health;
          this._healthConfig =
            health && typeof health === 'object' && !Array.isArray(health)
              ? (health as HealthRemoteConfig)
              : null;
          this._configFetchedAtMs = Date.now();
          this._configVersion = typeof config.version === 'string' ? config.version : null;
          this._configEtag = resp.headers.find(([k]) => k.toLowerCase() === 'etag')?.[1] ?? null;
          this._healthDegraded.delete('remote_config_unparseable');
        }

        // Tier 4 / Tier 8 / SKAN sections, applied to the JS engines. The Rust
        // core takes all three from `updateRemoteConfig` below
        // (client.rs:1767-1777); this is the same propagation for the engine
        // that actually runs on React Native.
        //
        // Deliberately OUTSIDE the parse try/catch above. Sharing it would let
        // a throw anywhere in here jump into that catch and overwrite
        // `_remoteConfigJson` / `_eventsConfig` with the "unparseable" values
        // for a config that parsed perfectly well — turning a feature-config
        // problem into a silent loss of the allowlist and sampling rules.
        if (config !== null) {
          this._skanConfig = config.skan ?? null;
          if (!this.wasm) {
            try {
              // A section that is absent or the wrong shape parses to null and
              // is skipped, leaving the previous definitions in place — same as
              // `config.feature_flags: None` in client.rs:1768.
              const flags = parseFeatureFlagConfig(config.feature_flags);
              if (flags) {
                this.flagEngine.setDefinitions(flags);
                // Fresh definitions can change every response, so the next read
                // re-emits one exposure per flag (client.rs:1770-1772).
                this.flagEngine.clearExposureDedup();
              }
              const surveys = parseSurveyConfig(config.surveys);
              if (surveys) this.surveyEngine.setDefinitions(surveys);
            } catch (e) {
              if (this.enableDebug) {
                console.warn('[Layers] failed to apply feature config:', e);
              }
            }
            this._fireFeatureFlagListeners();
          }
        }

        // Push config into WASM/Rust core so it can apply sampling, rate limits, etc.
        if (this.wasm) {
          try {
            const etag = resp.headers.find(([k]) => k.toLowerCase() === 'etag')?.[1] ?? null;
            this.wasm.updateRemoteConfig(body, etag ?? undefined);
            // Tier 4: a successful config refresh may have produced new flag
            // definitions. Notify subscribers so they can re-render. The
            // listener fires even when the flag set didn't change — that's
            // the same posh /decide-style refresh contract PostHog uses.
            this._fireFeatureFlagListeners();
            this._healthDegraded.delete('remote_config_not_applied');
          } catch {
            // Best-effort — config is still available via getRemoteConfigJson().
            // The Rust core keeps its STALE sampling, rate limits and
            // killswitch, so every server-side control is silently a refresh
            // behind. No events are lost, so it is a degradation, not a drop.
            this._healthDegraded.add('remote_config_not_applied');
          }
        }
      }
    }
  }

  /**
   * Return the cached remote config JSON string from the last successful
   * fetchRemoteConfig() call. Returns null if config has not been fetched yet.
   */
  getRemoteConfigJson(): string | null {
    // Prefer the WASM core's stored config (authoritative after updateRemoteConfig)
    if (this.wasm) {
      try {
        const val: unknown = this.wasm.getRemoteConfigJson();
        if (typeof val === 'string') return val;
      } catch {
        // Fall through to JS-side cache
      }
    }
    return this._remoteConfigJson;
  }

  shutdown(): void {
    if (this._isShutDown) return;

    // Cadence hook 4 of 4, and it must run BEFORE `_isShutDown` is set and
    // before the queue is emptied below — otherwise the final beacon is built
    // against a queue that has already been drained (reporting depth 0 for
    // events that were still owed) or refused outright by `checkNotShutDown`.
    // Emitting here means it rides out inside the shutdown file with everything
    // else, and the next launch delivers it.
    this.maybeEmitHealth(true);

    this._isShutDown = true;

    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    // When WASM is loaded, persist the Rust queue contents. Use the
    // UNGATED queueSnapshotJson(), not drain(): drain() is privacy-gated
    // and returns null when the gate is closed (consent denied /
    // consent_required pre-grant) even with events queued — using it here
    // silently dropped the whole pre-consent backlog at shutdown (BugBot).
    // Durability is not delivery; gated events must survive a restart.
    let shutdownPersisted = true;
    if (this.wasm) {
      try {
        const eventsJson: string | null = this.wasm.queueSnapshotJson();
        if (eventsJson !== null) {
          const key = `events_shutdown_${Date.now()}`;
          let parsed: QueuedEvent[] | null = null;
          try {
            const value: unknown = JSON.parse(eventsJson);
            if (Array.isArray(value)) parsed = value as QueuedEvent[];
          } catch {
            // Unparseable — fall through to the single-key write below, which
            // preserves the bytes verbatim.
          }
          if (parsed !== null) {
            if (!this.writeEventChunks(key, parsed)) shutdownPersisted = false;
          } else {
            try {
              this.persistence.write(key, new TextEncoder().encode(eventsJson));
            } catch {
              // Best effort — keep the rolling snapshot as the fallback copy.
              shutdownPersisted = false;
            }
          }
        }
      } catch {
        // WASM snapshot failed
        this._health.persistWriteFailures += 1;
        this._health.dirty = true;
        shutdownPersisted = false;
      }
      try {
        // Rust's own shutdown persistence lands in NullPersistence on WASM;
        // the durable copy is the one written above.
        this.wasm.shutdown();
      } catch {
        // WASM shutdown failed
      }
    }

    // Persist any remaining JS fallback queue events (includes rehydrated
    // events). Chunked for the same reason the rolling snapshot is: this is
    // the LARGEST write the SDK ever makes — the entire queue, at the one
    // moment it cannot be retried — and a single >2 MB AsyncStorage row is
    // unreadable on Android, taking the identity record down with it.
    if (this.queue.length > 0) {
      const key = `events_shutdown_js_${Date.now()}`;
      if (!this.writeEventChunks(key, this.queue)) {
        // Best effort — keep the rolling snapshot as the fallback copy.
        //
        // COUNTED AS LOSS ONLY IF NOTHING DURABLE SURVIVES. `shutdownPersisted`
        // stays false here, which is precisely what stops `deleteSnapshot()`
        // running below — so in the ordinary case the rolling snapshot still
        // holds these events and the next launch hydrates them. Counting them
        // anyway would book the loss twice: once here, and once again as queue
        // depth after they come back, which yields a NEGATIVE residual. The
        // server spec reads a negative residual as corruption
        // (`sdk-health-negative-residual`), so an over-count is not a harmless
        // conservative choice — it raises a different, louder, wrong alarm.
        //
        // Real loss is the case where the store is failing so completely that
        // no snapshot exists either; that is what this probe distinguishes.
        if (!this.hasDurableSnapshot()) {
          this.recordDrop('shutdown_write_failed', this.queue.length);
        } else {
          this._health.persistWriteFailures += 1;
          this._health.dirty = true;
        }
        shutdownPersisted = false;
      }
      this.queue = [];
    }

    // The shutdown files above hold everything that was still queued; a
    // leftover rolling snapshot would only duplicate those events on the
    // next launch. ONLY delete it when every shutdown write succeeded — if
    // one failed, the snapshot may be the last durable copy of those
    // events. Deliberate trade-off: on PARTIAL success (e.g. the WASM
    // batch wrote but the JS one failed) the kept snapshot overlaps the
    // written shutdown file and the next launch re-sends the overlap —
    // server-side event_id dedup absorbs duplicates, while a lost event is
    // gone for good. At-least-once beats at-most-once here.
    if (shutdownPersisted) {
      this.deleteSnapshot();
    }

    // Last durable flush point for the counters. `persistHealthCounters` writes
    // the identity record, which is a different key from the event files above,
    // so a failure there does not cost events — only the counter tail.
    this.persistHealthCounters(true);
  }

  isInitialized(): boolean {
    if (this.wasm) {
      try {
        return this.wasm.isInitialized();
      } catch {
        // WASM failed — fall back to JS
      }
    }
    return !this._isShutDown;
  }

  getDeviceContext(): DeviceContext {
    return { ...this.deviceContext };
  }

  setDeviceContext(context: DeviceContext): void {
    this.checkNotShutDown();
    // Normalize here, once, for BOTH engines: this is the single boundary the
    // Rust core and the JS engine both receive device context through, and the
    // Rust core stores the string verbatim. See {@link normalizeLocale}.
    const normalized: DeviceContext = { ...context };
    // Install identity survives a partial update, matching
    // core/src/client.rs::set_device_context. Everything else (IDFA, ATT
    // status, deeplink id, ...) keeps replace semantics so omitting it
    // still clears it.
    for (const key of INSTALL_IDENTITY_KEYS) {
      if (normalized[key] === undefined && this.deviceContext[key] !== undefined) {
        Object.assign(normalized, { [key]: this.deviceContext[key] });
      }
    }
    if (typeof normalized.locale === 'string') {
      normalized.locale = normalizeLocale(normalized.locale);
    }
    this.deviceContext = normalized;

    if (this.wasm) {
      try {
        // The Rust core gets the NORMALIZED context, not the caller's — both
        // engines have to agree on what reaches the wire.
        this.wasm.setDeviceContext(normalized);
      } catch (e) {
        if (this.enableDebug) console.warn('[Layers] WASM setDeviceContext failed:', e);
      }
      // The Rust core emits its own parked `$first_open` from inside
      // set_device_context — nothing for the JS layer to do.
      return;
    }

    this.backfillSessionEvents(normalized);

    // First context arrival is the natural emission point for a parked
    // `$first_open` — the event now carries the real platform instead of the
    // pre-context default (core/src/client.rs::set_device_context).
    this.emitPendingFirstOpen();
  }

  /**
   * Fill in device fields that events of the CURRENT session were queued
   * without, from a newly arrived context. Placeholders only: a field that
   * already holds a real value is never overwritten.
   *
   * React Native builds its first events before it can read the device:
   * `$first_open` is built on the eager core the moment the constructor stamps
   * the synchronous context, and the swap to the durable core emits
   * `$sdk_health`. Both went out with no `install_id` and `app_version:
   * "unknown"`, because the install id and the app version need an
   * AsyncStorage read and a native round trip that finish later. Pre-init
   * user events had the same gap. The session match keeps this to the launch
   * that is setting the context; events hydrated from earlier launches keep
   * the context they were built under.
   *
   * Ad identifiers (`idfa`, `att_status`) are stamped at delivery time.
   */
  private backfillSessionEvents(ctx: DeviceContext): void {
    const sessionId = this._sessionId;
    for (const event of this.queue) {
      if (event.session_id !== sessionId) continue;
      if (event.app_version === 'unknown' && ctx.appVersion) event.app_version = ctx.appVersion;
      if (event.os_version === 'unknown' && ctx.osVersion) event.os_version = ctx.osVersion;
      if (event.device_model === 'unknown' && ctx.deviceModel) {
        event.device_model = ctx.deviceModel;
      }
      if (!event.install_id && ctx.installId) event.install_id = ctx.installId;
      if (!event.build_number && ctx.buildNumber) event.build_number = ctx.buildNumber;
      if (!event.screen_size && ctx.screenSize) event.screen_size = ctx.screenSize;
      if (!event.idfv && ctx.idfv) event.idfv = ctx.idfv;
      if (!event.timezone && ctx.timezone) event.timezone = ctx.timezone;
    }
  }

  getSessionId(): string {
    return this.getOrRotateSessionId();
  }

  /**
   * Start a new session: fresh session ID, reset activity timer, and — because
   * this is a session boundary like any other — `session_number` incremented
   * and persisted.
   *
   * Note: The WASM core does not expose a session reset method, so when WASM
   * is loaded, the Rust-side session continues its own timeout-based rotation.
   * The JS fallback session is always reset.
   */
  startNewSession(): void {
    this.checkNotShutDown();
    this.rotateSession();
  }

  getConsentState(): ConsentState {
    if (this.wasm) {
      try {
        return this.wasm.getConsentState() as ConsentState;
      } catch {
        // WASM failed — fall back to JS
      }
    }
    return { ...this.consent };
  }

  queueDepth(): number {
    // When WASM is loaded, the Rust queue is the source of truth.
    // Add any rehydrated JS events that haven't been flushed yet.
    if (this.wasm) {
      return this.wasm.queueDepth() + this.queue.length;
    }
    return this.queue.length;
  }

  /**
   * Drain queued events and return a JSON Blob suitable for navigator.sendBeacon().
   * Returns null if no events are available, or if the delivery gate is shut.
   *
   * Drained events are held internally. If sendBeacon succeeds, call
   * clearBeaconEvents(). If it fails, call requeueBeaconEvents() to push
   * events back into the queue before falling back to flush().
   *
   * GATED, because the beacon is a delivery mechanism and ADR 0001 admits
   * exactly one gate for all delivery. It used to pass none of them, which
   * was survivable only by accident: consent-denied events were destroyed at
   * `track()`, so an ungated unload had nothing private left to leak. Now
   * that they correctly QUEUE and wait for a grant, an ungated beacon would
   * ship the entire denied backlog the moment the tab closes — the exact
   * data the user refused, delivered by the one path nobody watches.
   *
   * A shut gate returns null, and every caller's null branch falls through
   * to `flush()` — the crash-safety snapshot. Delivery is what's gated;
   * persistence is not, so the backlog survives to the next launch and ships
   * once the gate opens. Nothing is dropped.
   */
  createBeaconPayload(): Blob | null {
    // Clear any stale beacon events from a previous call
    this._lastBeaconEvents = [];
    this._lastBeaconWasmJson = null;

    // Privacy gate first — consent / DNT / consentRequired-pre-grant.
    if (!this.flushAllowed()) {
      if (this.enableDebug) {
        console.log('[Layers] beacon suppressed — privacy gate not passing');
      }
      return null;
    }

    // Check for work BEFORE the delivery gate: `shouldAttemptFlush()` can
    // claim the breaker's half-open probe slot, and an unload with an empty
    // queue must not consume it (same reasoning as `flushAsync`).
    if (this.queueDepth() === 0) return null;

    // Retry-After + circuit breaker, through the very same call the other
    // delivery paths make. An open breaker or a server-issued Retry-After
    // suppresses the beacon: the server has said "stop", and a beacon cannot
    // read the reply that would say otherwise.
    if (!(this.wasm ? this.wasm.shouldAttemptFlush() : this.fallbackShouldAttemptFlush())) {
      if (this.enableDebug) {
        console.log('[Layers] beacon suppressed — core delivery gate closed');
      }
      return null;
    }

    if (this.wasm) {
      // First include any rehydrated JS events
      let jsEvents: QueuedEvent[] = [];
      if (this.queue.length > 0) {
        jsEvents = this.drainJsQueue(this.maxBatchSize);
      }

      const batchJson: string | null = this.wasm.drain(this.maxBatchSize);

      if (batchJson === null && jsEvents.length === 0) {
        // Gate passed but nothing came out — pre-wire abort releases the
        // claimed probe without recording a delivery failure.
        this.wasm.abortFlushAttempt();
        return null;
      }

      // Stash for potential requeue
      this._lastBeaconEvents = jsEvents;
      const routed = this.routeForDelivery(jsEvents, null);
      this._lastBeaconWasmJson = batchJson;
      let wasmEventCount = 0;
      if (batchJson !== null) {
        try {
          wasmEventCount = ((JSON.parse(batchJson) as { events?: unknown[] }).events ?? []).length;
        } catch {
          // Unparseable — the requeue path handles it; count nothing here.
        }
      }
      this._lastBeaconInflight = jsEvents.length + wasmEventCount;
      this._health.inflight += this._lastBeaconInflight;

      // Merge JS events into the WASM batch if both exist
      if (batchJson !== null && jsEvents.length > 0) {
        try {
          const parsed = JSON.parse(batchJson);
          const wasmEvents: QueuedEvent[] = parsed.events ?? [];
          const merged = this.createBatch([...routed.events, ...wasmEvents]);
          return new Blob([JSON.stringify(merged)], { type: 'application/json' });
        } catch {
          // Parse failed — send just the WASM batch
          return new Blob([batchJson], { type: 'application/json' });
        }
      }

      if (batchJson !== null) {
        return new Blob([batchJson], { type: 'application/json' });
      }

      // Only JS events
      const batch = this.createBatch(routed.events);
      return new Blob([JSON.stringify(batch)], { type: 'application/json' });
    }

    // JS fallback path
    const events = this.drainJsQueue(this.maxBatchSize);
    if (events.length === 0) {
      // Pre-wire abort, as above.
      this.fallbackAbortFlushAttempt();
      return null;
    }

    this._lastBeaconEvents = events;
    this._lastBeaconInflight = events.length;
    this._health.inflight += events.length;
    const routed = this.routeForDelivery(events, null);
    const batch = this.createBatch(routed.events);
    return new Blob([JSON.stringify(batch)], { type: 'application/json' });
  }

  /**
   * Push events drained by createBeaconPayload() back into the queue.
   * Call this when sendBeacon returns false, before calling flush().
   */
  requeueBeaconEvents(): void {
    const hadDrainedEvents = this._lastBeaconWasmJson !== null || this._lastBeaconEvents.length > 0;

    // Requeue WASM events back into the Rust queue
    if (this._lastBeaconWasmJson !== null && this.wasm) {
      try {
        const parsed = JSON.parse(this._lastBeaconWasmJson);
        const eventsJson = JSON.stringify(parsed.events ?? []);
        this.wasm.requeue(eventsJson);
      } catch {
        // Parse failed — events lost from WASM side. The count is whatever the
        // drain reported minus the JS half, which is all this side can know.
        this.recordDrop(
          'requeue_failed',
          Math.max(0, this._lastBeaconInflight - this._lastBeaconEvents.length)
        );
      }
    }

    // Requeue JS events back into the JS queue
    if (this._lastBeaconEvents.length > 0) {
      this.queue.unshift(...this._lastBeaconEvents.map(withoutIdfa));
    }

    // Back in a queue (or counted as lost) either way — no longer in flight.
    this._health.inflight = Math.max(0, this._health.inflight - this._lastBeaconInflight);
    this._lastBeaconInflight = 0;
    this._lastBeaconEvents = [];
    this._lastBeaconWasmJson = null;

    // `sendBeacon` refused the payload, so nothing reached the wire. That is
    // a pre-wire abort: release the half-open probe `createBeaconPayload`
    // claimed, WITHOUT recording a delivery failure (no request was made, so
    // neither success nor failure is true). Both helpers no-op when the
    // breaker is not half-open.
    if (hadDrainedEvents) {
      if (this.wasm) {
        this.wasm.abortFlushAttempt();
      } else {
        this.fallbackAbortFlushAttempt();
      }
    }
  }

  /**
   * Clear the held beacon events after a successful sendBeacon call.
   */
  clearBeaconEvents(): void {
    const hadDrainedEvents = this._lastBeaconWasmJson !== null || this._lastBeaconEvents.length > 0;

    // UNACKED, never "delivered". The browser accepted the payload for
    // background delivery; the SERVER's answer is unobservable by construction,
    // so the reconciler is told these events' fate is unknown rather than being
    // handed a receipt nothing ever issued.
    this._health.deliveredUnacked += this._lastBeaconInflight;
    this._health.inflight = Math.max(0, this._health.inflight - this._lastBeaconInflight);
    this._health.dirty = true;
    this._lastBeaconInflight = 0;
    this._lastBeaconEvents = [];
    this._lastBeaconWasmJson = null;

    // Release the half-open probe, exactly as the failure path does.
    //
    // "Success" here means the browser accepted the payload for background
    // delivery — NOT that the server accepted it. A beacon's HTTP outcome is
    // unobservable by construction, so this claim can never be settled by the
    // thing that took it. The claim exists to serialize probes of a possibly
    // down server; holding one that will never resolve blocks the next real
    // flush for the full 45s expiry, which bites whenever the page comes back
    // (visibilitychange hidden → visible) rather than actually unloading.
    //
    // Releasing fabricates nothing — unlike reporting a success we did not
    // observe, which would close the breaker on no evidence — and blocks
    // nothing. The next flush, the one with a readable status code, becomes
    // the true probe. Worst case is one extra request against a recovering
    // server, which is the cheaper of the two errors.
    if (hadDrainedEvents) {
      if (this.wasm) {
        this.wasm.abortFlushAttempt();
      } else {
        this.fallbackAbortFlushAttempt();
      }
    }

    // Re-sync the crash-safety snapshot with the (now smaller) queues so a
    // reload doesn't re-hydrate and resend what the beacon just delivered.
    // Synchronous persistence write — safe in pagehide/beforeunload, the
    // context where beacons fire.
    this.doFlush();
  }

  // --- Internal ---

  /** Keys that could enable prototype pollution attacks. */
  private static readonly BANNED_KEYS = new Set(['__proto__', 'constructor', 'prototype']);
  private static readonly MAX_PROPERTIES_COUNT = 100;
  private static readonly MAX_KEY_LENGTH = 128;
  private static readonly MAX_VALUE_SIZE = 65_536; // 64 KB
  private static readonly MAX_PAYLOAD_SIZE = 1_048_576; // 1 MB

  /**
   * Validate event/user properties to prevent prototype pollution and enforce size limits.
   * Returns the sanitized properties object, or throws LayersError if invalid.
   */
  private static validateProperties(
    properties: Record<string, unknown>,
    label: string
  ): Record<string, unknown> {
    const keys = Object.keys(properties);
    if (keys.length > LayersCore.MAX_PROPERTIES_COUNT) {
      throw new LayersError(
        'INVALID_ARGUMENT',
        `${label} exceeds max property count (${LayersCore.MAX_PROPERTIES_COUNT})`
      );
    }

    let totalSize = 0;
    for (const key of keys) {
      if (LayersCore.BANNED_KEYS.has(key)) {
        throw new LayersError('INVALID_ARGUMENT', `${label} contains disallowed key: ${key}`);
      }
      if (key.length > LayersCore.MAX_KEY_LENGTH) {
        throw new LayersError(
          'INVALID_ARGUMENT',
          `${label} key exceeds max length (${LayersCore.MAX_KEY_LENGTH}): ${key.slice(0, 32)}...`
        );
      }
      const val = properties[key];
      const valSize = typeof val === 'string' ? val.length : (JSON.stringify(val)?.length ?? 0);
      if (valSize > LayersCore.MAX_VALUE_SIZE) {
        throw new LayersError(
          'INVALID_ARGUMENT',
          `${label} value for key "${key}" exceeds max size (${LayersCore.MAX_VALUE_SIZE} bytes)`
        );
      }
      totalSize += key.length + valSize;
    }

    if (totalSize > LayersCore.MAX_PAYLOAD_SIZE) {
      throw new LayersError(
        'INVALID_ARGUMENT',
        `${label} total payload exceeds max size (${LayersCore.MAX_PAYLOAD_SIZE} bytes)`
      );
    }

    return properties;
  }

  private checkNotShutDown(): void {
    if (this._isShutDown) {
      throw new LayersError('SHUT_DOWN', 'SDK is shut down');
    }
  }

  /**
   * Apply the registered before_send hook to a track/screen call.
   *
   * Returns:
   *  - `null` if the hook returned `null` (drop the event).
   *  - `undefined` if no hook is registered (no-op — caller proceeds).
   *  - A `BeforeSendEvent` if the hook returned a (possibly modified) event.
   *
   * Hook exceptions are swallowed in production, logged in debug mode, and
   * cause the event to be dropped — same semantics as the Swift / Kotlin
   * before_send adapters which return `nil` on adapter failure.
   */
  private applyBeforeSend(
    eventName: string,
    properties: EventProperties | undefined,
    userId: string | undefined,
    anonymousId: string | undefined,
    options?: { isScreen?: boolean; screenName?: string }
  ): BeforeSendEvent | null | undefined {
    if (!this._beforeSendHook) return undefined;
    const propertiesCopy: Record<string, unknown> =
      options?.isScreen && options.screenName
        ? { screen_name: options.screenName, ...(properties ?? {}) }
        : { ...(properties ?? {}) };
    const input: BeforeSendEvent = {
      event: eventName,
      properties: propertiesCopy,
      ...(userId != null && { userId }),
      ...(anonymousId != null && { anonymousId })
    };
    try {
      const result = this._beforeSendHook(input);
      if (result === null) {
        this.recordDrop('before_send_returned_none');
        return null;
      }
      if (result === undefined) return undefined;
      // Hook returned a (possibly modified) event — normalize properties to a plain object
      if (!result.event || typeof result.event !== 'string') {
        if (this.enableDebug) {
          console.warn('[Layers] before_send hook returned invalid event — passing through');
        }
        return undefined;
      }
      const normalized: BeforeSendEvent = {
        event: result.event,
        properties: result.properties ?? {},
        ...(result.userId != null && { userId: result.userId }),
        ...(result.anonymousId != null && { anonymousId: result.anonymousId })
      };
      return normalized;
    } catch (e) {
      if (this.enableDebug) console.warn('[Layers] before_send hook threw — dropping event:', e);
      this.recordDrop('before_send_errored');
      return null;
    }
  }

  /**
   * Apply the registered `before_send` hook to a FULLY-BUILT fallback event —
   * the last gate before `enqueue`, exactly where the Rust core runs it
   * (client.rs:872-879).
   *
   * Returns `false` when the event must be dropped: the hook returned `null`,
   * or it threw (fail-closed, matching `apply_before_send`'s `Err` arm).
   *
   * The hook is handed the same `BeforeSendEvent` shape every wrapper's
   * `setBeforeSend` already speaks, populated from the built event, and its
   * result is written back onto that event. Rust hands over the whole typed
   * `Event`, so a hook there can also rewrite `event_id`/`timestamp`/device
   * fields; this surface is deliberately narrower — the fields it does expose
   * behave identically.
   */
  private applyBeforeSendToEvent(event: QueuedEvent, isScreen: boolean): boolean {
    if (!this._beforeSendHook) return true;

    const properties: Record<string, unknown> = { ...(event.properties ?? {}) };

    // Commerce keys were just promoted OUT of `properties` onto the event, and
    // Rust's hook still sees them because it is handed the whole `Event`
    // including `extra` (client.rs:836-853 then :873). This surface only
    // carries `properties`, so without lending them back a PII-redaction hook
    // that deletes `receipt_data` would stop redacting the moment promotion
    // moved the key — and `receipt_data` would ship top-level regardless of
    // what the hook did. They are taken back out below.
    const isCommerce = LayersCore.COMMERCE_EVENTS.has(event.event);
    if (isCommerce) {
      for (const key of LayersCore.COMMERCE_KEYS) {
        if (Object.prototype.hasOwnProperty.call(event, key)) properties[key] = event[key];
      }
    }

    // `screen()` puts the screen name top-level (as Rust does, via `extra`),
    // but the hook contract has always presented it inside `properties`. Only
    // inject when the caller did not put a `screen_name` of their own there —
    // Rust never touches `properties`, so a caller key must survive.
    const injectScreenName =
      isScreen &&
      typeof event.screen_name === 'string' &&
      !Object.prototype.hasOwnProperty.call(properties, 'screen_name');
    if (injectScreenName) {
      properties.screen_name = event.screen_name;
    }
    const input: BeforeSendEvent = {
      event: event.event,
      properties,
      ...(typeof event.user_id === 'string' && { userId: event.user_id }),
      ...(typeof event.anonymous_id === 'string' && { anonymousId: event.anonymous_id })
    };

    let result: BeforeSendEvent | null | undefined;
    try {
      result = this._beforeSendHook(input);
    } catch (e) {
      if (this.enableDebug) console.warn('[Layers] before_send hook threw — dropping event:', e);
      this.recordDrop('before_send_errored');
      return false;
    }
    if (result === null) {
      this.recordDrop('before_send_returned_none');
      return false;
    }
    if (result === undefined) return true;
    if (!result.event || typeof result.event !== 'string') {
      if (this.enableDebug) {
        console.warn('[Layers] before_send hook returned invalid event — passing through');
      }
      return true;
    }

    event.event = result.event;
    const out: Record<string, unknown> = { ...(result.properties ?? {}) };
    if (injectScreenName) {
      if (typeof out.screen_name === 'string') event.screen_name = out.screen_name;
      // Back out of `properties` again — this engine injected it, it is a
      // top-level field on the wire, and leaving it would ship it twice.
      delete out.screen_name;
    }
    // Re-apply promotion to whatever the hook handed back: a key it kept goes
    // top-level again, a key it DELETED is removed from the event entirely.
    // Scrubbing a promoted key is the whole reason it was lent out.
    if (isCommerce) {
      for (const key of LayersCore.COMMERCE_KEYS) {
        if (Object.prototype.hasOwnProperty.call(out, key)) {
          event[key] = out[key];
          delete out[key];
        } else if (Object.prototype.hasOwnProperty.call(event, key)) {
          delete event[key];
        }
      }
    }
    if (Object.keys(out).length > 0) {
      event.properties = out as EventProperties;
    } else {
      // An empty object is a different payload from an absent one — the same
      // distinction commerce promotion makes.
      delete event.properties;
    }
    if (result.userId != null) {
      event.app_user_id = result.userId;
      event.user_id = result.userId;
    }
    if (result.anonymousId != null) {
      event.anonymous_id = result.anonymousId;
    }
    return true;
  }

  private analyticsAllowed(): boolean {
    // CMv2 takes precedence over the legacy alias when both are set.
    if (typeof this.consent.analyticsStorage === 'boolean') {
      return this.consent.analyticsStorage;
    }
    return this.consent.analytics !== false;
  }

  /**
   * JS-fallback enforcement of the server's events config: killswitch,
   * allowlist, denylist. The Rust core applies this authoritatively when WASM
   * is active; this mirror keeps server-driven controls working on the
   * fallback engine — e.g. React Native, where WebAssembly cannot be bundled.
   * Mirrors `RemoteConfigManager::is_event_allowed`.
   *
   * Split from {@link fallbackSampled} because the two run at DIFFERENT points
   * in the Rust pipeline: this one gates before the event is built or the rate
   * limiter is consulted (client.rs:758), while sampling runs after building
   * (client.rs:864-870). Merging them changed which events consume a
   * rate-limit slot.
   */
  private fallbackConfigAllowed(eventName: string): boolean {
    return this.fallbackConfigVerdict(eventName) === 'allowed';
  }

  /**
   * The same three checks, but SAYING WHICH ONE FIRED.
   *
   * The boolean form conflated a fleet-wide killswitch with a single denylisted
   * event name, and "is this app killswitched, or is this one event
   * denylisted?" is the first question an operator asks. Mirrors the
   * `EventVerdict` split in core/src/remote_config.rs; `fallbackConfigAllowed`
   * stays as the thin wrapper so existing call sites are untouched.
   */
  private fallbackConfigVerdict(
    eventName: string
  ): 'allowed' | 'killswitch' | 'not_allowlisted' | 'denylisted' {
    const ev = this._eventsConfig;
    if (!ev) return 'allowed';
    if (ev.killswitch === true) return 'killswitch';
    if (Array.isArray(ev.allowlist) && !ev.allowlist.includes(eventName)) return 'not_allowlisted';
    if (Array.isArray(ev.denylist) && ev.denylist.includes(eventName)) return 'denylisted';
    return 'allowed';
  }

  /**
   * Server-driven sampling for the fallback engine. Mirrors
   * `Sampler::should_sample`, except the draw is `Math.random()` rather than
   * the deterministic FNV-1a hash of the event id (statistically equivalent
   * for its purpose; the ids differ per event either way).
   */
  private fallbackSampled(eventName: string): boolean {
    const ev = this._eventsConfig;
    if (!ev) return true;
    const rate = ev.per_event_sampling?.[eventName] ?? ev.sampling_rate;
    if (typeof rate === 'number' && rate >= 0 && rate < 1 && Math.random() >= rate) {
      return false;
    }
    return true;
  }

  /**
   * Merge registered super-properties into a per-call property bag.
   *
   * Port of `merge_super_properties` (client.rs:2472-2494) on top of
   * `SuperPropertiesManager::merge_into` (super_properties.rs:163-171): the
   * CALLER'S keys always win, and the result is `undefined` when both sides
   * are empty so the `properties` key is omitted from the wire rather than
   * sent as `{}`.
   *
   * Returns a NEW object whenever anything is merged — the caller's object is
   * never mutated, because `track()` hands it straight onto the event and a
   * host app may reuse one props object across calls.
   */
  private mergeSuperProperties(properties?: EventProperties): EventProperties | undefined {
    const superKeys = Object.keys(this._superProperties);
    // Non-object properties bypass the merge (the caller's choice), matching
    // the `Some(other) => return Some(other)` arm in Rust.
    if (properties && (typeof properties !== 'object' || Array.isArray(properties))) {
      return properties;
    }
    if (superKeys.length === 0) {
      // Copied even when there is nothing to merge. Returning the caller's
      // object put it straight into the queue by reference, so a host app that
      // reused one props bag across calls saw its LATER mutations appear on
      // ALREADY-queued events. Registering a single super-property happened to
      // fix it (this branch stopped running), which is the worst shape for a
      // bug like this to have.
      return properties && Object.keys(properties).length > 0 ? { ...properties } : undefined;
    }

    const merged: EventProperties = { ...properties };
    for (const key of superKeys) {
      // `undefined` counts as absent rather than as a caller-supplied value:
      // a key whose value JSON.stringify erases must not shadow a registered
      // super-property, which would drop the key from the wire entirely.
      if (merged[key] === undefined) {
        merged[key] = this._superProperties[key];
      }
    }
    return Object.keys(merged).length > 0 ? merged : undefined;
  }

  /**
   * Is ad-storage (IDFA / click IDs) allowed? Default Allowed — only an
   * explicit denial gates it. Mirrors `ConsentState::advertising_allowed`
   * in core/src/consent.rs.
   */
  private advertisingAllowed(): boolean {
    // CMv2 takes precedence over the legacy alias when both are set.
    if (typeof this.consent.adStorage === 'boolean') {
      return this.consent.adStorage;
    }
    return this.consent.advertising !== false;
  }

  /**
   * Is forwarding user data to ad partners allowed? Default Allowed — only
   * an explicit denial gates it. Mirrors `ConsentState::ad_user_data_allowed`
   * in core/src/consent.rs — note the fallback is the legacy `advertising`
   * alias, NOT `adStorage`.
   */
  private adUserDataAllowed(): boolean {
    if (typeof this.consent.adUserData === 'boolean') {
      return this.consent.adUserData;
    }
    return this.consent.advertising !== false;
  }

  /**
   * Is personalized advertising allowed? Default Allowed — only an explicit
   * denial gates it. Mirrors `ConsentState::ad_personalization_allowed` in
   * core/src/consent.rs.
   */
  private adPersonalizationAllowed(): boolean {
    if (typeof this.consent.adPersonalization === 'boolean') {
      return this.consent.adPersonalization;
    }
    return this.consent.advertising !== false;
  }

  private getOrRotateSessionId(): string {
    if (this.wasm) {
      try {
        return this.wasm.getSessionId();
      } catch {
        // WASM failed — fall through to JS session
      }
    }

    // JS fallback: timeout-based rotation matching Rust SessionManager behavior
    const now = Date.now();
    if (now - this._sessionLastActivity > this._sessionTimeoutMs) {
      this.rotateSession();
    }
    this._sessionLastActivity = now;
    return this._sessionId;
  }

  /**
   * The one and only session boundary in this engine. Port of
   * `SessionManager::force_rotate_snapshot` (core/src/session.rs:123-134):
   * new id, activity timer reset, `session_number` incremented — as one step,
   * because they are one step. Rust also derives `is_first` from the freshly
   * incremented number; `buildEvent` does the same via `_sessionNumber === 1`,
   * so nothing here has to set it.
   *
   * Every caller goes through here on purpose. Rotating `_sessionId` on its
   * own is invisible in review and corrupting on the wire: the new session
   * ships under the PREVIOUS session's `session_number`, which merges two
   * sessions into one for retention, and a rotation out of session 1 keeps
   * claiming `is_first_session: true` for the rest of the install.
   *
   * Persisting immediately is what makes a within-launch rotation survive the
   * next launch — same read-modify-write as `persist_session_number` in
   * core/src/client.rs:2455-2470. The `!this.wasm` guard is not redundant with
   * the callers: `getOrRotateSessionId` reaches this line with WASM still live
   * when `wasm.getSessionId()` throws, and the Rust core owns its own
   * `identity_state` record there.
   */
  private rotateSession(): void {
    this._sessionId = generateUUID();
    this._sessionLastActivity = Date.now();
    this._sessionNumber = Math.max(this._sessionNumber + 1, 1);
    if (!this.wasm) this.persistSessionNumber(this._sessionNumber);
    // Session-scoped feature state resets with the session: survey dismissals
    // and event counts (surveys.rs:492-496). Flag exposure dedup is handled by
    // `refreshFlagSession`, which compares ids on the next read rather than
    // clearing here — a rotation triggered from inside `getOrRotateSessionId`
    // reaches this line before the new id is observable.
    this.surveyEngine.clearSessionState();
  }

  /**
   * Mint or resume the durable identity + session counter (JS fallback only).
   *
   * Port of the identity block of `LayersCore::init` in core/src/client.rs
   * (`client.rs:470-517`). Runs BEFORE {@link initFirstOpen} so the parked
   * `$first_open` is built with the same ids as every sibling event.
   *
   * Writes through the SAME `identity_state` record and the same serde field
   * names as the Rust core — `readIdentity`/`writeIdentity` do a
   * read-modify-write so this cooperates with the `$first_open` fields rather
   * than clobbering them.
   *
   * Skipped entirely when WASM is live: the Rust core mints its own ids there,
   * and minting a second set here would put two different `anonymous_id`s in
   * play for one install.
   */
  /**
   * Write the replaced core's identity into an EMPTY record, before
   * {@link initIdentity} can mint one of its own.
   *
   * Same precedence as {@link seedIdentityFrom}, which this runs instead of:
   * the incoming ids are taken only when this core's persistence held no
   * identity of its own. A store that already names an `anonymous_id` is
   * holding the install's real one — from a previous app run, which outranks
   * anything the replaced core minted into a store that started empty this
   * launch — so it is left alone and `initIdentity` resumes it.
   *
   * WHY THIS RUNS AT INIT AND NOT AFTER IT. `seedIdentityFrom` produces the
   * same in-memory state, but it is reached through `applyMigrationState`,
   * which the caller can only invoke once `init` has returned — by which point
   * `initIdentity` and `initFirstOpen` have already minted a throwaway
   * identity, a throwaway `first_open_time` and a throwaway
   * `first_open_event_id` and WRITTEN them. Correcting a persisted record is
   * only as reliable as the store's write ordering, and
   * `@react-native-async-storage/async-storage` v3 does not order writes at all
   * (one unsynchronised `launch {}` per call in `RNStorage.kt`, where v1/v2 ran
   * every operation through a `SerialExecutor`). When the mint's write
   * committed last, the install's durable record named an identity that had
   * never appeared on the wire; the next launch resumed it, and one install
   * became two `anonymous_id`s, two `device_id`s and two `$first_open`s.
   *
   * Seating first removes the second write rather than racing it: there is only
   * ever one identity in the record, so no commit order can produce a wrong
   * one. That is the hard ordering — a delay would only narrow the window.
   *
   * `first_open_emitted` is deliberately NOT seeded here. Whether this install
   * still owes a `$first_open` is settled by
   * {@link settleFirstOpenAfterMigration}, once migration has finished moving
   * events and it is knowable whether a copy survived; seeding the flag now
   * would retire the event for an install that never sent one.
   */
  private seedIdentityBeforeMint(identity: MigrationIdentity): void {
    if (this.wasm) return;
    if (!identity.anonymousId) return;

    const seeded = this.updateIdentity((record) => {
      if (typeof record.anonymous_id === 'string' && record.anonymous_id.length > 0) return false;

      record.anonymous_id = identity.anonymousId;
      if (identity.deviceId) record.device_id = identity.deviceId;
      if (identity.firstOpenTime) record.first_open_time = identity.firstOpenTime;
      // The id comes across even when the event does not: a destination that ends
      // up having to re-emit must do it under the ORIGINAL id so server-side
      // dedup absorbs the copy that did land. A confirmed delivery comes across
      // for the same reason in reverse.
      if (identity.firstOpenEventId) record.first_open_event_id = identity.firstOpenEventId;
      if (identity.firstOpenDelivered) record.first_open_delivered = true;
      if (identity.sessionNumber > 0) {
        record.last_session_number = identity.sessionNumber;
        this.seededSessionNumber = identity.sessionNumber;
      }
      return true;
    });
    if (seeded) {
      this.identitySeeded = true;
      // Same launch, same session: the number is claimed above, the id with it.
      if (identity.sessionId) this._sessionId = identity.sessionId;
    }
  }

  private initIdentity(): void {
    if (this.wasm) return;

    this.updateIdentity((identity) => {
      let dirty = false;

      if (typeof identity.device_id !== 'string' || identity.device_id.length === 0) {
        identity.device_id = generateUUID();
        dirty = true;
      }
      if (typeof identity.anonymous_id !== 'string' || identity.anonymous_id.length === 0) {
        identity.anonymous_id = generateUUID();
        this.identityMinted = true;
        dirty = true;
      }
      this.deviceId = identity.device_id as string;
      this.anonymousId = identity.anonymous_id as string;

      // Every launch starts a fresh session, so the next number is last+1.
      // `.max(1)` keeps a corrupt/absent counter from producing session 0, which
      // would make `is_first_session` false for a genuine first install
      // (`claim_session_number` in core/src/client.rs clamps the same way).
      //
      // The stored value is coerced exactly as `PersistedIdentity::from_slice`
      // coerces it. Both engines write this one record, so a value neither of
      // them expects — `"7"`, `7.5`, `-5`, `2^32` — must resolve to the SAME
      // number on both sides or the two writers disagree about which session
      // this launch is, on the same install, from the same bytes.
      // `Math.min` mirrors Rust's `saturating_add`: a counter already at the
      // u32 ceiling stays there rather than stepping past what the record can
      // hold, which would diverge from the other engine on the very next read.
      //
      // A seeded number is CLAIMED, not counted: the replaced core already
      // counted this launch against the same record, so stepping it again would
      // split one launch across two session numbers.
      const next =
        this.seededSessionNumber ??
        Math.min(Math.max(coerceSessionNumber(identity.last_session_number) + 1, 1), U32_MAX);
      if (identity.last_session_number !== next) {
        identity.last_session_number = next;
        dirty = true;
      }
      this._sessionNumber = next;

      return dirty;
    });
  }

  /**
   * Adopt the identity of the core this one is replacing.
   *
   * Two callers swap cores mid-launch: `@layers/client` on the web
   * fallback→WASM upgrade, and `@layers/react-native` when the AsyncStorage
   * preload resolves and the eager MemoryPersistence core is replaced. Both
   * cores run {@link initIdentity}, so without this the swap mints a SECOND
   * identity: events queued before it go out under one `anonymous_id` and
   * everything after under another, which server-side stitching cannot join.
   *
   * React Native reaches this through `LayersCore.init`'s `migration` option
   * rather than a follow-up call, which additionally runs
   * {@link seedIdentityBeforeMint} BEFORE the mint — see there for the
   * persisted-record race that the two-step form leaves open.
   *
   * Precedence is `identityMinted` (or the earlier `identitySeeded`): this core
   * adopts the incoming ids only when its own persistence held no record. A
   * destination that RESUMED a durable identity is holding the install's real
   * one — from a previous app run, which outranks anything the replaced core
   * minted into a store that started empty this launch — so it keeps it.
   *
   * `sessionNumber` is carried rather than recounted. Both cores called
   * `initIdentity`, so both counted this launch; leaving the destination's own
   * number would ship the pre-swap events as session N and the rest as N+1,
   * splitting one launch into two sessions and dropping `is_first_session` on
   * a genuine first install.
   *
   * On the WASM path the IDS need nothing: the Rust core mints its own and
   * exposes no setter (core/src/wasm_api.rs), but both cores read and write
   * the same `identity_state` record through the persistence bridge, so the
   * destination RESUMES the ids the replaced core persisted instead of minting
   * a second set.
   *
   * The SESSION NUMBER does need handing over, because both cores CLAIM one at
   * init. Left alone, one page load spans two numbers, the counter climbs by
   * two per navigation, and a genuine first visit loses `is_first_session` on
   * every event the Rust core builds — it comes up at 2. The hand-off is this
   * direct call, carrying a number the caller already holds; see
   * `adopt_session_number` in core/src/client.rs for why a marker left in the
   * shared record cannot do this safely.
   */
  private adoptMigratedIdentity(identity: MigrationIdentity): void {
    if (this.wasm) {
      if (identity.sessionNumber > 0) {
        try {
          this.wasm.adoptSessionNumber(identity.sessionNumber);
        } catch (e) {
          if (this.enableDebug) {
            console.warn('[Layers] WASM adoptSessionNumber failed:', e);
          }
        }
      }
      return;
    }
    // Captured before seedIdentityFrom clears the flag: whether this core took
    // the replaced core's identity decides whose events those were.
    //
    // `identitySeeded` is the same fact reached earlier — {@link
    // seedIdentityBeforeMint} already wrote those ids into an empty record, so
    // `initIdentity` RESUMED them and `identityMinted` is false. Reading only
    // the mint flag here would send a seeded swap down the reconcile branch
    // and, worse, drop `deliveredAsThisInstall`: a replaced core that flushed
    // its `$first_open` before the swap would go unrecorded and the install
    // would emit a second one.
    const adoptedItsIdentity = this.identityMinted || this.identitySeeded;
    // One hand-off per seat. A later applyMigrationState carries some THIRD
    // core's ids, which this core did not adopt.
    this.identitySeeded = false;
    if (this.identityMinted) {
      this.seedIdentityFrom(identity);
    } else {
      this.reconcileMigratedEvents(identity);
    }
    // Both branches change what is in the queue, so `$first_open` is settled
    // once here, after the dust clears, rather than guessed by each of them.
    this.settleFirstOpenAfterMigration(identity, adoptedItsIdentity);
  }

  /**
   * Whether a queued `$first_open` belongs to the install THIS core is running.
   *
   * The whole `$first_open` state machine turns on telling "this install
   * already has one" from "the core I am replacing built one" — and the only
   * honest record of that is the event itself. A migrated event carries the
   * replaced core's throwaway `anonymous_id`; one with no `anonymous_id` at
   * all predates identity minting, so it can only have come from an earlier
   * launch of this install. Nothing has to infer provenance from a flag.
   */
  /**
   * ACCEPTED RESIDUAL — this predicate is identity-scoped while
   * {@link reconcileWasmFirstOpen} is install-scoped, and the asymmetry is
   * deliberate.
   *
   * One reachable case pays for it. A pre-bridge install upgrades (its durable
   * `$first_open` carries the ephemeral `anonymous_id` a NullPersistence WASM
   * core minted and threw away) AND the host tracks between `new
   * LayersClient()` and `await init()`, which makes the fallback core BUILD
   * its parked copy before the swap. This predicate rejects the hydrated
   * stranger, the fallback builds its own, and that one page load puts TWO ids
   * on the wire. Pinned by `first-open-invariant.test.ts` → FRESH 4.
   *
   * It is bounded and self-correcting: the armed copy earns its receipt in the
   * same flush, so `first_open_delivered` goes true and no later launch
   * re-emits. One duplicate on one load of one cohort, once.
   *
   * Making this install-scoped too was tried and REFUTED by the existing
   * suite. `test/fallback.test.ts` → "still emits $first_open when a durable
   * install never finished emitting" and "pins a rewritten $first_open to the
   * install first session" both fail: on the migration path a queued copy's
   * fate is still undecided — {@link reconcileMigratedEvents} may DROP it as a
   * duplicate — so treating a foreign one as proof records an emission for an
   * event that is then discarded. That trade is strictly worse: it loses the
   * event on a path that is reached far more often than this one.
   *
   * {@link reconcileWasmFirstOpen} has no such hazard, which is exactly why it
   * is blind: nothing drops the copy it inspects.
   */
  private ownsQueuedFirstOpen(event: QueuedEvent): boolean {
    return (
      event.event === FIRST_OPEN_EVENT &&
      (event.anonymous_id == null || event.anonymous_id === this.anonymousId)
    );
  }

  /**
   * Settle `$first_open` after a core swap — the single place that decides
   * whether this install still owes one.
   *
   * The invariant, on the wire: exactly one `$first_open` per install is
   * delivered, and it is never dropped without having been sent. Holding that
   * requires knowing whether a copy EXISTS, which is only true once migration
   * has finished moving events around — so neither {@link initFirstOpen} (runs
   * before the swap) nor either migration branch (each sees half the picture)
   * can decide it alone.
   *
   * A queued copy settles it outright — reconcile has already rewritten any
   * keeper onto this identity, so whatever is in the queue is ours.
   *
   * Failing that, the replaced core having DELIVERED one settles it, but ONLY
   * when this core took that core's identity. The replaced core can flush
   * before the swap completes (a 30s tick, or the 20-event threshold), and on
   * the reconcile path its ids were discarded — so that delivery was some
   * other identity's `$first_open`, and crediting it to this install leaves
   * the install having never sent one under its real ids.
   *
   * Otherwise the install is still owed one and the parked copy stays armed:
   * `firstOpenEmitted` on its own only says the event was BUILT, and trusting
   * it when no copy survived the migration retires `$first_open` forever with
   * nothing ever sent.
   *
   * Whatever it decides, the `event_id` is written with the flag. Settling
   * with the flag alone left a brand-new install holding "BUILT, no id" — the
   * one shape {@link shouldReArmFirstOpen} must take at its word — so the
   * install lost `$first_open` for good the moment the migrated copy died
   * before delivery, which is the ordinary outcome of a first launch that
   * ends in a crash or an OS kill.
   */
  private settleFirstOpenAfterMigration(
    identity: MigrationIdentity,
    adoptedItsIdentity: boolean
  ): void {
    const queued = this.queue.some((e) => e.event === FIRST_OPEN_EVENT);
    const deliveredAsThisInstall =
      adoptedItsIdentity && identity.firstOpenEmitted && !identity.firstOpenQueued;
    if (!queued && !deliveredAsThisInstall) return;

    // Prefer the surviving copy's id to the exported one: that event is the
    // one that will actually go out, so its id cannot drift from what a later
    // re-arm has to match. The exported id is reached only when nothing is
    // queued, which by the guard above means the replaced core delivered
    // under ids this core adopted — so it is this install's id either way.
    const eventId = this.queuedFirstOpenEventId() ?? identity.firstOpenEventId;

    this.updateIdentity((record) => {
      let dirty = false;
      if (record.first_open_emitted !== true) {
        record.first_open_emitted = true;
        dirty = true;
      }
      if (eventId !== null && eventId.length > 0 && record.first_open_event_id !== eventId) {
        record.first_open_event_id = eventId;
        dirty = true;
      }
      return dirty;
    });
    this.pendingFirstOpen = null;
  }

  /**
   * Destination had no identity of its own — take the replaced core's whole
   * record, ids and `$first_open` state alike.
   */
  private seedIdentityFrom(identity: MigrationIdentity): void {
    this.updateIdentity((record) => {
      if (identity.anonymousId) {
        this.anonymousId = identity.anonymousId;
        record.anonymous_id = identity.anonymousId;
      }
      if (identity.deviceId) {
        this.deviceId = identity.deviceId;
        record.device_id = identity.deviceId;
      }
      if (identity.sessionNumber > 0) {
        this._sessionNumber = identity.sessionNumber;
        record.last_session_number = identity.sessionNumber;
      }
      if (identity.sessionId) this._sessionId = identity.sessionId;
      if (identity.firstOpenTime) {
        record.first_open_time = identity.firstOpenTime;
        this.firstOpenTime = identity.firstOpenTime;
        if (this.pendingFirstOpen) {
          this.pendingFirstOpen = {
            ...this.pendingFirstOpen,
            first_open_time: identity.firstOpenTime
          };
        }
      }
      // The `$first_open` id comes across even when the event itself does not:
      // a destination that ends up having to re-emit must do it under the
      // ORIGINAL id so server-side dedup absorbs the copy that did land. A
      // confirmed delivery comes across for the same reason in reverse — this
      // core just adopted the ids that delivery went out under, so the receipt
      // is this install's and re-emitting against it would be pointless work.
      if (identity.firstOpenEventId) {
        record.first_open_event_id = identity.firstOpenEventId;
      }
      if (identity.firstOpenDelivered) {
        record.first_open_delivered = true;
      }
      // `first_open_emitted` itself is settled by settleFirstOpenAfterMigration
      // once both branches have finished moving events.
    });
    // The install now has a durable identity, so a second applyMigrationState
    // call must not overwrite it with a third core's ephemeral one.
    this.identityMinted = false;
  }

  /**
   * Destination kept its own durable identity, so the events the replaced core
   * handed over are stamped with ids that no longer mean anything.
   *
   * The replaced core always starts on an empty store (RN's eager core holds a
   * fresh MemoryPersistence every launch), so it always mints throwaway ids
   * and always looks like a first install. On a returning install its queued
   * events therefore carry an `anonymous_id` seen exactly once in the entire
   * dataset, a `session_number` of 1, and a `$first_open` this install already
   * emitted launches ago.
   *
   * Matching on the replaced core's `anonymous_id` is what keeps this
   * surgical: a queue hydrated from THIS install's earlier launches can hold
   * events under a legitimately different identity (a pre-`reset()` one), and
   * rewriting those would join a logged-out user's events back to the current
   * one.
   */
  private reconcileMigratedEvents(identity: MigrationIdentity): void {
    const staleId = identity.anonymousId;
    if (!staleId || staleId === this.anonymousId) return;

    // BUILT or DELIVERED — see {@link firstOpenAccountedFor}. This runs on a
    // fresh launch against a record AsyncStorage handed back, so the two flags
    // can arrive split; gating only on the build flag kept the replaced core's
    // duplicate `$first_open` and rewrote it onto this identity.
    const alreadyEmitted = firstOpenAccountedFor(this.readIdentity());
    this.queue = this.queue.filter((event) => {
      if (event.anonymous_id !== staleId) return true;
      if (event.event === FIRST_OPEN_EVENT && alreadyEmitted) return false;
      if (this.anonymousId) event.anonymous_id = this.anonymousId;
      if (this.deviceId) event.device_id = this.deviceId;
      if (this.firstOpenTime) event.first_open_time = this.firstOpenTime;
      event.session_id = this.getOrRotateSessionId();
      // `session_number` / `is_first_session` are install-lifecycle fields, not
      // identity ones. On `$first_open` they are deliberately pinned to the
      // install's FIRST session by emitPendingFirstOpen (and by Rust's
      // `patch_newest_named`) no matter when the event actually ships, so
      // restamping them from the resumed session would report a returning
      // install's first open as session N / not-first and corrupt retention
      // and first-open cohorts. Every other event belongs to the launch that
      // is delivering it, so the triple moves together there — a rewritten
      // number under the replaced core's session_id would put two ids on one
      // number.
      if (event.event !== FIRST_OPEN_EVENT) {
        event.session_number = this._sessionNumber;
        event.is_first_session = this._sessionNumber === 1;
      }
      return true;
    });
  }

  /**
   * Best-effort persist of the latest `session_number` after a within-launch
   * rotation. Read-modify-write so a concurrent `$first_open` flag update is
   * not lost. Mirrors `persist_session_number` in core/src/client.rs.
   */
  private persistSessionNumber(sessionNumber: number): void {
    this.updateIdentity((identity) => {
      if (identity.last_session_number === sessionNumber) return false;
      identity.last_session_number = sessionNumber;
      return true;
    });
  }

  // --- `$first_open` (JS fallback only) ---
  //
  // Port of the `$first_open` block in core/src/client.rs (see `init`,
  // `emit_pending_first_open`, `reset`). The Rust core owns this event on
  // every WASM/native platform; the JS fallback — which is the PRODUCTION
  // engine on React Native, and the failure-mode engine on the web — simply
  // never implemented it, so `$first_open` was missing from every RN install
  // (verified on-device: an Expo release build delivered app_install and
  // app_open but no `$first_open`).
  //
  // Each rule below exists because a simpler version shipped a bug; the Rust
  // file carries the same annotations.

  /**
   * Decide, at init, whether this install still owes a `$first_open`.
   *
   * Called from `init()` AFTER queue hydration and only on the fallback path:
   * when WASM is live the Rust core runs this logic itself, and doing it here
   * too would double-emit.
   *
   * The gate is the PERSISTED `first_open_emitted` flag, never a "was this the
   * first launch" inference: init persists `first_open_time` on the very first
   * run, so a process that dies before emission would look like a returning
   * install on its next start and lose `$first_open` permanently.
   *
   * Nothing is BUILT here. At init no wrapper has called `setDeviceContext`
   * yet, so an event built now is stamped with the pre-context default
   * platform ('web') — Android installs reported as web. The props are parked
   * and the event is built by {@link emitPendingFirstOpen} when device context
   * arrives, or at the first track/screen if the wrapper never sets it.
   */
  private initFirstOpen(): void {
    if (this.wasm) return;

    const identity = this.readIdentity();

    let firstOpenTime =
      typeof identity.first_open_time === 'string' && identity.first_open_time.length > 0
        ? identity.first_open_time
        : null;
    if (firstOpenTime === null) {
      firstOpenTime = new Date().toISOString();
      identity.first_open_time = firstOpenTime;
      this.updateIdentity((record) => {
        record.first_open_time = firstOpenTime;
      });
    }
    this.firstOpenTime = firstOpenTime;

    if (firstOpenAccountedFor(identity)) {
      // The flag says BUILT, which is not the same as SENT. If neither a
      // durable copy nor a delivery receipt survived, the install is still
      // owed one.
      if (this.shouldReArmFirstOpen(identity)) {
        this.pendingFirstOpen = { first_open_time: firstOpenTime };
      }
      return;
    }

    // A prior run may have QUEUED `$first_open` (just hydrated above from the
    // crash-safety snapshot) and then died before its `first_open_emitted`
    // write landed. The hydrated copy IS the event — parking another would
    // emit a duplicate carrying a fresh event_id that server-side dedup cannot
    // absorb. Repair the flag instead.
    //
    // Only THIS install's copy is proof. A `$first_open` migrated from the
    // core being replaced is queued by the time this runs, but its fate is
    // still undecided — the reconcile pass may drop it as a duplicate — so
    // letting it flip the flag here recorded an emission for an event that
    // was then discarded, losing `$first_open` for the life of the install.
    if (this.queue.some((e) => this.ownsQueuedFirstOpen(e))) {
      identity.first_open_emitted = true;
      // The id travels with the flag, always. Repairing the flag alone left
      // the record saying BUILT with nothing to re-emit under, so the event
      // was still lost for good if the hydrated copy died later — a FIFO
      // eviction on a full queue takes the oldest event, and `$first_open` is
      // the oldest event this install has.
      const repairedId = this.queuedFirstOpenEventId();
      if (repairedId !== null) identity.first_open_event_id = repairedId;
      this.updateIdentity((record) => {
        record.first_open_emitted = true;
        if (repairedId !== null) record.first_open_event_id = repairedId;
      });
      return;
    }

    // Nothing built yet: ARM the install, minting the id NOW rather than at
    // build time. Verbatim port of `settle_first_open_at_init` in
    // core/src/client.rs — both engines write this record, so an install must
    // come up armed the same way whichever one runs.
    //
    // WHY MINT AT PARK. `first_open_event_id` is what makes a re-emission
    // safe, and {@link shouldReArmFirstOpen} refuses to re-arm without one:
    // a record carrying the flag with no id is what a PRE-UPGRADE SDK wrote,
    // and re-arming those would ship a fresh id across the whole installed
    // base that dedup could not absorb.
    //
    // Minting only on BUILD left a window where the record was armed but
    // idless, and two ordinary things wrote the flag into it — `reset()` (a
    // logout before the first track; the flag is install-scoped so reset sets
    // it unconditionally) and a server killswitch suppressing the build. Both
    // produced flag-without-id, indistinguishable from the legacy shape, so
    // the install was retired having never sent one.
    //
    // An id already in the record is REUSED, never replaced: a second core
    // over the same store (another tab, the fallback→WASM swap) must arm under
    // the same id, or the two put two ids on the wire.
    if (persistedFirstOpenEventId(identity) === null) {
      const minted = generateUUID();
      identity.first_open_event_id = minted;
      this.updateIdentity((record) => {
        // Re-checked against the FRESH record, not the snapshot: another core
        // over the same store may have armed in between, and two ids on one
        // install is the failure this whole mechanism exists to avoid.
        const existing = persistedFirstOpenEventId(record);
        if (existing !== null) {
          identity.first_open_event_id = existing;
          return false;
        }
        record.first_open_event_id = minted;
        return true;
      });
    }
    this.pendingFirstOpen = { first_open_time: firstOpenTime };
  }

  /**
   * Whether an install whose record says `$first_open` was emitted is
   * nonetheless still owed one.
   *
   * Invariant extension: `first_open_emitted` records only that the event was
   * BUILT, so an install still owes a `$first_open` whenever neither a queued
   * copy nor a delivery receipt survives — and the re-emission reuses the
   * original `event_id` so that a copy which did reach the server is absorbed
   * by dedup rather than duplicated.
   *
   * This closes the plain-relaunch half of the same built/queued/delivered
   * conflation the migration path had: the flag was persisted the moment
   * `track()` accepted the event, so a hard crash before the crash-safety
   * snapshot reached storage — or a snapshot that never landed while the
   * small identity write did — retired `$first_open` with nothing ever sent.
   *
   * A record carrying the flag with NO id is taken at its word. There is
   * nothing to re-emit under, so re-arming would ship a fresh `event_id` that
   * server-side dedup cannot absorb — a duplicate `$first_open` across the
   * entire installed base on the release that ships this.
   *
   * That is now a narrow class, and deliberately so: every writer that marks
   * an event as BUILT records the id in the same write (see
   * {@link PersistedIdentity.first_open_event_id}), including the migration
   * settle, which is where a brand-new install used to land in exactly this
   * shape. What is left is a record written before the field existed, or a
   * `reset()` that retired a `$first_open` the install never built — and
   * neither has an event to re-send.
   */
  private shouldReArmFirstOpen(identity: PersistedIdentity): boolean {
    if (persistedFirstOpenEventId(identity) === null) return false;
    if (identity.first_open_delivered === true) return false;
    return !this.queue.some((e) => this.ownsQueuedFirstOpen(e));
  }

  /**
   * Tell the Rust core about a `$first_open` recovered into the JS queue.
   *
   * The persistence bridge refuses the `events_` prefix — queue durability on
   * the WASM path stays with this file, which hydrates and chunks those keys
   * itself — so a `$first_open` restored from a previous page load lands HERE,
   * in a queue the Rust core cannot see. Its own init then reaches one of two
   * wrong conclusions: park a SECOND `$first_open` under a fresh `event_id`
   * (its record said nothing was built) or re-arm one that is already sitting
   * in this queue waiting to go out. The first is the worse of the two —
   * server-side dedup keys on `event_id`, so two different ids are two
   * `$first_open`s in the warehouse.
   *
   * One call after hydration settles it: the Rust core stands down and records
   * the recovered copy's id, so any later re-arm re-emits under the id that is
   * actually on the wire.
   *
   * No-op on the fallback engine — `initFirstOpen` reads this queue directly.
   */
  private reconcileWasmFirstOpen(): void {
    if (!this.wasm) return;
    try {
      // Detection here is INSTALL-scoped, deliberately blind to which
      // `anonymous_id` the recovered copy carries.
      //
      // `$first_open` is install-scoped, not identity-scoped — `reset()` sets
      // the flag and preserves `first_open_time` for exactly that reason, so a
      // logout does not make a long-standing install look new. A copy in this
      // queue under some other `anonymous_id` is therefore still THIS
      // install's `$first_open`: it came from a pre-`reset()` identity, or
      // from the ephemeral one a pre-bridge WASM core minted under
      // NullPersistence and threw away at unload.
      //
      // Filtering those out is what put TWO `$first_open`s on the wire under
      // DIFFERENT ids for every install upgrading from a pre-bridge release —
      // the copy was refused as foreign, the core parked its own, and
      // server-side dedup keys on `event_id` so both landed. The whole
      // upgrade cohort, which is the cohort this bridge exists for.
      //
      // {@link ownsQueuedFirstOpen} keeps its ownership test on the fallback
      // path, where the question is different: there a migrated copy may still
      // be DROPPED by {@link reconcileMigratedEvents}, so a foreign one is not
      // yet proof of anything. Nothing drops the copy here.
      let recovered: string | null = null;
      for (let i = this.queue.length - 1; i >= 0; i--) {
        const queued = this.queue[i]!;
        if (queued.event !== FIRST_OPEN_EVENT) continue;
        const eventId = queued.event_id;
        if (typeof eventId === 'string' && eventId.length > 0) {
          recovered = eventId;
          break;
        }
      }
      this.wasm.reconcileFirstOpen(recovered);
    } catch (e) {
      if (this.enableDebug) {
        console.warn('[Layers] WASM reconcileFirstOpen failed:', e);
      }
    }
  }

  /**
   * `event_id` of the newest queued `$first_open` this core owns, or null.
   *
   * The queued event is the authoritative copy of the id: it is the one that
   * will be delivered, so recording anything else would leave a re-arm
   * re-emitting under an id the server never saw — defeating the dedup this
   * whole mechanism runs on.
   */
  private queuedFirstOpenEventId(): string | null {
    for (let i = this.queue.length - 1; i >= 0; i--) {
      const queued = this.queue[i]!;
      if (!this.ownsQueuedFirstOpen(queued)) continue;
      const eventId = queued.event_id;
      if (typeof eventId === 'string' && eventId.length > 0) return eventId;
    }
    return null;
  }

  /**
   * Record the receipt once a batch containing `$first_open` was accepted by
   * the server — the only thing that stops the re-arm firing forever.
   *
   * Runs on BOTH engines, for events delivered out of THIS queue. On the WASM
   * path that queue holds only what hydration recovered from a previous page
   * load: the persistence bridge refuses the `events_` prefix, so those copies
   * never pass through the Rust core's `drain`, and `recordFlushResult` — the
   * receipt route for drained batches — never sees them. Without this an
   * install whose `$first_open` came back from the snapshot and shipped from
   * here would re-emit it on every launch for the rest of its life.
   */
  private confirmFirstOpenDelivered(delivered: QueuedEvent[]): void {
    // EVERY `$first_open` in the batch, not the first one found. A hydrated
    // backlog can hold a copy from an earlier identity, and this install's
    // armed copy rides out alongside it in the same batch. Taking the first
    // meant the ownership guard rejected the stranger and returned — while
    // the armed copy had just been delivered and earned nothing, leaving the
    // install re-emitting on every launch for the rest of its life.
    const deliveredIds = delivered
      .filter((e) => e.event === FIRST_OPEN_EVENT)
      .map((e) => e.event_id)
      .filter((id): id is string => typeof id === 'string' && id.length > 0);
    if (deliveredIds.length === 0) return;

    if (this.wasm) {
      // The Rust core rules on ownership, but it is handed ONE id — so pick
      // the armed one when the shared record names it. Both engines read the
      // same `identity_state` through the persistence bridge, which is what
      // makes this side able to tell.
      const armed = persistedFirstOpenEventId(this.readIdentity());
      const chosen = armed !== null && deliveredIds.includes(armed) ? armed : deliveredIds[0]!;
      try {
        this.wasm.confirmFirstOpenDelivered(chosen);
      } catch (e) {
        if (this.enableDebug) {
          console.warn('[Layers] WASM confirmFirstOpenDelivered failed:', e);
        }
      }
      return;
    }
    this.updateIdentity((identity) => {
      if (identity.first_open_delivered === true) return false;
      // OWNERSHIP. A receipt is only this install's if it is for the event this
      // install is armed under. Confirming any delivered `$first_open` let a
      // copy belonging to some other identity retire this install's own —
      // `first_open_delivered` against a foreign id disables the re-arm
      // permanently, so the event still owed is never sent. Mirrors the same
      // guard in `confirm_first_open_delivered` (core/src/client.rs).
      const armed = persistedFirstOpenEventId(identity);
      if (armed !== null && !deliveredIds.includes(armed)) {
        if (this.enableDebug) {
          console.warn(
            '[Layers] ignoring a $first_open receipt: the batch carried no copy of the armed event'
          );
        }
        return false;
      }
      // A record with no id is the pre-upgrade shape: no claim to contradict, so
      // a delivered copy names it.
      if (armed === null) {
        identity.first_open_emitted = true;
        identity.first_open_event_id = deliveredIds[0]!;
      }
      identity.first_open_delivered = true;
      return true;
    });
  }

  /**
   * Build and queue the parked `$first_open`, if one is owed.
   *
   * Taking the props out of the field BEFORE calling `track()` makes the
   * re-entrant call (track → emitPendingFirstOpen) a no-op.
   *
   * The install is marked emitted ONLY when the event was actually ACCEPTED.
   * `track()` returns normally for an event that was silently suppressed —
   * remote-config denylist/killswitch, sampling, a `before_send` hook
   * returning null — and marking those as emitted would lose the event
   * forever, so suppression simply retries on the next launch.
   *
   * Acceptance is counted, never measured by queue depth. `enqueue` evicts the
   * head of a FULL queue before pushing, which leaves the length unchanged: a
   * depth comparison read that as "suppressed", left `first_open_emitted`
   * false while the event shipped anyway, and the next launch emitted a SECOND
   * `$first_open` with a fresh `event_id` that server-side dedup cannot
   * absorb. Reachable on any install that hydrates a full queue before the
   * first open has been emitted.
   */
  private emitPendingFirstOpen(): void {
    const props = this.pendingFirstOpen;
    if (props === null) return;
    this.pendingFirstOpen = null;

    const acceptedBefore = this._enqueuedCount;
    try {
      this.track(FIRST_OPEN_EVENT, props);
    } catch (e) {
      // `track()` no longer throws for delivery-side conditions, but a
      // `before_send` hook or property validation still can. The flag stays
      // false, so the next launch re-parks and retries.
      if (this.enableDebug) console.warn(`[Layers] Failed to emit ${FIRST_OPEN_EVENT}:`, e);
      return;
    }
    if (this._enqueuedCount === acceptedBefore) {
      if (this.enableDebug) {
        console.log(`[Layers] ${FIRST_OPEN_EVENT} suppressed; will retry on next launch`);
      }
      return;
    }

    // OVERRIDE the install-session fields. `buildEvent` already stamps
    // session_number / is_first_session on every event from the CURRENT
    // session, but `$first_open` semantically belongs to the install's FIRST
    // session — a deferred emission after a 30-minute idle rotation, or a
    // re-park on a later launch, would otherwise ship `session_number: 2+` /
    // `is_first_session: false` on a genuine first install. The Rust core
    // keeps the identical override on top of its own general stamping
    // (core/src/client.rs:695-698, `patch_newest_named`), so this is parity,
    // not a workaround for the general stamping being absent.
    // This read is for `priorEventId` ONLY, and it must happen BEFORE the patch
    // loop — a re-arm has to re-emit under the id the original copy carried,
    // which only this record still knows. The SNAPSHOT is deliberately not
    // carried to the write below: `doFlush()` writes this same record (it
    // persists the health counters), so writing a snapshot taken before it
    // discarded those counters entirely. See {@link updateIdentity}.
    const priorEventId = persistedFirstOpenEventId(this.readIdentity());

    let eventId: string | null = null;
    for (let i = this.queue.length - 1; i >= 0; i--) {
      const queued = this.queue[i]!;
      if (queued.event === FIRST_OPEN_EVENT) {
        queued.session_number = 1;
        queued.is_first_session = true;
        if (priorEventId !== null) queued.event_id = priorEventId;
        eventId = queued.event_id;
        break;
      }
    }

    // Snapshot BEFORE flipping the flag, not after: a crash in between must
    // leave a REPAIRABLE state (flag false + a durable copy of the event,
    // which the next init's crash-repair branch reconciles). Flipping first
    // and dying would record "emitted" for an event that only ever existed in
    // memory — which is now recoverable rather than terminal, because the id
    // below lets the next launch re-emit the very same event.
    this.doFlush();

    // Fresh read, AFTER `doFlush()`, so the counters it just persisted survive.
    this.updateIdentity((identity) => {
      identity.first_open_emitted = true;
      if (eventId !== null) identity.first_open_event_id = eventId;
    });
  }

  /** Read the persisted identity record; `{}` when absent or unparseable. */
  private readIdentity(): PersistedIdentity {
    try {
      const raw = this.persistence.read(IDENTITY_PERSISTENCE_KEY);
      if (!raw || raw.length === 0) return {};
      const parsed: unknown = JSON.parse(new TextDecoder().decode(raw));
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as PersistedIdentity;
      }
      return {};
    } catch {
      // Persistence unavailable or corrupted — treat as a fresh record. Worst
      // case `$first_open` is re-attempted on a later launch.
      return {};
    }
  }

  /**
   * Read the identity record, apply `mutator`, and write it back — as ONE step.
   *
   * THE ONLY SANCTIONED WAY TO CHANGE THIS RECORD, and the reason is a bug this
   * replaced. Several callers did `readIdentity()` → mutate → `writeIdentity()`
   * with other work in between, and one of them — `emitPendingFirstOpen` —
   * called `doFlush()` in that gap. `doFlush()` persists the health counters
   * through this same record, so the inner write landed and the outer write then
   * overwrote it with a snapshot taken before it. Measured: in-memory
   * `accepted` 2, persisted 0.
   *
   * No concurrency is involved, and JS being single-threaded does not help at
   * all — the inner write simply completes and is then clobbered. It was worse
   * than an ordinary lost update because `persistHealthCounters` clears its
   * dirty flag on that inner write, so the in-memory counters believed they
   * were durable and never rewrote: the loss was permanent, not transient. A
   * restart then resumed rolled-back counters against a queue that had been
   * persisted separately and was current, so depth exceeded `accepted` and the
   * reconciler computed a NEGATIVE residual — which the server classifies as
   * corruption rather than as loss.
   *
   * Holding the read and the write together removes the shape rather than the
   * instance. Mirrors `update_identity` in core/src/client.rs.
   *
   * `mutator` returns `false` to skip the write entirely (for callers that only
   * write when something actually changed); `true` or nothing writes. Returns
   * whether a write was attempted.
   */
  private updateIdentity(mutator: (identity: PersistedIdentity) => boolean | void): boolean {
    const identity = this.readIdentity();
    if (mutator(identity) === false) return false;
    this.writeIdentityRecord(identity);
    return true;
  }

  /**
   * Persist the identity record (best-effort, mirrors the Rust core).
   *
   * PRIVATE TO {@link updateIdentity}. Nothing else may call it: a caller
   * holding a snapshot read earlier cannot know whether anything wrote to the
   * record in between, and writing that snapshot back silently discards
   * whatever did. See {@link updateIdentity} for the measured instance.
   */
  private writeIdentityRecord(identity: PersistedIdentity): void {
    // Re-stamped from memory on EVERY write — see healthForWrite().
    identity.health = this.healthForWrite(identity.health);
    try {
      this.persistence.write(
        IDENTITY_PERSISTENCE_KEY,
        new TextEncoder().encode(JSON.stringify(identity))
      );
    } catch {
      // Best-effort: an unwritable flag means at worst a duplicate
      // `$first_open` next launch, which server-side dedup cannot absorb but
      // which is still better than failing init.
      //
      // Counted, but NOT via `recordDrop` — this method is on the persist path
      // for the counters themselves, and a `recordDrop` here would re-dirty
      // the record it just failed to write, every time.
      this._health.persistWriteFailures += 1;
    }
  }

  // --- JS fallback event building (only used when WASM is NOT loaded) ---

  /**
   * Commerce events whose known keys are promoted out of `properties` and onto
   * the event as top-level fields. Verbatim port of `COMMERCE_EVENTS` /
   * `COMMERCE_KEYS` in core/src/client.rs:796-853 — the ingest service reads
   * revenue/LTV from top-level columns, so leaving them nested (as the
   * fallback did) made RN revenue reporting silently empty.
   *
   * The Rust core serializes these through `#[serde(flatten)]` on `extra`;
   * here they are assigned directly onto the event object, which produces the
   * same JSON.
   */
  private static readonly COMMERCE_EVENTS: ReadonlySet<string> = new Set([
    'purchase_success',
    'purchase_attempt',
    'purchase_fail',
    'subscribe',
    'refund',
    'add_to_cart',
    'remove_from_cart',
    'begin_checkout',
    'view_item'
  ]);

  private static readonly COMMERCE_KEYS: readonly string[] = [
    'product_id',
    'price',
    'revenue',
    'currency',
    'quantity',
    'transaction_id',
    'store',
    'period',
    'is_renewal',
    'is_trial',
    'subscription_group_id',
    'purchase_token',
    'is_auto_renewing',
    'order_id',
    'subtotal',
    'total',
    'item_count',
    'product_ids',
    'product_name',
    'category',
    'value',
    'amount',
    'reason',
    'error_code',
    'error_message',
    'receipt_data'
  ];

  /**
   * Move commerce keys from `event.properties` up to the top level. No-op for
   * non-commerce events. When every property was promoted the `properties` key
   * is dropped entirely rather than left as `{}` — that is what the Rust core
   * does (`event.properties = None`), and an empty object on the wire is a
   * different payload from an absent one.
   *
   * Promotion happens into a COPY. `track()` assigns the caller's properties
   * object onto the event by reference, so deleting the promoted keys in place
   * would strip them from an object the host app still owns — a caller that
   * reuses one props object across two `track()` calls would silently send the
   * second one without its commerce fields. The Rust core cannot hit this: it
   * receives a serialized clone across the FFI boundary.
   */
  private promoteCommerceKeys(eventName: string, event: QueuedEvent): void {
    if (!LayersCore.COMMERCE_EVENTS.has(eventName)) return;
    const props = event.properties;
    if (!props || typeof props !== 'object' || Array.isArray(props)) return;

    const remaining: EventProperties = { ...props };
    for (const key of LayersCore.COMMERCE_KEYS) {
      if (Object.prototype.hasOwnProperty.call(remaining, key)) {
        event[key] = remaining[key];
        delete remaining[key];
      }
    }
    if (Object.keys(remaining).length === 0) {
      delete event.properties;
    } else {
      event.properties = remaining;
    }
  }

  private buildEvent(eventName: string): QueuedEvent {
    const ctx = this.deviceContext;
    const sessionId = this.getOrRotateSessionId();

    const event: QueuedEvent = {
      event: eventName,
      timestamp: new Date().toISOString(),
      event_id: generateUUID(),
      app_id: this.appId,
      environment: this.environment,
      platform: ctx.platform ?? 'web',
      os_version: ctx.osVersion ?? 'unknown',
      app_version: ctx.appVersion ?? 'unknown',
      device_model: ctx.deviceModel ?? 'unknown',
      // `en_US`, not `en-US`: the Rust core's default (client.rs:2366) uses an
      // underscore, and both engines write into the same `sdk_events.locale`
      // column — the two spellings split every GROUP BY on locale.
      locale: ctx.locale ?? 'en_US'
    };

    // Session identity as an atomic triple, exactly as core/src/client.rs:
    // 2372-2378 stamps it. `getOrRotateSessionId()` performs any rotation
    // (and the counter bump) first, so the number read here always belongs to
    // the id above.
    event.session_id = sessionId;
    event.session_number = this._sessionNumber;
    event.is_first_session = this._sessionNumber === 1;

    if (this.userId) {
      event.app_user_id = this.userId;
      event.user_id = this.userId;
    }

    // Always emit anonymous_id and device_id for identity stitching
    // (client.rs:2387-2393). Per-call overrides in track()/screen() may
    // replace anonymous_id afterwards.
    if (this.anonymousId) event.anonymous_id = this.anonymousId;
    if (this.deviceId) event.device_id = this.deviceId;

    // First-open timestamp is set once per install (client.rs:2396-2398).
    if (this.firstOpenTime) event.first_open_time = this.firstOpenTime;

    // Group membership (client.rs:2400-2410). Both keys are top-level, and an
    // EMPTY membership map omits `$groups` entirely rather than sending `{}` —
    // an empty object is a different payload from an absent one.
    if (this._groupId) event.group_id = this._groupId;
    if (Object.keys(this._groups).length > 0) event.$groups = { ...this._groups };

    if (ctx.buildNumber) event.build_number = ctx.buildNumber;
    if (ctx.screenSize) event.screen_size = ctx.screenSize;
    if (ctx.installId) event.install_id = ctx.installId;

    // IDFV always sent (first-party identifier)
    if (ctx.idfv) event.idfv = ctx.idfv;

    // IDFA and ATT status are stamped at delivery by routeForDelivery.

    if (ctx.timezone) event.timezone = ctx.timezone;

    // Tier 9 (Consent Mode v2): tag the event with metadata flags so the
    // ingest service / downstream forwarders can tell which ad-related
    // forwarding is permitted. A flag is emitted only when the category is
    // explicitly denied; an app that never calls setConsent() ships neither
    // flag, and the server treats an absent flag as "unknown" and forwards.
    // The server cannot tell an unset category from a user's denial — both
    // would arrive as `false` — so stamping the flag on unset consent is a
    // fleet-wide CAPI outage, and from v3.2.0 to v3.2.12 it was one.
    // Mirrors the same block in core/src/client.rs (`build_event`).
    if (!this.adUserDataAllowed()) event.$ad_user_data = false;
    if (!this.adPersonalizationAllowed()) event.$ad_personalization = false;

    return event;
  }

  /**
   * Monotonic count of events this engine has ACCEPTED into the queue.
   *
   * Queue length cannot answer "was that event accepted?": `enqueue` evicts the
   * head when the queue is full, so a successful push leaves the length
   * unchanged. Anything comparing depth before/after therefore reads a full
   * queue as a rejection.
   */
  private _enqueuedCount = 0;

  private enqueue(event: QueuedEvent): void {
    if (this.queue.length >= this.maxQueueSize) {
      // The oldest event is destroyed to make room. Silent until now — no log,
      // no counter — and on a full queue it takes `$first_open` first, because
      // that is the oldest event the install has.
      this.queue.shift();
      this.recordDrop('queue_overflow');
    }
    this.queue.push(withoutIdfa(event));
    this._enqueuedCount += 1;
  }

  private maybeFlush(): void {
    if (this.queue.length >= this.flushThreshold) {
      this.doFlush();
    }
  }

  /**
   * Write a crash-safety snapshot of the queued events to persistence.
   *
   * This does NOT drain anything and does NOT deliver anything. The queues
   * (Rust queue on the WASM path; `this.queue` for the fallback engine and
   * rehydrated events) remain the single source of deliverable events —
   * delivery happens exclusively in `flushAsync()`. The snapshot is written
   * under one rolling key, overwritten each call, deleted once the queues
   * are empty, and consumed by hydration on the next init.
   *
   * History: this method used to DRAIN the queues into per-millisecond
   * `events_batch_*` keys. That made `flush()` a delivery black hole —
   * drained events were invisible to `flushAsync()` until the next page
   * load / process restart, and were silently destroyed where persistence
   * is a no-op (the default outside browsers). Mirrors the same fix in
   * core/src/client.rs::do_flush.
   */
  private doFlush(): void {
    try {
      // Oldest first: the fallback batch on the wire (drained from the head
      // of `queue`), then the rehydrated / fallback queue, then the live Rust
      // queue.
      let events: QueuedEvent[] = [...this._inflightEvents, ...this.queue];
      let wasmReadFailed = false;
      if (this.wasm) {
        try {
          const snapshotJson: string | null = this.wasm.queueSnapshotJson();
          if (snapshotJson) {
            events = events.concat(JSON.parse(snapshotJson) as QueuedEvent[]);
          }
        } catch {
          wasmReadFailed = true;
        }
      }

      if (wasmReadFailed) {
        // Incomplete view of the queues — don't mutate the on-disk snapshot
        // at all. Overwriting it with only the JS queue would truncate the
        // durable copy (dropping every Rust-queued event), and deleting it
        // could drop the only copy. Leave it untouched; the next doFlush
        // retries with a complete view. (BugBot rounds 11 + 12.)
        return;
      }

      if (events.length === 0) {
        // Everything delivered — drop the stale snapshot so the next init
        // doesn't re-hydrate already-sent events.
        this.deleteSnapshot();
        this._snapshotBytes = 0;
        return;
      }

      this.writeSnapshotChunks(events);
    } catch {
      // Best-effort: events remain queued in memory either way. NOT a drop —
      // it removes the crash-safety copy, it does not destroy an event — so it
      // gets its own counter rather than a `dropped_by_reason` bucket.
      this._health.persistWriteFailures += 1;
      this._health.dirty = true;
    } finally {
      // Durable flush point 2 of 3. Dirty-gated, so a tick that moved no
      // counter costs no storage round-trip.
      this.persistHealthCounters();
    }
  }

  /**
   * Write the snapshot across as many keys as it takes to keep every one of
   * them under {@link MAX_SNAPSHOT_CHUNK_BYTES}.
   *
   * The accounting is explicit and lossless: events are packed in queue order
   * until the next one would push the chunk over the cap, and a single event
   * that exceeds the cap on its own gets a chunk to itself rather than being
   * dropped or trimmed. An oversized row is a durability risk on Android; a
   * silently discarded event is data loss, which is strictly worse.
   */
  private writeSnapshotChunks(events: QueuedEvent[]): void {
    const encoder = new TextEncoder();
    const written = new Set<string>();
    let index = 0;
    let bytes = 0;

    for (const chunk of chunkEvents(events.map(withoutIdfa))) {
      // Skip any index a reserved key occupies: those chunks still hold events
      // this queue never hydrated, so renumbering onto one destroys them.
      while (this._reservedSnapshotKeys.has(snapshotChunkKey(index))) index += 1;
      const key = snapshotChunkKey(index);
      const encoded = encoder.encode(JSON.stringify(chunk));
      this.persistence.write(key, encoded);
      bytes += encoded.byteLength;
      written.add(key);
      index += 1;
    }
    this._snapshotBytes = bytes;

    // Chunks left over from a LARGER previous snapshot hold events this one no
    // longer contains — the next init would re-hydrate them as duplicates of
    // already-delivered events, so they go now. Enumerating what is actually
    // on disk (rather than probing indices) is what makes this correct when
    // reserved keys leave the numbering sparse.
    this.deleteSnapshot(written);
  }

  /**
   * Remove snapshot chunks, best-effort, key by key.
   *
   * `keep` is the set just written by {@link writeSnapshotChunks}. Reserved
   * keys are ALWAYS kept: they hold events that were never hydrated into this
   * queue, so an empty queue is no evidence they were delivered.
   */
  private deleteSnapshot(keep?: ReadonlySet<string>): void {
    let keys: string[] = [EVENTS_SNAPSHOT_KEY];
    try {
      const listed = this.persistence.listKeys(EVENTS_SNAPSHOT_KEY);
      if (listed.length > 0) keys = listed;
    } catch {
      // listKeys unavailable — fall back to the base key alone.
    }
    for (const key of keys) {
      if (keep?.has(key)) continue;
      if (this._reservedSnapshotKeys.has(key)) continue;
      try {
        this.persistence.delete(key);
      } catch {
        // Best effort
      }
    }
  }

  /**
   * Delete the whole durable event store — every `events_*` key — and forget
   * the snapshot reservations.
   *
   * The unit is the `events_` prefix, not the snapshot prefix, because that is
   * the unit `init` hydrates from: rolling snapshot chunks, `events_shutdown*`
   * files, and legacy `events_batch_*` files are one store as far as the next
   * launch is concerned. Deleting only the snapshot left any of the others
   * that a full queue had skipped on disk, and the next launch hydrated that
   * pre-logout backlog under the rotated identity — re-joining exactly the
   * events `reset()` exists to separate.
   *
   * Reserved chunks go too. {@link deleteSnapshot} deliberately keeps them —
   * they hold events this queue had no room to hydrate, so an empty queue is
   * no evidence they were delivered — but `reset()` is the one caller for
   * which that reasoning inverts: those events belong to the identity being
   * discarded, so "never read" is no reason to keep them past a logout.
   */
  private dropPersistedEvents(): void {
    // The base snapshot key is the one name that can be reached without a
    // working `listKeys`, and the rolling snapshot is the common case.
    let keys: string[] = [EVENTS_SNAPSHOT_KEY];
    try {
      const listed = this.persistence.listKeys(EVENTS_KEY_PREFIX);
      if (listed.length > 0) keys = listed;
    } catch {
      // listKeys unavailable — fall back to the base key alone.
    }
    for (const key of keys) {
      try {
        this.persistence.delete(key);
      } catch {
        // Best effort.
      }
    }
    this._reservedSnapshotKeys.clear();
  }

  /**
   * Write `events` under `baseKey`, split so no single persistence key exceeds
   * {@link MAX_SNAPSHOT_CHUNK_BYTES}. Returns false if any chunk failed to
   * write, so the caller can keep whatever other durable copy it has.
   */
  private writeEventChunks(baseKey: string, events: QueuedEvent[]): boolean {
    const encoder = new TextEncoder();
    let ok = true;
    chunkEvents(events.map(withoutIdfa)).forEach((chunk, index) => {
      try {
        this.persistence.write(chunkKey(baseKey, index), encoder.encode(JSON.stringify(chunk)));
      } catch {
        this._health.persistWriteFailures += 1;
        this._health.dirty = true;
        ok = false;
      }
    });
    return ok;
  }

  /**
   * Mark a snapshot chunk as holding events this core never hydrated — the
   * queue was already full when hydration reached it. It stays on disk,
   * untouched by every later snapshot write, until a launch with room for it.
   */
  private reserveSnapshotKey(key: string): void {
    if (key.startsWith(EVENTS_SNAPSHOT_KEY)) this._reservedSnapshotKeys.add(key);
  }

  private drainJsQueue(count: number): QueuedEvent[] {
    return this.queue.splice(0, count);
  }

  private createBatch(events: QueuedEvent[]): EventBatch {
    return {
      events,
      batch_id: generateUUID(),
      sent_at: new Date().toISOString(),
      // Every batch carries the wire protocol version, matching
      // `EventBatch::new` in core/src/event.rs:409-425. This method builds the
      // fallback engine's batches AND — on the WASM path — the rehydrated-queue
      // (`flushJsQueue`) and `sendBeacon` batches, none of which pass through
      // the Rust serializer, so those shipped unversioned too.
      protocol_version: PROTOCOL_VERSION
    };
  }

  private buildHeaders(): Array<[string, string]> {
    const headers: Array<[string, string]> = [
      ['Content-Type', 'application/json'],
      ['X-App-Id', this.appId],
      ['X-Environment', this.environment],
      ['X-SDK-Version', this.sdkVersionHeader()],
      ['X-Session-Id', this.getSessionId()],
      ['X-Request-Id', generateUUID()]
    ];
    const debugToken = this.getDebugToken();
    if (debugToken !== null) {
      headers.push(['X-Debug-Token', debugToken]);
    }
    return headers;
  }

  /**
   * Return the per-device DebugView token, or null if debug mode is off.
   * The token is generated once on first use and persisted under `debug_token`
   * so it survives restarts. Useful for surfacing the token in dev UIs.
   */
  getDebugToken(): string | null {
    return this.debugToken;
  }

  /** Persist key for the debug token — kept private to avoid accidental collisions. */
  private static readonly DEBUG_TOKEN_KEY = 'debug_token';

  private loadOrMintDebugToken(): string {
    try {
      const existing = this.persistence.read(LayersCore.DEBUG_TOKEN_KEY);
      if (existing && existing.length > 0) {
        const decoded = new TextDecoder().decode(existing).trim();
        if (decoded.length > 0) return decoded;
      }
    } catch {
      // Persistence unavailable — fall through and mint a fresh token.
    }
    const token = generateUUID();
    try {
      this.persistence.write(LayersCore.DEBUG_TOKEN_KEY, new TextEncoder().encode(token));
    } catch {
      // Best-effort persistence — token will regenerate next session.
    }
    return token;
  }

  /** Stamp ad identifiers at delivery and choose the host (spec: ATT tracking host split). */
  private routeForDelivery(
    events: QueuedEvent[],
    liveAttStatus: string | null
  ): { url: string; events: QueuedEvent[] } {
    const ctx = this.deviceContext;
    const live = liveAttStatus?.trim() || null;
    const adAllowed = this.advertisingAllowed();
    const attApplies = live !== null || this.attStatusProvider !== undefined;
    const status = adAllowed ? (attApplies ? (live ?? ctx.attStatus) : ctx.attStatus) : undefined;
    const idfa = adAllowed
      ? attApplies
        ? live === 'authorized' && ctx.idfa?.trim()
          ? ctx.idfa
          : undefined
        : ctx.idfa
      : undefined;

    return {
      url: `${attApplies && idfa ? this.trackingBaseUrl : this.baseUrl}/events`,
      events: events.map((event) => {
        const copy = withoutIdfa(event);
        if (idfa) copy.idfa = idfa;
        if (status !== undefined && (attApplies || status)) {
          copy.att_status = status;
        } else {
          delete copy.att_status;
        }
        return copy;
      })
    };
  }

  private async readLiveAttStatus(): Promise<string | null> {
    const provider = this.attStatusProvider;
    if (!provider) return null;

    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const result = await Promise.race([
        Promise.resolve().then(provider),
        new Promise<null>((resolve) => {
          timer = setTimeout(() => resolve(null), 1_000);
          const handle = timer as typeof timer & { unref?: () => void };
          handle.unref?.();
        })
      ]);
      return typeof result === 'string' && result.trim() ? result.trim() : 'not_determined';
    } catch {
      return 'not_determined';
    } finally {
      if (timer !== undefined) clearTimeout(timer);
    }
  }

  /** Flush rehydrated JS events via HTTP (used when WASM is loaded but JS queue has leftover events). */
  private async flushJsQueue(liveAttStatus: string | null): Promise<void> {
    // Defense in depth: never deliver the JS queue while the privacy gate
    // is closed (flushAsync also checks, but this method must be safe for
    // any future caller). Events stay queued for the next attempt.
    if (!this.flushAllowed()) return;
    if (this.queue.length === 0) return;

    // Same delivery gate and outcome report as every other path (ADR
    // 0001) — this delivery attempt must feed the breaker/Retry-After
    // state like any other.
    const gateOpen = this.wasm ? this.wasm.shouldAttemptFlush() : this.fallbackShouldAttemptFlush();
    if (!gateOpen) return;

    const events = this.drainJsQueue(this.maxBatchSize);
    if (events.length === 0) {
      // Gate passed but the queue raced empty — pre-wire abort: release a
      // claimed half-open probe WITHOUT counting a delivery failure (no
      // request was made; status 0 would wrongly feed the breaker).
      if (this.wasm) {
        this.wasm.abortFlushAttempt();
      } else {
        this.fallbackAbortFlushAttempt();
      }
      return;
    }
    this._health.inflight += events.length;

    const routed = this.routeForDelivery(events, liveAttStatus);
    const batch = this.createBatch(routed.events);
    const body = new TextEncoder().encode(JSON.stringify(batch));
    const headers = this.buildHeaders();

    let status = 0;
    let retryAfterHeader: string | null = null;
    try {
      const resp = await this.httpClient.post(routed.url, headers, body);
      status = resp.status;
      retryAfterHeader = this.extractHeader(resp.headers, 'retry-after');
    } catch {
      // No response — status stays 0.
    }

    const verdict = this.wasm
      ? this.wasm.recordFlushResult(status, retryAfterHeader)
      : this.fallbackRecordFlushResult(status, retryAfterHeader);
    this.recordFlushOutcome(status, verdict as 'delivered' | 'retry_later' | 'drop', events.length);
    if (verdict === 'delivered') {
      // These events never passed through the Rust core's drain, so the
      // receipt route for drained batches did not see them. This is the only
      // place a `$first_open` recovered into THIS queue can earn one.
      this.confirmFirstOpenDelivered(events);
    }
    if (verdict === 'retry_later') {
      this.queue.unshift(...events.map(withoutIdfa));
    } else if (verdict === 'drop') {
      // Surface non-retryable rejections exactly like the main flush
      // path — a silently vanishing batch is undebuggable.
      throw new LayersError('HTTP', `HTTP ${status}`, status);
    }
  }

  private startFlushTimer(): void {
    if (this.flushIntervalMs <= 0) return;
    this.flushTimer = setInterval(() => {
      try {
        if (!this._isShutDown && this.queueDepth() > 0) {
          void this.flushAsync().catch((e) => {
            // Background flush failed. Transient failures requeue and
            // retry; drop verdicts surface here — log so a discarded
            // poison batch is never fully silent.
            if (this.enableDebug) {
              console.warn('[Layers] background flush error:', e);
            }
          });
        }
      } catch {
        // Synchronous throw in timer callback (e.g., queueDepth() or checkCircuit) — swallow
      }
    }, this.flushIntervalMs);

    // Unref timer in Node.js so it doesn't keep the process alive
    if (typeof this.flushTimer === 'object' && 'unref' in this.flushTimer) {
      (this.flushTimer as NodeJS.Timeout).unref();
    }
  }

  // --- Retry-After gate (public accessors) ---

  /**
   * Check whether the server has requested a Retry-After delay that is currently active.
   * When active, flush attempts are skipped until the delay expires.
   */
  isRetryAfterActive(): boolean {
    if (this.wasm) {
      return this.wasm.isRetryAfterActive();
    }
    return this._retryAfterUntilMs > 0 && Date.now() < this._retryAfterUntilMs;
  }

  /**
   * Return the remaining Retry-After delay in milliseconds, or 0 if not active.
   */
  retryAfterRemainingMs(): number {
    if (this.wasm) {
      return Number(this.wasm.retryAfterRemainingMs());
    }
    if (this._retryAfterUntilMs > 0) {
      const remaining = this._retryAfterUntilMs - Date.now();
      return remaining > 0 ? remaining : 0;
    }
    return 0;
  }

  // --- Internal helpers ---

  /**
   * Extract a header value from an array of [key, value] pairs (case-insensitive).
   */
  private extractHeader(headers: Array<[string, string]>, name: string): string | null {
    const lower = name.toLowerCase();
    for (const [k, v] of headers) {
      if (k.toLowerCase() === lower) return v;
    }
    return null;
  }

  /**
   * Parse a Retry-After header value and set the JS fallback Retry-After gate.
   * Supports both delta-seconds (e.g. "120") and HTTP-date formats.
   * Caps at RETRY_AFTER_MAX_SECS (300s) to match the WASM/Rust core.
   */
  private updateJsRetryAfter(headerValue: string | null): void {
    if (!headerValue) {
      // No header — use a default of 60s for 429/5xx
      this._retryAfterUntilMs = Date.now() + 60_000;
      return;
    }

    // Try parsing as integer (delta-seconds)
    const deltaSecs = parseInt(headerValue, 10);
    if (!isNaN(deltaSecs) && deltaSecs > 0) {
      const cappedSecs = Math.min(deltaSecs, LayersCore.RETRY_AFTER_MAX_SECS);
      this._retryAfterUntilMs = Date.now() + cappedSecs * 1_000;
      return;
    }

    // Try parsing as HTTP-date (e.g. "Wed, 21 Oct 2015 07:28:00 GMT")
    const dateMs = Date.parse(headerValue);
    if (!isNaN(dateMs)) {
      const delaySecs = Math.max(0, (dateMs - Date.now()) / 1_000);
      const cappedSecs = Math.min(delaySecs, LayersCore.RETRY_AFTER_MAX_SECS);
      this._retryAfterUntilMs = Date.now() + cappedSecs * 1_000;
      return;
    }

    // Unparseable — use a default of 60s
    this._retryAfterUntilMs = Date.now() + 60_000;
  }

  // --- Delivery policy: JS fallback mirror (ADR 0001) ---
  //
  // Where WebAssembly is unavailable, the fallback engine mirrors the Rust
  // core's delivery policy in TS: same status classification, same breaker
  // thresholds, same Retry-After semantics (gate only set by an actual
  // 429/503 header). Parity is asserted by tests, not shared code.

  /** While half-open, the time the single allowed probe was claimed (0 = unclaimed). */
  private fallbackProbeClaimedAt = 0;
  /** Mirror of the Rust breaker's PROBE_CLAIM_TIMEOUT (45s). */
  private static readonly PROBE_CLAIM_TIMEOUT_MS = 45_000;

  /**
   * Mirror of the core's `should_attempt_flush` minus the privacy gate
   * (callers check `flushAllowed()` first): Retry-After delay + circuit
   * breaker. May transition the breaker open → half-open and claim the
   * single half-open probe slot (the claim expires after 45s so an
   * abandoned probe cannot wedge the circuit — same as the Rust core).
   */
  private fallbackShouldAttemptFlush(): boolean {
    if (this._retryAfterUntilMs > 0 && Date.now() < this._retryAfterUntilMs) {
      return false;
    }
    if (this.circuitState === 'open') {
      if (Date.now() - this.circuitOpenedAt < this.circuitResetMs) {
        return false;
      }
      this.circuitState = 'half-open';
      this.fallbackProbeClaimedAt = Date.now();
      return true;
    }
    if (this.circuitState === 'half-open') {
      // Only one probe at a time; an expired claim may be re-claimed.
      if (
        this.fallbackProbeClaimedAt > 0 &&
        Date.now() - this.fallbackProbeClaimedAt < LayersCore.PROBE_CLAIM_TIMEOUT_MS
      ) {
        return false;
      }
      this.fallbackProbeClaimedAt = Date.now();
    }
    return true;
  }

  /**
   * Mirror of the core's `abort_flush_attempt`: a pre-wire abort after a
   * passed gate releases a claimed half-open probe WITHOUT recording an
   * outcome — no request happened, so the breaker must not count it.
   */
  private fallbackAbortFlushAttempt(): void {
    this.fallbackProbeClaimedAt = 0;
  }

  /**
   * Mirror of the core's `record_flush_result`. `status` 0 = no response
   * (network error / timeout).
   *
   * `403` is retryable here for the same reason it is in `client.rs`: it is
   * an environment verdict (edge/IAM misconfiguration, WAF rule, proxy
   * refusing the route), returned to every request regardless of payload,
   * and it clears when the misconfiguration does. `401` stays a drop —
   * definitively-invalid credentials per server change layers#4865.
   */
  private fallbackRecordFlushResult(
    status: number,
    retryAfterHeader: string | null
  ): 'delivered' | 'retry_later' | 'drop' {
    if (status >= 200 && status < 300) {
      this.circuitRecordSuccess();
      this._retryAfterUntilMs = 0;
      return 'delivered';
    }
    if (status === 0 || status === 403 || status === 408 || status === 429 || status >= 500) {
      this.circuitRecordFailure();
      // Match the Rust core: the gate is only set by an actual Retry-After
      // header on 429/503 — other failures back off via the breaker.
      if ((status === 429 || status === 503) && retryAfterHeader) {
        this.updateJsRetryAfter(retryAfterHeader);
      }
      return 'retry_later';
    }
    // Non-retryable 4xx proves connectivity — breaker success, drop batch.
    this.circuitRecordSuccess();
    return 'drop';
  }

  // --- Circuit breaker ---
  //
  // The breaker gates DELIVERY only (`fallbackShouldAttemptFlush` above),
  // never ingest. A `checkCircuit()` helper used to throw `CIRCUIT_OPEN` out
  // of `track()`/`screen()`; it was removed because it inverted ADR 0001 —
  // an open breaker means the network is failing, which is when the queue
  // matters most, not when it should start refusing events.

  private circuitRecordSuccess(): void {
    if (this.circuitState === 'half-open') {
      // Successful request in half-open state — fully close the circuit
      this.circuitFailures = 0;
      this.circuitState = 'closed';
      this.fallbackProbeClaimedAt = 0;
    } else {
      this.circuitFailures = 0;
    }
  }

  private circuitRecordFailure(): void {
    if (this.circuitState === 'half-open') {
      // Probe failed — back to open.
      this.circuitFailures++;
      this.circuitState = 'open';
      this.circuitOpenedAt = Date.now();
      this.fallbackProbeClaimedAt = 0;
      this._health.breakerOpens += 1;
      this._health.dirty = true;
      return;
    }
    this.circuitFailures++;
    if (this.circuitFailures >= this.circuitThreshold) {
      const wasClosed = this.circuitState === 'closed';
      this.circuitState = 'open';
      this.circuitOpenedAt = Date.now();
      if (wasClosed) {
        this._health.breakerOpens += 1;
        this._health.dirty = true;
      }
    }
  }

  // ── Tier 8: Surveys ──────────────────────────────────────────────────

  /** Returns the list of survey ids currently cached from `/config`. */
  knownSurveyIds(): string[] {
    if (!this.wasm) return this.surveyEngine.knownIds();
    try {
      return this.wasm.knownSurveyIds();
    } catch {
      return [];
    }
  }

  /**
   * Returns ALL currently-cached survey definitions as a JSON array string.
   * Wrappers parse this once per build-context call to discover targeting
   * dependencies (e.g. feature flag references) without iterating active
   * surveys, which would create a circular dependency with flag-gated
   * surveys.
   */
  surveyDefinitionsJson(): string {
    if (!this.wasm) return this.surveyEngine.definitionsJson();
    try {
      return this.wasm.surveyDefinitionsJson();
    } catch {
      return '[]';
    }
  }

  /**
   * Return surveys currently eligible to show given a targeting context
   * (URL/screen, person properties, feature flag values, event counts).
   * Returns `[]` when consent is denied or the WASM core is unavailable.
   */
  getActiveSurveys(
    ctx: import('./types.js').SurveyTargetingContext = {}
  ): import('./types.js').SurveyDefinition[] {
    if (!this.wasm) {
      // Privacy-first: denied analytics consent makes every survey ineligible
      // (client.rs:1816-1818).
      if (!this.analyticsAllowed()) return [];
      return this.surveyEngine.getActiveSurveys(ctx);
    }
    try {
      const json = this.wasm.getActiveSurveysJson(ctx as unknown);
      return JSON.parse(json) as import('./types.js').SurveyDefinition[];
    } catch {
      return [];
    }
  }

  /** Mark a survey as shown — emits the `survey shown` auto-event. */
  markSurveyShown(surveyId: string): void {
    if (!this.wasm) {
      this.emitSurveyAutoEvent(this.surveyEngine.recordShow(surveyId));
      return;
    }
    try {
      this.wasm.markSurveyShown(surveyId);
    } catch {
      // Best effort
    }
  }

  /** Mark a survey as dismissed — emits the `survey dismissed` auto-event. */
  markSurveyDismissed(surveyId: string): void {
    if (!this.wasm) {
      this.emitSurveyAutoEvent(this.surveyEngine.recordDismiss(surveyId));
      return;
    }
    try {
      this.wasm.markSurveyDismissed(surveyId);
    } catch {
      // Best effort
    }
  }

  /** Submit a survey response — emits the `survey sent` auto-event. */
  submitSurveyResponse(surveyId: string, response: import('./types.js').SurveyResponse): void {
    if (!this.wasm) {
      this.emitSurveyAutoEvent(this.surveyEngine.recordResponse(surveyId, response));
      return;
    }
    try {
      this.wasm.submitSurveyResponse(surveyId, response as unknown);
    } catch {
      // Best effort
    }
  }

  /**
   * Route a survey auto-event through the standard `track()` pipeline, exactly
   * as `emit_survey_event` does (client.rs:1867-1876). Null means the survey id
   * was unknown — the Rust core returns an error there, which the WASM branches
   * above swallow, so a silent no-op is the matching behaviour.
   */
  private emitSurveyAutoEvent(event: import('./surveys.js').SurveyAutoEvent | null): void {
    if (!event) return;
    try {
      this.track(event.eventName, event.properties as EventProperties);
    } catch (e) {
      if (this.enableDebug) {
        console.warn(`[Layers] failed to emit ${event.eventName}:`, e);
      }
    }
  }

  /**
   * Snapshot of per-survey-id show-history for cross-restart persistence.
   * Wrappers can write this to localStorage and restore on init via
   * `seedSurveyShowHistory()`.
   */
  surveyShowHistoryJson(): string {
    if (!this.wasm) return JSON.stringify(this.surveyEngine.showHistory());
    try {
      return this.wasm.surveyShowHistoryJson();
    } catch {
      return '{}';
    }
  }

  /** Restore a previously snapshotted survey show-history map. */
  seedSurveyShowHistory(historyJson: string): void {
    if (!this.wasm) {
      try {
        const parsed: unknown = JSON.parse(historyJson);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          // `seedShowHistory` is all-or-nothing and reports rejection; a
          // partial seed would under-count shows and let a capped survey
          // re-show. Nothing to do on rejection — same as the WASM branch
          // below, where the Rust error is swallowed and history stands.
          this.surveyEngine.seedShowHistory(parsed as Record<string, unknown>);
        }
      } catch {
        // Invalid history JSON — Rust returns an error the WASM branch below
        // swallows, so leave the in-memory history untouched.
      }
      return;
    }
    try {
      this.wasm.seedSurveyShowHistory(historyJson);
    } catch {
      // Best effort
    }
  }
}
